import { useState, useEffect, useMemo, useRef, Fragment } from "react";
import { createPortal } from "react-dom";
import { T, S, fmtDate } from "./theme";
import { Badge, Btn, CardExpandRail, CardMetricList } from "./components";
import { ensureXLSX } from "./xlsx";
import { fetchStationList, fetchSpecialties, fetchVISNs, fetchProviders, fetchSpecialtyStationMap, fetchWeeks, fetchRecentWeekly, fetchStationWeeklyHistory, exportStationWeeklyHistoryCSV, fetchMonthlyReport, fetchMonthlyReportTotal, fetchMonthlyReportTotalsBatch, batchLookupProviders, bulkLookupProviders, fetchLicensedGrowth, fetchLaunchedGrowth, fetchActiveGrowth, fetchDecommissionedGrowth, fetchGrowth30d, fetchActivitySummary, fetchSpecialtySummary, fetchActivityBreakdown, fetchActivityDetail, fetchStationUtilization, fetchSpecialtyUtilization, fetchVisnUtilization, fetchRecordingLeaderboard } from "./api";
import { UNASSIGNED_RISE, distinctRiseNumbers, formatRiseLabel, groupStationsByRise, pruneVisnFilters, visnsForRise } from "./rise";
import { createLatestRequestGuard } from "./latestRequest";

const ACTIVITY_BREAKDOWN_GROUP_BY = { "by-station": "station_id", "by-visn": "visn", "by-rise": "new_visn", "by-specialty": "specialty" };
const ACTIVITY_BREAKDOWN_LABELS = { station_id: "Station", visn: "VISN", new_visn: "RISE", specialty: "Specialty" };
const ENGAGEMENT_GROUP_TABS = [
  { launch: "by-station", util: "station", label: "By Station" },
  { launch: "by-visn", util: "visn", label: "By VISN" },
  { launch: "by-rise", util: "rise", label: "By RISE" },
  { launch: "by-specialty", util: "specialty", label: "By Specialty" },
];
const ACTIVITY_DETAIL_LABELS = { at_risk: "At Risk", power_users: "Power Users", not_launched: "Not Launched", inactive: "Inactive" };

// Rough idle-time / volume breakdown shared by the top-level At Risk / Power
// Users stat boxes and the per-row breakdown in the Breakdown tab's table.
function computeActivityBuckets(type, rows) {
  const isAtRisk = type === "at_risk";
  const values = rows.map((p) => isAtRisk
    ? (p.last_appt ? Math.floor((Date.now() - new Date(p.last_appt).getTime()) / 86400000) : null)
    : (p.week_recordings ?? 0));
  const buckets = isAtRisk
    ? [
        { label: "No recorded activity", test: (v) => v == null },
        { label: "14–30 days idle", test: (v) => v != null && v <= 30 },
        { label: "31–60 days idle", test: (v) => v != null && v > 30 && v <= 60 },
        { label: "61–90 days idle", test: (v) => v != null && v > 60 && v <= 90 },
        { label: "90+ days idle", test: (v) => v != null && v > 90 },
      ]
    : [
        { label: "16–25 / week", test: (v) => v >= 16 && v <= 25 },
        { label: "26–50 / week", test: (v) => v >= 26 && v <= 50 },
        { label: "51–100 / week", test: (v) => v >= 51 && v <= 100 },
        { label: "100+ / week", test: (v) => v > 100 },
      ];
  const counted = buckets
    .map((b) => ({ label: b.label, count: values.filter(b.test).length }))
    .filter((b) => b.count > 0);
  return { isAtRisk, counted, total: rows.length };
}

// Small accent "What's included?" pill used on summary cards. Opens a
// tooltip rendered via a portal (fixed position, computed from the pill's
// bounding rect) so a card's `overflow: hidden` — needed for its collapse
// animation — can't clip the popover.
function InfoPopover({ label = "What's included?", children }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const ref = useRef(null);

  const show = () => {
    const rect = ref.current?.getBoundingClientRect();
    if (rect) setPos({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
    setOpen(true);
  };
  const hide = () => setOpen(false);

  return (
    <div
      style={{ marginLeft: "auto", paddingLeft: 16, flexShrink: 0, alignSelf: "center" }}
      onClick={(e) => e.stopPropagation()}
      onMouseEnter={show}
      onMouseLeave={hide}
    >
      <div
        ref={ref}
        onClick={() => (open ? hide() : show())}
        style={{
          display: "flex", alignItems: "center", gap: 5, padding: "3px 2px",
          color: open ? T.accent : T.textDim,
          cursor: "pointer", transition: "color 0.15s",
        }}
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="9.5" />
          <line x1="12" y1="10.8" x2="12" y2="16.5" />
          <circle cx="12" cy="7.6" r="0.25" fill="currentColor" stroke="currentColor" strokeWidth="1.8" />
        </svg>
        <span style={{ fontSize: 11, fontWeight: 500, letterSpacing: 0.2, whiteSpace: "nowrap" }}>{label}</span>
      </div>
      {pos && createPortal(
        <div style={{
          position: "fixed", top: pos.top, right: pos.right, zIndex: 1000, width: 260,
          background: T.surface, border: `1px solid ${T.border}`, borderRadius: T.radiusSm,
          boxShadow: T.shadow, padding: "12px 14px",
          opacity: open ? 1 : 0,
          transform: open ? "translateY(0)" : "translateY(-4px)",
          pointerEvents: open ? "auto" : "none",
          transition: "opacity 0.15s, transform 0.15s",
        }}
          onMouseEnter={show}
          onMouseLeave={hide}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 8, fontSize: 12, color: T.textMuted, lineHeight: 1.4 }}>
            {children}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

const GEO_BADGE = {
  fontSize: 13, padding: "2px 7px", borderRadius: 20,
  background: T.accentSoft, color: T.accent, fontWeight: 500,
};

// Station 619 plus the original purple VISN pill, with the same pill for RISE.
function StationIdentity({ station, titleStyle }) {
  const hasVisn = station?.visn != null && station.visn !== "";
  const hasRise = station?.new_visn != null && station.new_visn !== "";
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
      <span style={titleStyle}>Station {station?.station_id}</span>
      {hasVisn && <span style={GEO_BADGE}>VISN {station.visn}</span>}
      {hasRise && <span style={GEO_BADGE}>RISE {station.new_visn}</span>}
    </span>
  );
}

export default function Dashboard({ showToast, navigate, userJourneyModalOpen = false, setUserJourneyModalOpen = () => {}, stationGroupingModalOpen = false, setStationGroupingModalOpen = () => {} }) {
  const [dashInner, setDashInner] = useState("by-station");
  const [visnFilters, setVisnFilters] = useState([]);
  const [riseFilters, setRiseFilters] = useState([]);
  const [dashSpecialties, setDashSpecialties] = useState([]);
  const [search, setSearch] = useState("");
  const [stationPage, setStationPage] = useState(1);
  const [visnSearch, setVisnSearch] = useState("");
  const [riseSearch, setRiseSearch] = useState("");
  const [openDropdown, setOpenDropdown] = useState(null);
  const [hoveredDot, setHoveredDot] = useState(null);
  const [hoveredBar, setHoveredBar] = useState(null);
  const [momMetric, setMomMetric] = useState("mom-license-states"); // top-level tab: "mom-license-states" | "geography" | "doctors" | "all" | "email-outreach"
  // Which of Provisioned/Launched/Active/Decommissioned are toggled on inside
  // the Month Over Month License States tab — a multi-select (not a
  // single-select radio), so any combination can show together on the bar
  // chart and as stacked definition/monthly-table sections below it. All
  // four start on so the chart opens showing everything at once.
  const [visibleLicenseStates, setVisibleLicenseStates] = useState({ provisioned: true, launched: true, active: true, decommissioned: true });
  const [selectedStation, setSelectedStation] = useState(null);
  const [selectedVisn, setSelectedVisn] = useState(null);
  const [selectedRise, setSelectedRise] = useState(null);
  const [stationProviders, setStationProviders] = useState([]);
  const [stationWeekly, setStationWeekly] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [weeks, setWeeks] = useState([]);
  const [providerSearch, setProviderSearch] = useState("");
  const [providerPage, setProviderPage] = useState(1);
  const [visnPage, setVisnPage] = useState(1);
  const [risePage, setRisePage] = useState(1);
  const [stationSort, setStationSort] = useState({ key: "launched", dir: "desc" });
  const [visnSort, setVisnSort] = useState({ key: "launched", dir: "desc" });
  const [riseSort, setRiseSort] = useState({ key: "launched", dir: "desc" });
  const [selectedSpecialtyAgg, setSelectedSpecialtyAgg] = useState(null);
  const [specialtySummary, setSpecialtySummary] = useState([]);
  const [specialtySummaryLoading, setSpecialtySummaryLoading] = useState(false);
  const [specialtySearch, setSpecialtySearch] = useState("");
  const [specialtyPage, setSpecialtyPage] = useState(1);
  const [specialtySort, setSpecialtySort] = useState({ key: "launched", dir: "desc" });
  const [specialtyDetailPage, setSpecialtyDetailPage] = useState(1);
  // Browsable "list every station / specialty" tables inside the Utilization
  // Report (By Station / By Specialty) — separate sort/page state from the
  // Launch Report's own tables above so browsing one doesn't reset the other.
  const [utilStationSort, setUtilStationSort] = useState({ key: "launched", dir: "desc" });
  const [utilStationPage, setUtilStationPage] = useState(1);
  const [utilSpecialtySort, setUtilSpecialtySort] = useState({ key: "launched", dir: "desc" });
  const [utilSpecialtyPage, setUtilSpecialtyPage] = useState(1);
  const [weeklyHistoryModal, setWeeklyHistoryModal] = useState(null);
  const [providerDetailModal, setProviderDetailModal] = useState(null);
  const [decommissionedGrowth, setDecommissionedGrowth] = useState([]);

  const hasAutoSelected = useRef(false);

  const [rightPanelWidth, setRightPanelWidth] = useState(null); // null = 60/40 default
  // Which purpose-based group shows in the shared tooling panel below the card row.
  const [toolCategory, setToolCategory] = useState("trends"); // "trends" | "user-lookup" | "breakdowns" — "user-flagging" and "reports" folded into "trends" (their only content — Decommissioned Users and the monthly export — now lives under the matching metric filter)
  // Sub-tab inside "User Lookup" (single/batch provider lookup vs email outreach check).
  // userJourneyModalOpen / setUserJourneyModalOpen now come in as props (lifted
  // to index.js) so the ThunderCats header easter egg can open this panel from
  // any page, not just while Dashboard itself is mounted.
  const [userJourneyModalSize, setUserJourneyModalSize] = useState(null); // null = default size; { width, height } once the user drags the resize handle
  const [userJourneyModalPos, setUserJourneyModalPos] = useState(null); // null = centered; { x, y } (viewport pixels) once the user drags the header
  // stationGroupingModalOpen / setStationGroupingModalOpen also come in as
  // props (lifted to index.js), same reasoning as the Provider Lifecycle panel.
  const [stationGroupingModalSize, setStationGroupingModalSize] = useState(null);
  const [stationGroupingModalPos, setStationGroupingModalPos] = useState(null);
  // Station Utilization search — Tools › Trends › Station Utilization
  const [stationSearchInput, setStationSearchInput] = useState("");
  const [geoCitySearch, setGeoCitySearch] = useState(""); // Geography tab's city filter
  const [stationReport, setStationReport] = useState(null);
  const [stationReportLoading, setStationReportLoading] = useState(false);
  const [stationReportError, setStationReportError] = useState(null);
  // Utilization tab's station search — typeahead against the station list
  // already loaded client-side (groupedStations), same portal-dropdown
  // pattern as the Doctor Lookup suggest box below (needed because this
  // input also sits inside an overflow:hidden card).
  const [stationSearchSuggestOpen, setStationSearchSuggestOpen] = useState(false);
  const stationSearchInputRef = useRef(null);
  const [stationSearchDropdownPos, setStationSearchDropdownPos] = useState(null);
  const updateStationSearchDropdownPos = () => {
    const rect = stationSearchInputRef.current?.getBoundingClientRect();
    if (rect) setStationSearchDropdownPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
  };
  useEffect(() => {
    if (!stationSearchSuggestOpen) return;
    updateStationSearchDropdownPos();
    const handler = () => updateStationSearchDropdownPos();
    window.addEventListener("scroll", handler, true);
    window.addEventListener("resize", handler);
    return () => {
      window.removeEventListener("scroll", handler, true);
      window.removeEventListener("resize", handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stationSearchSuggestOpen]);
  const [stationWeeklyModalOpen, setStationWeeklyModalOpen] = useState(false); // full weekly table popup, opened from the preview
  const [stationDoctorsModalOpen, setStationDoctorsModalOpen] = useState(false); // full doctor roster popup, opened from the drill-down preview
  // By Specialty utilization report — same shape as Station Utilization above,
  // scoped by specialty instead. Specialties are a small fixed list already
  // loaded (`specialties`), so a plain <select> replaces the station search's
  // typeahead — no portal/dropdown-position machinery needed.
  const [utilizationSearchMode, setUtilizationSearchMode] = useState("station"); // "station" | "visn" | "rise" | "specialty" — sub-tab inside the Utilization tab
  const [launchUtilView, setLaunchUtilView] = useState("launch"); // "utilization" | "launch" | "breakdown" — toggle inside the Engagement tab; Launch Report is the default view
  const [specialtyUtilSelected, setSpecialtyUtilSelected] = useState("");
  const [specialtyReport, setSpecialtyReport] = useState(null);
  const [specialtyReportLoading, setSpecialtyReportLoading] = useState(false);
  const [specialtyReportError, setSpecialtyReportError] = useState(null);
  const [specialtyWeeklyModalOpen, setSpecialtyWeeklyModalOpen] = useState(false);
  const [specialtyDoctorsModalOpen, setSpecialtyDoctorsModalOpen] = useState(false);
  /* ── By VISN utilization report — same round trip as By Station/By
     Specialty above, scoped by VISN instead. Kept independent of `selectedVisn`
     (the Launch Report's own VISN drill-down state) so the two reports don't
     clobber each other's selection. ── */
  const [visnUtilSelected, setVisnUtilSelected] = useState("");
  const [visnReport, setVisnReport] = useState(null);
  const [visnReportLoading, setVisnReportLoading] = useState(false);
  const [visnReportError, setVisnReportError] = useState(null);
  const [visnWeeklyModalOpen, setVisnWeeklyModalOpen] = useState(false);
  const [visnDoctorsModalOpen, setVisnDoctorsModalOpen] = useState(false);
  const [utilVisnSort, setUtilVisnSort] = useState({ key: "launched", dir: "desc" });
  const [utilVisnPage, setUtilVisnPage] = useState(1);
  const [riseUtilSelected, setRiseUtilSelected] = useState("");
  const [riseReport, setRiseReport] = useState(null);
  const [riseReportLoading, setRiseReportLoading] = useState(false);
  const [riseReportError, setRiseReportError] = useState(null);
  const [riseWeeklyModalOpen, setRiseWeeklyModalOpen] = useState(false);
  const [riseDoctorsModalOpen, setRiseDoctorsModalOpen] = useState(false);
  const [utilRiseSort, setUtilRiseSort] = useState({ key: "launched", dir: "desc" });
  const [utilRisePage, setUtilRisePage] = useState(1);
  const stationSearchGuard = useRef(createLatestRequestGuard()).current;
  const specialtySearchGuard = useRef(createLatestRequestGuard()).current;
  const visnSearchGuard = useRef(createLatestRequestGuard()).current;
  const riseSearchGuard = useRef(createLatestRequestGuard()).current;
  const specialtySummaryGuard = useRef(createLatestRequestGuard()).current;
  const [hoveredLaunchTrendBar, setHoveredLaunchTrendBar] = useState(null);
  const [launchedTableOpen, setLaunchedTableOpen] = useState(false); // Launched trend monthly table popup, Tools › Trends
  const [activeTableOpen, setActiveTableOpen] = useState(false); // Active trend monthly table popup, Tools › Trends
  const [provisionedTableOpen, setProvisionedTableOpen] = useState(false); // Provisioned trend monthly table popup, Tools › Trends
  const [decommissionedTableOpen, setDecommissionedTableOpen] = useState(false);
  // Drag position for each monthly-table popup above — null means still
  // centered; once dragged via the header, holds { x, y } in viewport
  // pixels. Kept separate per popup so moving one doesn't affect where
  // the others reopen.
  const [launchedTablePos, setLaunchedTablePos] = useState(null);
  const [provisionedTablePos, setProvisionedTablePos] = useState(null);
  const [activeTablePos, setActiveTablePos] = useState(null);
  const [decommissionedTablePos, setDecommissionedTablePos] = useState(null);
  const [activityBreakdownInner, setActivityBreakdownInner] = useState("by-station");
  const [activityBreakdownData, setActivityBreakdownData] = useState({});
  const [activityBreakdownLoading, setActivityBreakdownLoading] = useState(false);
  const [activityBreakdownSearch, setActivityBreakdownSearch] = useState("");

  /* ── User Leaderboard tab — top 10 recording providers, either over a
     30-day trailing window or cumulative (all-time), optionally narrowed to
     one specialty/VISN/station. Re-fetched whenever the tab is open and any
     of these conditions change (not just once) — unlike the other lazy-cache
     endpoints, the result set genuinely depends on the filter combination, so
     there's nothing to usefully cache. No search/pagination state needed —
     only 10 rows. ── */
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);
  const [leaderboardSort, setLeaderboardSort] = useState({ key: "recordings", dir: "desc" });
  const [leaderboardCumulative, setLeaderboardCumulative] = useState(false); // false = trailing 30-day window, true = all-time cumulative
  const [leaderboardSpecialtyFilter, setLeaderboardSpecialtyFilter] = useState("");
  const [leaderboardVisnFilter, setLeaderboardVisnFilter] = useState("");
  const [leaderboardStationFilter, setLeaderboardStationFilter] = useState("");
  const [activityBreakdownSort, setActivityBreakdownSort] = useState({ key: "active_licenses", dir: "desc" });
  const [activityBreakdownPage, setActivityBreakdownPage] = useState(1);
  const [activityDetailType, setActivityDetailType] = useState(null); // null | "at_risk" | "power_users"
  const [activityDetailData, setActivityDetailData] = useState({});
  const [activityDetailLoading, setActivityDetailLoading] = useState(false);
  const [activityDetailError, setActivityDetailError] = useState(null);
  // Per-row rough breakdown inside the Breakdown tab's table (By Station/VISN/Specialty)
  const [rowDetailSelection, setRowDetailSelection] = useState(null); // null | { groupBy, groupKey, type }
  const [rowDetailData, setRowDetailData] = useState({}); // keyed by `${groupBy}|${groupKey}|${type}`
  const [rowDetailLoading, setRowDetailLoading] = useState(false);
  const [rowDetailError, setRowDetailError] = useState(null);
  const [reportYear, setReportYear] = useState(() => String(new Date().getFullYear()));
  const [reportMonth, setReportMonth] = useState(() => String(new Date().getMonth() + 1));
  const [exportingXlsx, setExportingXlsx] = useState(false);
  const [exportCount, setExportCount] = useState(null);
  const [exportCountLoading, setExportCountLoading] = useState(false);
  const [providerLookup, setProviderLookup] = useState("");
  const [providerLookupResults, setProviderLookupResults] = useState(null);
  const [providerLookupLoading, setProviderLookupLoading] = useState(false);
  const [providerLookupSuggestOpen, setProviderLookupSuggestOpen] = useState(false);
  const [providerLookupSelected, setProviderLookupSelected] = useState(null); // full row for the picked suggestion
  const providerLookupInputRef = useRef(null);
  const [providerLookupDropdownPos, setProviderLookupDropdownPos] = useState(null);
  const updateProviderLookupDropdownPos = () => {
    const rect = providerLookupInputRef.current?.getBoundingClientRect();
    if (rect) setProviderLookupDropdownPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
  };
  // The suggestion dropdown is rendered via a portal (see below) because this
  // card uses overflow:hidden for its collapse animation / rounded corners,
  // which would otherwise clip the dropdown. Reposition it on scroll/resize
  // while it's open, same pattern as InfoPopover above.
  useEffect(() => {
    if (!providerLookupSuggestOpen) return;
    updateProviderLookupDropdownPos();
    const handler = () => updateProviderLookupDropdownPos();
    window.addEventListener("scroll", handler, true);
    window.addEventListener("resize", handler);
    return () => {
      window.removeEventListener("scroll", handler, true);
      window.removeEventListener("resize", handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerLookupSuggestOpen]);
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchResults, setBatchResults] = useState(null); // null = no run yet
  const batchFileRef = useRef(null);
  const [bulkLookupLoading, setBulkLookupLoading] = useState(false);
  const [bulkLookupResults, setBulkLookupResults] = useState(null); // null = no run yet
  const [bulkLookupPasteText, setBulkLookupPasteText] = useState("");
  const [bulkLookupPasteOpen, setBulkLookupPasteOpen] = useState(false);
  const [licenseGrowth, setLicenseGrowth] = useState([]);
  const [launchedGrowth, setLaunchedGrowth] = useState([]);
  const [activeGrowth, setActiveGrowth] = useState([]);
  const [growth30d, setGrowth30d] = useState({ provisioned_30d: 0, launched_30d: 0 });
  const [activitySummary, setActivitySummary] = useState({
    active_licenses: 0, active_licenses_30d: 0, new_active_licenses_30d: 0, inactive_licenses: 0, at_risk_count: 0, power_user_count: 0, avg_dau: 0, avg_daily_patients: 0, dau_history: [],
  });
  // Overview card expand toggles — collapsed shows one headline number,
  // expanded reveals every metric in CardMetricList (easy to add more later).
  // Billing has no separate data source yet —
  // ponytail: provisionedMetrics' Billing row reads totalLicensed until billing counts exist.
  const [provisionedExpanded, setProvisionedExpanded] = useState(false);
  const [launchedExpanded, setLaunchedExpanded] = useState(false);
  const [activeExpanded, setActiveExpanded] = useState(false);
  const MONTH_NAMES = ["","January","February","March","April","May","June","July","August","September","October","November","December"];

  const runProviderLookup = async (query) => {
    if (!query.trim()) { setProviderLookupResults(null); return; }
    setProviderLookupLoading(true);
    try {
      const res = await fetchProviders({ search: query.trim(), limit: 10, allPeriods: true });
      setProviderLookupResults(res.data || []);
    } catch { setProviderLookupResults([]); }
    finally { setProviderLookupLoading(false); }
  };

  // /providers (used above) is also the query behind paginated station
  // rosters, so it's kept lean — no activity join there. To show Last Note /
  // Monthly / Cumulative Recordings for a single selected provider, reuse the
  // bulk-lookup endpoint (which already computes that) with a single entry,
  // matched by email if we have one, else the exact stored name. Runs after
  // the selection is already shown so the base detail appears instantly and
  // these three fields fill in a beat later.
  const fetchProviderLookupActivity = async (r) => {
    const name = r.provider_name || "";
    try {
      const res = await bulkLookupProviders({
        raws: [name || r.secid || ""],
        emails: [r.email || ""],
        namePatterns: [r.email ? "" : `%${name.toLowerCase()}%`],
      });
      const rows = res.data || [];
      const match = rows.find((m) => m.doctor_user_id === r.doctor_user_id) || rows[0];
      if (match) {
        setProviderLookupSelected((prev) => (prev && prev.doctor_user_id === r.doctor_user_id ? { ...prev, ...match } : prev));
      }
    } catch { /* leave base detail as-is if this fails */ }
  };

  // Live-as-you-type: search provider_activations for similar names/emails
  // while typing, debounced so it doesn't fire on every keystroke. Enter/the
  // Search button still work for an immediate lookup.
  useEffect(() => {
    const handle = setTimeout(() => { runProviderLookup(providerLookup); }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerLookup]);

  const runBatchLookup = async (file) => {
    if (!file) return;
    setBatchLoading(true);
    setBatchResults(null);
    try {
      const XLSX = await ensureXLSX();
      const ab = await file.arrayBuffer();
      const wb = XLSX.read(ab, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
      const entries = rows.flat().map(v => String(v).trim()).filter(Boolean);

      // Build parallel arrays for the server
      const raws = [], emails = [], namePatterns = [];
      entries.forEach(raw => {
        raws.push(raw);
        if (raw.includes("@")) {
          emails.push(raw); // server lowercases both sides
          // Also build a name fallback from email local part
          const local = raw.split("@")[0];
          const name = local.split(".").map(p => p.replace(/\d+$/, "")).filter(Boolean).join(" ");
          namePatterns.push(`%${name}%`);
        } else {
          emails.push("");
          namePatterns.push(`%${raw}%`);
        }
      });

      // Single request — one DB query
      const res = await batchLookupProviders({ raws, emails, namePatterns });
      const dbRows = res.data || [];

      // Group DB results by matched_raw
      const byRaw = {};
      dbRows.forEach(r => {
        const key = r.matched_raw || "";
        if (!byRaw[key]) byRaw[key] = [];
        byRaw[key].push(r);
      });

      // Build output rows preserving input order
      const outputRows = [];
      raws.forEach(raw => {
        const found = byRaw[raw] || [];
        if (found.length === 0) {
          outputRows.push({
            "Input": raw,
            "Found in DB": "No",
            "Launch Detail": "",
            "Status": "Not Found",
            "Provider Name": "",
            "Station": "",
            "VISN": "",
            "Specialty": "",
            "Date Activated": "",
          });
        } else {
          found.forEach(r => {
            const isPlaceholder = (r.provider_name || "").trim().toLowerCase() === "placeholder";
            outputRows.push({
              "Input": raw,
              "Found in DB": "Yes",
              "Launch Detail": isPlaceholder ? "Not Launched" : "Launched",
              "Status": r.date_activated ? "Licensed & Launched" : "Licensed (Not Yet Launched)",
              "Provider Name": r.provider_name || "",
              "Station": r.station_id || "",
              "VISN": r.visn ? `VISN ${r.visn}` : "",
              "Specialty": r.specialty || "",
              "Date Activated": r.date_activated ? new Date(r.date_activated).toLocaleDateString("en-US") : "",
            });
          });
        }
      });

      setBatchResults({ rows: outputRows, total: raws.length });
    } catch (err) { console.error("Batch lookup error:", err); }
    finally { setBatchLoading(false); }
  };

  const downloadBatchXlsx = async () => {
    if (!batchResults) return;
    const XLSX = await ensureXLSX();
    const ws = XLSX.utils.json_to_sheet(batchResults.rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Lookup Results");
    XLSX.writeFile(wb, `provider_lookup_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  // "Batch lookup" tab (inside the Provisioned Licenses card's Batch Lookup
  // tab, alongside the single-provider typeahead). Same matching approach as
  // the Email Outreach Check tab's upload flow, but paste-based and surfaces
  // launch + activity history so you can see it on-screen instead of only
  // via download.
  // Shared by both entry points (file upload and paste-from-spreadsheet) —
  // takes a flat list of raw cell values and runs the actual lookup.
  const lookupBulkEntries = async (entries) => {
    setBulkLookupLoading(true);
    setBulkLookupResults(null);
    try {
      // Provider names in this system appear to be stored "Last,First" (VA
      // roster convention), not "First Last". A plain name input is tried
      // against BOTH word orders — wildcarded between tokens so punctuation
      // like the comma doesn't block a match — so "Claire Charles" matches
      // a record stored as "Charles,Claire" or "Charles, Claire MD" etc.
      // Everything is lowercased here (in addition to the server also
      // lowering both sides) so matching is reliably case-insensitive, and
      // patterns are plain "contains" wildcards rather than requiring an
      // exact/anchored match.
      const namePatternsFor = (raw) => {
        const lower = raw.toLowerCase();
        const tokens = lower.split(/\s+/).filter(Boolean);
        if (tokens.length < 2) return [`%${lower}%`];
        const original = `%${tokens.join("%")}%`; // e.g. %claire%charles%
        const surnameFirst = `%${[tokens[tokens.length - 1], ...tokens.slice(0, -1)].join("%")}%`; // %charles%claire%
        return [...new Set([original, surnameFirst])];
      };

      // Note: entries may expand into more than one (raw, pattern) pair sent
      // to the API (see namePatternsFor above) — so these arrays can be
      // longer than `entries`. Results are still grouped back by raw text
      // below, and the output loop iterates the original unique `entries`
      // list so multiple candidate patterns don't produce duplicate rows.
      // Emails are sent lowercased too — the server matches them as a
      // case-insensitive "contains" now, not exact equality, so a partial
      // email still matches.
      const raws = [], emails = [], namePatterns = [];
      entries.forEach(raw => {
        const isEmail = raw.includes("@");
        // Recognize which kind of entry this is, then normalize both the
        // same way: reduce it to a "name candidate" and run it through the
        // single shared namePatternsFor(...) below, so a pasted name and a
        // pasted email get identical token-wildcarding + surname-first
        // handling instead of two divergent code paths. For emails, the name
        // candidate is guessed from the local part (dot-separated, trailing
        // digits stripped) — it's a fallback in case the stored email
        // differs (e.g. personal vs. VA email); for plain names, the raw
        // text itself is the candidate.
        const nameCandidate = isEmail
          ? raw.split("@")[0].split(".").map(p => p.replace(/\d+$/, "")).filter(Boolean).join(" ")
          : raw;
        const patterns = nameCandidate ? namePatternsFor(nameCandidate) : [""];
        patterns.forEach((pattern) => {
          raws.push(raw);
          emails.push(isEmail ? raw.toLowerCase() : "");
          namePatterns.push(pattern);
        });
      });

      const res = await bulkLookupProviders({ raws, emails, namePatterns });
      const dbRows = res.data || [];

      const byRaw = {};
      dbRows.forEach(r => {
        const key = r.matched_raw || "";
        if (!byRaw[key]) byRaw[key] = [];
        byRaw[key].push(r);
      });

      const outputRows = [];
      entries.forEach(raw => {
        const found = byRaw[raw] || [];
        if (found.length === 0) {
          outputRows.push({
            "Input": raw,
            "Found in DB": "No",
            "Launched": "",
            "Provider Name": "",
            "Station": "",
            "VISN": "",
            "Specialty": "",
            "Date Activated": "",
            "Last Note": "",
            "Monthly Recordings": "",
            "Cumulative Recordings": "",
          });
        } else {
          found.forEach(r => {
            const isPlaceholder = (r.provider_name || "").trim().toLowerCase() === "placeholder";
            outputRows.push({
              "Input": raw,
              "Found in DB": "Yes",
              "Launched": isPlaceholder ? "Not Launched" : "Launched",
              "Provider Name": r.provider_name || "",
              "Station": r.station_id || "",
              "VISN": r.visn ? `VISN ${r.visn}` : "",
              "Specialty": r.specialty || "",
              "Date Activated": r.date_activated ? new Date(r.date_activated).toLocaleDateString("en-US") : "",
              "Last Note": r.last_note_date ? new Date(r.last_note_date).toLocaleDateString("en-US") : "Never",
              "Monthly Recordings": r.monthly_recordings ?? 0,
              "Cumulative Recordings": r.cumulative_recordings ?? 0,
            });
          });
        }
      });

      setBulkLookupResults({ rows: outputRows, total: entries.length });
    } catch (err) { console.error("Bulk lookup error:", err); }
    finally { setBulkLookupLoading(false); }
  };

  // Paste-from-spreadsheet: copying cells out of Excel/Sheets and pasting into
  // a textarea yields tab-separated columns per line (or just one value per
  // line for a single column) — split on both and flatten.
  const runBulkLookupPaste = async (text) => {
    const entries = text
      .split(/\r?\n/)
      .flatMap((line) => line.split(/\t|,/))
      .map((v) => v.trim())
      .filter(Boolean);
    if (entries.length === 0) return;
    await lookupBulkEntries(entries);
  };

  // Export fetches the full dataset
  const exportXlsx = async () => {
    setExportingXlsx(true);
    try {
      const res = await fetchMonthlyReport({ report_year: reportYear, report_month: reportMonth, limit: 10000, exclude_decommissioned: true });
      const allRows = res.data || [];
      const XLSX = await ensureXLSX();
      const rows = allRows.map(r => ({
        "Provider Name": r.provider_name || "",
        "SECID": r.secid || "",
        "Station ID": r.station_id || "",
        "Site Name": r.site_name || "",
        "VISN": r.visn ? `VISN ${r.visn}` : "",
        "Specialty": r.specialty || "",
        "Date Activated": r.date_activated ? new Date(r.date_activated).toLocaleDateString("en-US") : "",
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, `${MONTH_NAMES[parseInt(reportMonth)]} ${reportYear}`);
      XLSX.writeFile(wb, `monthly_report_${reportYear}_${String(reportMonth).padStart(2,"0")}.xlsx`);
    } catch {}
    finally { setExportingXlsx(false); }
  };
  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const dragStartWidth = useRef(0);
  const containerRef = useRef(null);

  const [stations, setStations] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [visns, setVisns] = useState([]);
  // specialty → Set<3-char station prefix>, built once on load in background
  const [specialtyPrefixMap, setSpecialtyPrefixMap] = useState({});
  const [loading, setLoading] = useState(true);

  /* ── Initial load — all in one shot including the specialty→station map ── */
  useEffect(() => {
    const load = async () => {
      try {
        const [stRes, spRes, vRes, mapRes, wkRes] = await Promise.all([
          fetchStationList(), fetchSpecialties(), fetchVISNs(), fetchSpecialtyStationMap(), fetchWeeks(),
        ]);
        setStations(stRes.data || []);
        setSpecialties(spRes.specialties || []);
        setVisns(vRes.visns || []);
        // Convert arrays to Sets for O(1) lookup
        const map = {};
        for (const [sp, prefixes] of Object.entries(mapRes.map || {})) {
          map[sp] = new Set(prefixes);
        }
        setSpecialtyPrefixMap(map);
        setWeeks(wkRes.weeks || []);
      } catch (err) {
        console.error("Failed to load dashboard data:", err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);


  /* ── Close dropdown on outside click ── */
  useEffect(() => {
    const close = () => setOpenDropdown(null);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  /* ── Load specialty licensed/activated counts (By Specialty tab) — sourced
     directly from provider_activations, not the stations table rollup.
     Re-fetches with every selected VISN and RISE (empty = org-wide). ── */
  useEffect(() => {
    const isCurrent = specialtySummaryGuard.begin();
    setSpecialtySummaryLoading(true);
    fetchSpecialtySummary({
      visn: visnFilters.length > 0 ? visnFilters : undefined,
      new_visn: riseFilters.length > 0 ? riseFilters : undefined,
    })
      .then((res) => {
        if (isCurrent()) setSpecialtySummary(res.data || []);
      })
      .catch((e) => console.error("Failed to load specialty summary:", e))
      .finally(() => {
        if (isCurrent()) setSpecialtySummaryLoading(false);
      });
  }, [visnFilters, riseFilters]);

  // RISE is the parent filter — drop VISN selections that are no longer under
  // the selected RISE set (one RISE maps to many legacy VISNs).
  useEffect(() => {
    const allowed = visnsForRise(stations, riseFilters);
    setVisnFilters((prev) => {
      const next = pruneVisnFilters(prev, allowed);
      if (next.length === prev.length && next.every((v, i) => v === prev[i])) return prev;
      return next;
    });
  }, [riseFilters, stations]);

  useEffect(() => {
    const allowed = visnsForRise(stations, riseFilters);
    if (visnUtilSelected && !allowed.includes(String(visnUtilSelected))) {
      setVisnUtilSelected("");
      setVisnReport(null);
    }
  }, [riseFilters, stations, visnUtilSelected]);

  /* ── Load Active Licenses "Breakdown" data lazily, per inner tab, cached by group ──
     Gate moved from the old Breakdowns tab (toolCategory==="breakdowns" && breakdownSource==="active")
     to the Trends tab's Active metric filter, then to the User Flagging &
     Outreach tab, and now to its own toggle ("At-Risk & Power Users") inside
     the Engagement tab, since that's where this table now lives. */
  useEffect(() => {
    if (toolCategory !== "trends" || momMetric !== "all" || launchUtilView !== "breakdown") return;
    const groupBy = ACTIVITY_BREAKDOWN_GROUP_BY[activityBreakdownInner];
    if (!groupBy || activityBreakdownData[groupBy]) return;
    setActivityBreakdownLoading(true);
    fetchActivityBreakdown(groupBy)
      .then((res) => setActivityBreakdownData((prev) => ({ ...prev, [groupBy]: res.data || [] })))
      .catch((e) => console.error("Failed to load activity breakdown:", e))
      .finally(() => setActivityBreakdownLoading(false));
  }, [toolCategory, momMetric, launchUtilView, activityBreakdownInner, activityBreakdownData]);

  /* ── Load the same "Active Licenses" (recording-based) breakdown for the
     Utilization Report's browsable station/specialty lists — those lists
     were showing activated_to_date ("launched") in their Active column,
     which is a different metric than "has at least one recorded encounter."
     This fetches both groupings once, cached alongside the User Flagging &
     Outreach tab's copy of the same data (same activityBreakdownData cache,
     keyed by groupBy). ── */
  useEffect(() => {
    if (!(momMetric === "all" && launchUtilView === "utilization")) return;
    ["station_id", "specialty", "visn", "new_visn"].forEach((groupBy) => {
      if (activityBreakdownData[groupBy]) return;
      fetchActivityBreakdown(groupBy)
        .then((res) => setActivityBreakdownData((prev) => ({ ...prev, [groupBy]: res.data || [] })))
        .catch((e) => console.error("Failed to load activity breakdown for utilization report:", e));
    });
  }, [momMetric, launchUtilView, activityBreakdownData]);

  /* ── The Platform Utilization panel's Station/Site "Active" tiles are
     recording-based (via stationActiveLicensesById/groupActiveLicenses for
     Station, siteActiveLicensesById for Site) and render for the whole
     Engagement tab, not just the Utilization Report or Breakdown sub-views —
     so make sure both groupings are fetched as soon as the Engagement tab
     opens, regardless of which toggle is selected, instead of only lazily on
     the other two effects above (which only fetch "station_id"). ── */
  useEffect(() => {
    if (momMetric !== "all") return;
    ["station_id", "station_id_raw"].forEach((groupBy) => {
      if (activityBreakdownData[groupBy]) return;
      fetchActivityBreakdown(groupBy)
        .then((res) => setActivityBreakdownData((prev) => ({ ...prev, [groupBy]: res.data || [] })))
        .catch((e) => console.error("Failed to load activity breakdown for platform utilization panel:", e));
    });
  }, [momMetric, activityBreakdownData]);

  /* ── Leaderboard tab — (re)fetch whenever it's open and any filter changes. ── */
  useEffect(() => {
    if (momMetric !== "leaderboard") return;
    setLeaderboardLoading(true);
    fetchRecordingLeaderboard({
      days: 30,
      cumulative: leaderboardCumulative,
      specialty: leaderboardSpecialtyFilter,
      visn: leaderboardVisnFilter,
      station_id: leaderboardStationFilter,
    })
      .then((res) => { setLeaderboardData(res.data || []); })
      .catch((e) => console.error("Failed to load recording leaderboard:", e))
      .finally(() => setLeaderboardLoading(false));
  }, [momMetric, leaderboardCumulative, leaderboardSpecialtyFilter, leaderboardVisnFilter, leaderboardStationFilter]);

  /* ── Fetch export count when year/month changes ── */
  useEffect(() => {
    if (!reportYear || !reportMonth) return;
    setExportCountLoading(true);
    setExportCount(null);
    fetchMonthlyReportTotal({ report_year: reportYear, report_month: reportMonth })
      .then((total) => setExportCount(total))
      .catch(() => setExportCount(null))
      .finally(() => setExportCountLoading(false));
  }, [reportYear, reportMonth]);

  /* ── Fetch licensed growth data + lambda exclusions ──
     The growth endpoint returns a gross "new_count" per month, which doesn't
     account for decommissioning and so drifts from the monthly report export
     (a net "total licensed as of month-end, excluding currently-decommissioned
     providers" snapshot). To keep the bar chart in sync with that export, we
     re-derive each month's count as the delta between consecutive monthly
     report snapshot totals — the same source/filter the export uses — instead
     of trusting the growth endpoint's gross figure. */
  useEffect(() => {
    fetchLicensedGrowth()
      .then(async (res) => {
        const rows = res.data || [];
        if (rows.length === 0) { setLicenseGrowth([]); return; }

        const monthKeys = rows.map((r) => r.month); // "YYYYMM"
        const firstYear  = parseInt(monthKeys[0].slice(0, 4), 10);
        const firstMonth = parseInt(monthKeys[0].slice(4, 6), 10);
        const baselineMonth = firstMonth === 1 ? 12 : firstMonth - 1;
        const baselineYear  = firstMonth === 1 ? firstYear - 1 : firstYear;
        const baselineKey = `${baselineYear}${String(baselineMonth).padStart(2, "0")}`;
        const allKeys = [baselineKey, ...monthKeys];

        // One batched request for every month (instead of one request per
        // month) — this is what actually cuts down the very first page load,
        // where nothing is cached yet.
        const totalsByPeriod = await fetchMonthlyReportTotalsBatch(allKeys).catch(() => ({}));
        const totals = allKeys.map((key) => totalsByPeriod[key] ?? null);

        const withNet = rows.map((r, i) => {
          const prevTotal = totals[i];
          const curTotal  = totals[i + 1];
          const net = (prevTotal != null && curTotal != null) ? (curTotal - prevTotal) : null;
          // Fall back to the endpoint's gross count only if a snapshot call failed.
          return { ...r, new_count: net != null ? net : parseInt(r.new_count || 0, 10) };
        });
        setLicenseGrowth(withNet);
      })
      .catch(() => {});
    fetchLaunchedGrowth()
      .then((res) => setLaunchedGrowth(res.data || []))
      .catch(() => {});
    fetchActiveGrowth()
      .then((res) => setActiveGrowth(res.data || []))
      .catch(() => {});
    fetchDecommissionedGrowth()
      .then((res) => setDecommissionedGrowth(res.data || []))
      .catch(() => {});
    fetchGrowth30d()
      .then((res) => setGrowth30d({ provisioned_30d: res.provisioned_30d ?? 0, launched_30d: res.launched_30d ?? 0 }))
      .catch(() => {});
    fetchActivitySummary()
      .then((res) => {
        if (res?.error) { console.error("Activity summary error:", res.error, res.detail || ""); return; }
        setActivitySummary({
          active_licenses: res.active_licenses ?? 0,
          active_licenses_30d: res.active_licenses_30d ?? 0,
          new_active_licenses_30d: res.new_active_licenses_30d ?? 0,
          inactive_licenses: res.inactive_licenses ?? 0,
          at_risk_count: res.at_risk_count ?? 0,
          power_user_count: res.power_user_count ?? 0,
          avg_dau: res.avg_dau ?? 0,
          avg_daily_patients: res.avg_daily_patients ?? 0,
          dau_history: res.dau_history || [],
        });
      })
      .catch((err) => console.error("Failed to fetch activity summary:", err));
  }, []);


  /* ── Resize panel drag ── */
  const onDividerMouseDown = (e) => {
    e.preventDefault();
    isDragging.current = true;
    dragStartX.current = e.clientX;
    dragStartWidth.current = rightPanelWidth;
    const onMove = (ev) => {
      if (!isDragging.current) return;
      const delta = dragStartX.current - ev.clientX; // dragging left = wider right panel
      const containerW = containerRef.current?.offsetWidth ?? 900;
      const newW = Math.min(Math.max((dragStartWidth.current ?? containerW * 0.6) + delta, 260), containerW - 200);
      setRightPanelWidth(newW);
    };
    const onUp = () => {
      isDragging.current = false;
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  /* ── User Journey modal resize (drag the bottom-right corner handle) ──
     The panel is a non-blocking floating window (no full-screen backdrop),
     so there's no click-to-dismiss handler to worry about accidentally
     triggering here anymore — just the size update itself. */
  const onUserJourneyResizeMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startY = e.clientY;
    const startSize = userJourneyModalSize || { width: 780, height: Math.round(window.innerHeight * 0.85) };
    const onMove = (ev) => {
      const newWidth = Math.min(Math.max(startSize.width + (ev.clientX - startX), 480), window.innerWidth - 48);
      const newHeight = Math.min(Math.max(startSize.height + (ev.clientY - startY), 360), window.innerHeight - 48);
      setUserJourneyModalSize({ width: newWidth, height: newHeight });
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  /* ── User Journey modal drag (move the whole panel via its header) ──
     Reads the panel's current on-screen position via getBoundingClientRect
     so it works whether the panel is still centered (via CSS transform) or
     has already been dragged before — either way we convert to absolute
     left/top pixel coordinates once the drag starts. */
  const onUserJourneyDragMouseDown = (e) => {
    e.preventDefault();
    const panelEl = e.currentTarget.parentElement;
    const rect = panelEl.getBoundingClientRect();
    const startX = e.clientX;
    const startY = e.clientY;
    const startLeft = rect.left;
    const startTop = rect.top;
    const onMove = (ev) => {
      const newLeft = Math.min(Math.max(startLeft + (ev.clientX - startX), 0), window.innerWidth - 60);
      const newTop = Math.min(Math.max(startTop + (ev.clientY - startY), 0), window.innerHeight - 40);
      setUserJourneyModalPos({ x: newLeft, y: newTop });
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  /* ── Station Grouping modal resize/drag — same non-blocking floating
     pattern as the Provider Lifecycle panel above. ── */
  const onStationGroupingResizeMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startY = e.clientY;
    const startSize = stationGroupingModalSize || { width: 560, height: Math.round(window.innerHeight * 0.7) };
    const onMove = (ev) => {
      const newWidth = Math.min(Math.max(startSize.width + (ev.clientX - startX), 400), window.innerWidth - 48);
      const newHeight = Math.min(Math.max(startSize.height + (ev.clientY - startY), 300), window.innerHeight - 48);
      setStationGroupingModalSize({ width: newWidth, height: newHeight });
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  const onStationGroupingDragMouseDown = (e) => {
    e.preventDefault();
    const panelEl = e.currentTarget.parentElement;
    const rect = panelEl.getBoundingClientRect();
    const startX = e.clientX;
    const startY = e.clientY;
    const startLeft = rect.left;
    const startTop = rect.top;
    const onMove = (ev) => {
      const newLeft = Math.min(Math.max(startLeft + (ev.clientX - startX), 0), window.innerWidth - 60);
      const newTop = Math.min(Math.max(startTop + (ev.clientY - startY), 0), window.innerHeight - 40);
      setStationGroupingModalPos({ x: newLeft, y: newTop });
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  /* ── Generic drag-to-reposition for the non-blocking monthly-table popups
     (Provisioned/Launched/Active/Decommissioned) — same math as the handlers
     above, just parameterized by which position setter to update. ── */
  const makeTableDragHandler = (setPos) => (e) => {
    e.preventDefault();
    const panelEl = e.currentTarget.parentElement;
    const rect = panelEl.getBoundingClientRect();
    const startX = e.clientX;
    const startY = e.clientY;
    const startLeft = rect.left;
    const startTop = rect.top;
    const onMove = (ev) => {
      const newLeft = Math.min(Math.max(startLeft + (ev.clientX - startX), 0), window.innerWidth - 60);
      const newTop = Math.min(Math.max(startTop + (ev.clientY - startY), 0), window.innerHeight - 40);
      setPos({ x: newLeft, y: newTop });
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };
  const onLaunchedTableDragMouseDown = makeTableDragHandler(setLaunchedTablePos);
  const onProvisionedTableDragMouseDown = makeTableDragHandler(setProvisionedTablePos);
  const onActiveTableDragMouseDown = makeTableDragHandler(setActiveTablePos);
  const onDecommissionedTableDragMouseDown = makeTableDragHandler(setDecommissionedTablePos);


  /* ── Per-month delta → running-total rows, most-recent month first.
     Shared by the Provisioned/Launched card previews and their monthly-table
     popups so the cumulative math (and the anchor to the live Overview
     total for the latest month) only lives in one place. ── */
  const cumulativeGrowthRows = (growth, latestTotal) => {
    let cum = 0;
    const rows = growth.map((r) => {
      const delta = parseInt(r.new_count, 10) || 0;
      cum += delta;
      return { month: r.month, delta, total: cum };
    });
    // cum drifts from the true total by a constant offset whenever the
    // tracked growth window doesn't start from an actual zero baseline —
    // anchor every row to the live total via that offset, not just the last
    // one, or every earlier month shows a wrong (under/over-counted) total.
    if (rows.length > 0) {
      const offset = latestTotal - rows[rows.length - 1].total;
      rows.forEach((r) => { r.total += offset; });
    }
    return [...rows].reverse();
  };
  const decommissionedTotal = decommissionedGrowth.reduce((sum, r) => sum + (parseInt(r.new_count, 10) || 0), 0);

  /* ── Helpers ── */
  const licenseCount = (s) => s.licensed_to_date ?? s.activated_to_date ?? 0;
  const activeCount  = (s) => s.activated_to_date ?? 0;
  const mapReady = Object.keys(specialtyPrefixMap).length > 0;

  /* ── Doctor Drill-Down status (Utilization Report's station/specialty
     roster) — a doctor whose row's report_month no longer covers the current
     month (is_current_period === false, from /reports/station-utilization
     and /reports/specialty-utilization) is shown as "Decommissioned" instead
     of Active/Inactive, and is EXCLUDED from the "licensed"/"active" counts
     in the summary line above the table — matching /stations' licensed_to_date,
     which already requires the same current-period check. The row itself
     still stays visible in the table rather than being dropped. ── */
  const doctorStatusLabel = (d) => !d.is_current_period ? "Decommissioned" : d.has_recordings ? "Active" : "Inactive";
  const doctorStatusStyle = (d) => !d.is_current_period
    ? { bg: "rgba(120,120,140,0.1)", color: T.textMuted }
    : d.has_recordings
    ? { bg: T.greenSoft, color: T.green }
    : { bg: "rgba(120,120,140,0.1)", color: T.textMuted };

  /* ── Station grouping ──
     - station_id with extra characters after the 3-char base AND is_medical_center === true
       stands on its own (not rolled up).
     - station_id with extra characters after the 3-char base AND is_medical_center === false
       is grouped/rolled up under the 3-char base station_id.
     - station_id with no extra characters (already 3 chars or fewer) is keyed by itself. */
  const groupedStations = useMemo(() => Object.values(
    stations.reduce((acc, station) => {
      const rawId = String(station.station_id || "").trim();
      if (!rawId) return acc;
      const hasSuffix = rawId.length > 3;
      const isMedicalCenter = ["true", "t", "1", "yes", "y"].includes(String(station.is_medical_center).trim().toLowerCase());
      const groupKey = hasSuffix && isMedicalCenter ? rawId : (rawId.slice(0, 3) || rawId);
      if (!acc[groupKey]) {
        acc[groupKey] = { ...station, station_id: groupKey, station_ids: [], site_names: [], cities: [], raw_sites: [], licensed_to_date: 0, activated_to_date: 0 };
      }
      acc[groupKey].station_ids.push(rawId);
      if (station.site_name) acc[groupKey].site_names.push(station.site_name);
      if (station.city)      acc[groupKey].cities.push(station.city);
      acc[groupKey].raw_sites.push({ station_id: rawId, site_name: station.site_name || "" });
      acc[groupKey].licensed_to_date  += licenseCount(station);
      acc[groupKey].activated_to_date += activeCount(station);
      return acc;
    }, {})
  ).map((s) => {
    const ids    = [...new Set(s.station_ids)].sort();
    const names  = [...new Set(s.site_names)].sort();
    const cities = [...new Set(s.cities)].sort();
    // One entry per distinct raw station_id in this group (id + its own
    // name), so a rolled-up group can still be drilled into a single
    // sub-site — e.g. group "637" -> [{637, Charles George...}, {637GA, Franklin VA Clinic}].
    const subSites = [...new Map(s.raw_sites.map((r) => [r.station_id, r])).values()].sort((a, b) => a.station_id.localeCompare(b.station_id));
    return {
      ...s,
      sites_included: ids.join(", "),
      subsite_count:  ids.length,
      site_name: names.length  <= 1 ? names[0]  || "" : `${names.length} sites`,
      site_names: names, // every distinct sub-site name rolled into this group, for search matching
      sub_sites: subSites,
      city:      cities.length <= 1 ? cities[0] || "" : "Multiple",
    };
  }), [stations]);

  /* ── Raw station_id → site/sub-site name lookup, keyed by the exact
     (ungrouped) station_id — used by the User Flagging & Outreach "By
     Station" breakdown so its search box can match sub-site names too, the
     same way the Utilization tab's station search does. Deliberately not
     the rolled-up groupedStations map: the activity-breakdown endpoint
     groups by the raw station_id column, so lookups need to match 1:1
     against that same raw id. ── */
  const siteNameByStationId = useMemo(() => {
    const map = {};
    stations.forEach((s) => {
      const id = String(s.station_id || "").trim();
      if (id && !map[id]) map[id] = s.site_name || "";
    });
    return map;
  }, [stations]);

  /* ── Instant specialty filter prefixes (union across selected specialties) ── */
  const specialtyFilterPrefixes = useMemo(() => {
    if (dashSpecialties.length === 0 || !mapReady) return null;
    return new Set(dashSpecialties.flatMap((sp) => [...(specialtyPrefixMap[sp] || [])]));
  }, [dashSpecialties, specialtyPrefixMap, mapReady]);

  /* ── Instant available-specialties for selected RISE / VISNs ── */
  const availableSpecialties = useMemo(() => {
    if ((visnFilters.length === 0 && riseFilters.length === 0) || !mapReady) return null;
    const visnBaseIds = new Set(
      stations
        .filter((st) => {
          if (riseFilters.length > 0 && !riseFilters.includes(String(st.new_visn))) return false;
          if (visnFilters.length > 0 && !visnFilters.includes(String(st.visn))) return false;
          return true;
        })
        .map((st) => String(st.station_id || "").trim().slice(0, 3))
        .filter(Boolean)
    );
    const available = Object.entries(specialtyPrefixMap)
      .filter(([, prefixes]) => [...prefixes].some((p) => visnBaseIds.has(p)))
      .map(([sp]) => sp)
      .sort();
    return available.length > 0 ? available : null;
  }, [visnFilters, riseFilters, stations, specialtyPrefixMap, mapReady]);

  /* ── Filtered stations ── */
  const filtered = useMemo(() => groupedStations.filter((s) => {
    if (riseFilters.length > 0 && !riseFilters.includes(String(s.new_visn))) return false;
    if (visnFilters.length > 0 && !visnFilters.includes(String(s.visn))) return false;
    if (specialtyFilterPrefixes && !specialtyFilterPrefixes.has(String(s.station_id || "").slice(0, 3))) return false;
    if (search) {
      const q = search.toLowerCase();
      if (
        !(s.station_id     || "").toLowerCase().includes(q) &&
        !(s.sites_included || "").toLowerCase().includes(q) &&
        !(s.city           || "").toLowerCase().includes(q) &&
        !(s.site_names || []).some((n) => n.toLowerCase().includes(q))
      ) return false;
    }
    return true;
  }), [groupedStations, riseFilters, visnFilters, specialtyFilterPrefixes, search]);

  /* ── Metrics ── */
  const totalStations  = filtered.length;
  const activeStations = filtered.filter((x) => activeCount(x) > 0).length;
  const totalLicensed  = filtered.reduce((sum, x) => sum + licenseCount(x), 0);
  const activeLicensed = filtered.reduce((sum, x) => sum + activeCount(x), 0);

  const visnAgg = useMemo(() => Object.values(
    filtered.reduce((acc, s) => {
      const k = s.visn;
      if (!acc[k]) acc[k] = { visn: `VISN ${k}`, stations: 0, activeStations: 0, licensed: 0, activated: 0 };
      acc[k].stations++;
      if (activeCount(s) > 0) acc[k].activeStations++;
      acc[k].licensed  += licenseCount(s);
      acc[k].activated += activeCount(s);
      return acc;
    }, {})
  ), [filtered]);

  const riseAgg = useMemo(
    () => groupStationsByRise(filtered, { licenseCount, activeCount }),
    [filtered]
  );

  /* ── By Specialty aggregation — licensed/activated counts come straight from
     provider_activations (via /reports/specialty-summary, "VA Tech Sprint"
     already normalized to "Primary Care" server-side). The "stations" count
     here is just for display/drill-down and still uses specialtyPrefixMap,
     since a station's 3-char prefix can map to more than one specialty. ── */
  const specialtyAgg = useMemo(() => {
    return specialtySummary.map(({ specialty, licensed, activated }) => {
      const prefixes = specialtyPrefixMap[specialty] || new Set();
      const stations = filtered.filter((s) => prefixes.has(String(s.station_id || "").trim().slice(0, 3))).length;
      return { specialty, stations, licensed, activated };
    });
  }, [specialtySummary, filtered, specialtyPrefixMap]);

  const totalVisns    = visnAgg.length;
  const activeVisns   = visnAgg.filter((v) => v.activated > 0).length;
  const specialtyCount = specialties.length;

  /* ── Geography tab: license coverage by city/state ──
     Deliberately simpler than the offline license_geographical_mapping.html
     report it's based on: no Organization column or per-provider detail
     table (Doctor Lookup and By Station already cover that drill-down).
     Built from the raw `stations` list, NOT groupedStations — geography is
     purely a city/state rollup and shouldn't inherit the station-grouping
     rules (base station + rolled-up sub-sites) that exist for a totally
     different reason and cause "Multiple"/"N sites" artifacts here. */
  const geoStateAgg = useMemo(() => Object.values(
    stations.reduce((acc, s) => {
      const k = s.state || "Unknown";
      if (!acc[k]) acc[k] = { state: k, cities: new Set(), sites: 0, licensed: 0, activated: 0 };
      acc[k].cities.add(s.city || "Unknown");
      acc[k].sites++;
      acc[k].licensed  += licenseCount(s);
      acc[k].activated += activeCount(s);
      return acc;
    }, {})
  ).map((s) => ({ ...s, cities: s.cities.size })), [stations]);

  const geoCityAgg = useMemo(() => Object.values(
    stations.reduce((acc, s) => {
      const k = `${s.state || "Unknown"}|${s.city || "Unknown"}`;
      if (!acc[k]) acc[k] = { state: s.state || "Unknown", city: s.city || "Unknown", sites: 0, licensed: 0, activated: 0 };
      acc[k].sites++;
      acc[k].licensed  += licenseCount(s);
      acc[k].activated += activeCount(s);
      return acc;
    }, {})
  ), [stations]);

  const stationsWithLic    = filtered.filter((s) => licenseCount(s) > 0);

  /* ── Recording-based "Active" counts for the Utilization Report's
     browsable lists — sourced from the same Active Licenses breakdown as
     the User Flagging & Outreach tab (active_licenses = has at least one
     recorded encounter), NOT activated_to_date ("launched"). The breakdown
     endpoint groups by raw station_id, so a rolled-up station group's count
     is the sum across every raw id in that group. ── */
  const stationActiveLicensesById = useMemo(() => {
    const rows = activityBreakdownData.station_id || [];
    const map = {};
    rows.forEach((r) => { map[r.group_key] = r.active_licenses; });
    return map;
  }, [activityBreakdownData]);

  /* ── Per-RAW-site (not rolled-up) recording-based "Active" counts, for the
     Platform Utilization panel's Site tile. stationActiveLicensesById above
     is keyed by the CANONICAL/rolled-up station_id (e.g. "619"), so it can't
     distinguish one physical site from another inside a multi-site group —
     every raw sub-site in that group would resolve to the same canonical
     key (or, for ids that aren't the group's anchor id, to no key at all).
     station_id_raw groups the same activity-breakdown data by each doctor's
     literal, un-canonicalized station_id instead, matching how the raw
     `stations` list itself is keyed, so this actually varies per site. ── */
  const siteActiveLicensesById = useMemo(() => {
    const rows = activityBreakdownData.station_id_raw || [];
    const map = {};
    rows.forEach((r) => { map[r.group_key] = r.active_licenses; });
    return map;
  }, [activityBreakdownData]);
  const groupActiveLicenses = (group) => {
    // group.station_id is already the CANONICAL id for this whole rolled-up
    // group (computed by the same canonicalization rule — suffix + is_medical_center
    // -> keep it as-is, else take the 3-char prefix — that the backend applies
    // per-doctor in activity-breakdown's doctor_info CTE), and
    // stationActiveLicensesById is keyed by that same canonical id, already
    // summed across every raw sub-site in the group. So this is a single direct
    // lookup, not a sum over group.station_ids: those are the group's RAW
    // (un-canonicalized) sub-site ids, which only coincidentally match a
    // canonical key when the group happens to contain a bare anchor row (e.g.
    // "637" itself, alongside "637GA"/"637A4"). A group made up entirely of
    // suffixed sub-sites with no bare anchor row (no raw "637" in `stations`)
    // would have summed to 0 under the old per-raw-id lookup, even though the
    // canonical total is sitting right there under group.station_id.
    return stationActiveLicensesById[group.station_id] ?? 0;
  };

  const specialtyActiveLicensesByName = useMemo(() => {
    const rows = activityBreakdownData.specialty || [];
    const map = {};
    rows.forEach((r) => { map[r.group_key] = r.active_licenses; });
    return map;
  }, [activityBreakdownData]);

  /* ── Station/site options for the Leaderboard's station filter — every
     distinct raw station_id from the raw `stations` list (not the rolled-up
     groupedStations), since a leaderboard filter should be able to single out
     one specific site same as it can a whole canonical station. ── */
  const leaderboardStationOptions = useMemo(() => {
    const seen = new Map();
    stations.forEach((s) => {
      if (!s.station_id || seen.has(s.station_id)) return;
      seen.set(s.station_id, s.site_name || "");
    });
    return [...seen.entries()].sort((a, b) => a[0].localeCompare(b[0], undefined, { numeric: true }));
  }, [stations]);

  /* ── Recording-based "Active" counts per VISN, for the Utilization Report's
     By VISN list. Keyed by the RAW visn number (e.g. "5"), matching
     activity-breakdown's `visn` grouping (di.visn::text) — NOT visnAgg's
     display-formatted "VISN 5" key, so lookups must strip the "VISN " prefix
     first. ── */
  const visnActiveLicensesByNum = useMemo(() => {
    const rows = activityBreakdownData.visn || [];
    const map = {};
    rows.forEach((r) => { map[r.group_key] = r.active_licenses; });
    return map;
  }, [activityBreakdownData]);

  const riseActiveLicensesByNum = useMemo(() => {
    const rows = activityBreakdownData.new_visn || [];
    const map = {};
    rows.forEach((r) => { map[r.group_key] = r.active_licenses; });
    return map;
  }, [activityBreakdownData]);


  /* ── Station detail ── */
  const selectStation = async (station) => {
    if (selectedStation?.station_id === station.station_id) {
      setSelectedStation(null);
      setStationProviders([]);
      setStationWeekly([]);
      setProviderSearch("");
      setProviderPage(1);
      return;
    }
    setSelectedStation(station);
    setLoadingDetail(true);
    setStationProviders([]);
    setStationWeekly([]);
    setProviderSearch("");
    setProviderPage(1);
    try {
      const [provRes, weeklyRes] = await Promise.all([
        fetchProviders({ station_id: station.station_id, limit: 1000 }),
        fetchRecentWeekly({ station_id: station.station_id, limit: 4 }),
      ]);
      setStationProviders(provRes.data || []);
      setStationWeekly(weeklyRes.weeks || []);
    } catch (err) {
      console.error("Failed to load station detail:", err);
    } finally {
      setLoadingDetail(false);
    }
  };


  /* ── VISN detail ── */
  const selectVisn = (v) => {
    if (selectedVisn?.visn === v.visn) { setSelectedVisn(null); return; }
    setSelectedVisn(v);
  };

  const selectRise = (r) => {
    if (selectedRise?.riseKey === r.riseKey) { setSelectedRise(null); return; }
    setSelectedRise(r);
  };

  /* ── Specialty detail ── */
  const selectSpecialtyAgg = (sp) => {
    if (selectedSpecialtyAgg?.specialty === sp.specialty) { setSelectedSpecialtyAgg(null); return; }
    setSelectedSpecialtyAgg(sp);
    setSpecialtyDetailPage(1);
  };

  /* ── At Risk / Power Users detail — XLSX export of the full underlying list ── */
  const downloadActivityDetailXlsx = async (type) => {
    const rows = activityDetailData[type] || [];
    if (rows.length === 0) return;
    const XLSX = await ensureXLSX();
    const outRows = rows.map((p) => {
      if (type === "not_launched" || type === "inactive") {
        return {
          "Provider Name": p.provider_name || "",
          "Email": p.email || "",
          "Station ID": p.station_id || "",
          "Specialty": p.specialty || "",
          "Date Activated": p.date_activated ? new Date(p.date_activated).toLocaleDateString("en-US") : "",
          "Date Launched": p.date_launched ? new Date(p.date_launched).toLocaleDateString("en-US") : "",
        };
      }
      const isAtRisk = type === "at_risk";
      const daysIdle = p.last_appt ? Math.floor((Date.now() - new Date(p.last_appt).getTime()) / 86400000) : null;
      return isAtRisk
        ? {
            "Provider Name": p.provider_name || "",
            "Email": p.email || "",
            "Station ID": p.station_id || "",
            "Specialty": p.specialty || "",
            "Last Appointment": p.last_appt ? new Date(p.last_appt).toLocaleDateString("en-US") : "",
            "Days Idle": daysIdle ?? "",
          }
        : {
            "Provider Name": p.provider_name || "",
            "Email": p.email || "",
            "Station ID": p.station_id || "",
            "Specialty": p.specialty || "",
            "Recordings This Week": p.week_recordings ?? 0,
          };
    });
    const ws = XLSX.utils.json_to_sheet(outRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, ACTIVITY_DETAIL_LABELS[type] || type);
    XLSX.writeFile(wb, `${type}_providers_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  /* ── Station Utilization report — 2-tab XLSX export (Weekly Utilization +
     Doctor Drill-Down), same underlying data as the on-screen tables ── */
  const downloadStationUtilizationXlsx = async () => {
    if (!stationReport) return;
    const { station, weekly, doctors } = stationReport;
    const XLSX = await ensureXLSX();

    const weeklyRows = [...weekly].reverse().map((w) => ({
      "Week": w.week,
      "Total Recordings": w.total_recordings,
      "Total Patients": w.total_patients,
      "Utilization Score": w.utilization_score != null ? `${(w.utilization_score * 100).toFixed(2)}%` : "",
    }));

    const doctorRows = doctors.map((d) => {
      const isPlaceholder = (d.provider_name || "").trim().toLowerCase() === "placeholder";
      return {
        "Name": d.provider_name || "",
        "Email": d.email || "",
        "Specialty": d.specialty || "",
        "Created At": fmtDate(d.created_at),
        "Launched Date": isPlaceholder ? "Not Launched" : d.launched_date ? fmtDate(d.launched_date) : "",
        "Status": doctorStatusLabel(d),
      };
    });

    const wb = XLSX.utils.book_new();
    const weeklyWs = XLSX.utils.json_to_sheet(weeklyRows);
    weeklyWs["!cols"] = [{ wch: 14 }, { wch: 16 }, { wch: 14 }, { wch: 16 }];
    XLSX.utils.book_append_sheet(wb, weeklyWs, "Weekly Utilization");

    const doctorsWs = XLSX.utils.json_to_sheet(doctorRows);
    doctorsWs["!cols"] = [{ wch: 24 }, { wch: 28 }, { wch: 16 }, { wch: 14 }, { wch: 14 }, { wch: 10 }];
    XLSX.utils.book_append_sheet(wb, doctorsWs, "Doctor Drill-Down");

    XLSX.writeFile(wb, `station_utilization_${station.station_id}_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  /* ── Per-row rough breakdown (Breakdown tab) — XLSX export of the underlying
     provider list for that one station / VISN / specialty ── */
  const downloadRowDetailXlsx = async (groupBy, groupKey, type) => {
    const cacheKey = `${groupBy}|${groupKey}|${type}`;
    const rows = rowDetailData[cacheKey] || [];
    if (rows.length === 0) return;
    const isAtRisk = type === "at_risk";
    const XLSX = await ensureXLSX();
    const outRows = rows.map((p) => {
      const daysIdle = p.last_appt ? Math.floor((Date.now() - new Date(p.last_appt).getTime()) / 86400000) : null;
      return isAtRisk
        ? {
            "Provider Name": p.provider_name || "",
            "Email": p.email || "",
            "Station ID": p.station_id || "",
            "Specialty": p.specialty || "",
            "Last Appointment": p.last_appt ? new Date(p.last_appt).toLocaleDateString("en-US") : "",
            "Days Idle": daysIdle ?? "",
          }
        : {
            "Provider Name": p.provider_name || "",
            "Email": p.email || "",
            "Station ID": p.station_id || "",
            "Specialty": p.specialty || "",
            "Recordings This Week": p.week_recordings ?? 0,
          };
    });
    const ws = XLSX.utils.json_to_sheet(outRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, isAtRisk ? "At Risk" : "Power Users");
    const safeKey = String(groupKey).replace(/[^a-z0-9]+/gi, "_");
    XLSX.writeFile(wb, `${type}_${groupBy}_${safeKey}_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  /* ── At Risk / Power Users detail — toggled by clicking the stat number ── */
  const toggleActivityDetail = (type) => {
    if (activityDetailType === type) { setActivityDetailType(null); return; }
    setActivityDetailType(type);
    setActivityDetailError(null);
    if (!activityDetailData[type]) {
      setActivityDetailLoading(true);
      fetchActivityDetail(type)
        .then((res) => {
          if (res && res.error) throw new Error(res.error);
          setActivityDetailData((prev) => ({ ...prev, [type]: res.data || [] }));
        })
        .catch((e) => {
          console.error("Failed to load activity detail:", e);
          setActivityDetailError("Couldn't load this list — the server may need a restart to pick up this feature.");
        })
        .finally(() => setActivityDetailLoading(false));
    }
  };

  /* ── Station Utilization search — resolves the typed
     station, then loads its weekly recordings/patients/utilization report
     plus its doctor roster in one round trip. ── */
  const runStationSearch = async (q, opts) => {
    if (!q) return;
    const isCurrent = stationSearchGuard.begin();
    setStationReportLoading(true);
    setStationReportError(null);
    try {
      const res = await fetchStationUtilization(q, opts);
      if (!isCurrent()) return;
      if (res?.error) {
        setStationReportError(res.error);
        setStationReport(null);
      } else {
        setStationReport(res);
      }
    } catch (e) {
      if (!isCurrent()) return;
      console.error("Failed to load station utilization report:", e);
      setStationReportError("Failed to load station utilization report");
      setStationReport(null);
    } finally {
      if (isCurrent()) setStationReportLoading(false);
    }
  };
  const submitStationSearch = () => runStationSearch(stationSearchInput.trim());

  /* ── By Specialty utilization report — same round trip as Station
     Utilization above, scoped by specialty instead of station. ── */
  const runSpecialtySearch = async (specialty) => {
    if (!specialty) return;
    const isCurrent = specialtySearchGuard.begin();
    setSpecialtyReportLoading(true);
    setSpecialtyReportError(null);
    try {
      const res = await fetchSpecialtyUtilization(specialty);
      if (!isCurrent()) return;
      if (res?.error) {
        setSpecialtyReportError(res.error);
        setSpecialtyReport(null);
      } else {
        setSpecialtyReport(res);
      }
    } catch (e) {
      if (!isCurrent()) return;
      console.error("Failed to load specialty utilization report:", e);
      setSpecialtyReportError("Failed to load specialty utilization report");
      setSpecialtyReport(null);
    } finally {
      if (isCurrent()) setSpecialtyReportLoading(false);
    }
  };

  const downloadSpecialtyUtilizationXlsx = async () => {
    if (!specialtyReport) return;
    const { specialty, weekly, doctors } = specialtyReport;
    const XLSX = await ensureXLSX();

    const weeklyRows = [...weekly].reverse().map((w) => ({
      "Week": w.week,
      "Total Recordings": w.total_recordings,
      "Total Patients": w.total_patients,
      "Utilization Score": w.utilization_score != null ? `${(w.utilization_score * 100).toFixed(2)}%` : "",
    }));

    const doctorRows = doctors.map((d) => {
      const isPlaceholder = (d.provider_name || "").trim().toLowerCase() === "placeholder";
      return {
        "Name": d.provider_name || "",
        "Email": d.email || "",
        "Station": d.station_id || "",
        "Created At": fmtDate(d.created_at),
        "Launched Date": isPlaceholder ? "Not Launched" : d.launched_date ? fmtDate(d.launched_date) : "",
        "Status": doctorStatusLabel(d),
      };
    });

    const wb = XLSX.utils.book_new();
    const weeklyWs = XLSX.utils.json_to_sheet(weeklyRows);
    weeklyWs["!cols"] = [{ wch: 14 }, { wch: 16 }, { wch: 14 }, { wch: 16 }];
    XLSX.utils.book_append_sheet(wb, weeklyWs, "Weekly Utilization");

    const doctorsWs = XLSX.utils.json_to_sheet(doctorRows);
    doctorsWs["!cols"] = [{ wch: 24 }, { wch: 28 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 10 }];
    XLSX.utils.book_append_sheet(wb, doctorsWs, "Doctor Drill-Down");

    XLSX.writeFile(wb, `specialty_utilization_${specialty.replace(/[^a-z0-9]+/gi, "_")}_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  /* ── By VISN utilization report — same round trip as Station/Specialty
     Utilization above, scoped by VISN instead. `visn` here is the raw number
     (e.g. "5"), not the "VISN 5" display string used elsewhere. ── */
  const runVisnSearch = async (visn) => {
    if (!visn) return;
    const isCurrent = visnSearchGuard.begin();
    setVisnReportLoading(true);
    setVisnReportError(null);
    try {
      const res = await fetchVisnUtilization({ visn });
      if (!isCurrent()) return;
      if (res?.error) {
        setVisnReportError(res.error);
        setVisnReport(null);
      } else {
        setVisnReport(res);
      }
    } catch (e) {
      if (!isCurrent()) return;
      console.error("Failed to load VISN utilization report:", e);
      setVisnReportError("Failed to load VISN utilization report");
      setVisnReport(null);
    } finally {
      if (isCurrent()) setVisnReportLoading(false);
    }
  };

  const downloadVisnUtilizationXlsx = async () => {
    if (!visnReport) return;
    const { visn, weekly, doctors } = visnReport;
    const XLSX = await ensureXLSX();

    const weeklyRows = [...weekly].reverse().map((w) => ({
      "Week": w.week,
      "Total Recordings": w.total_recordings,
      "Total Patients": w.total_patients,
      "Utilization Score": w.utilization_score != null ? `${(w.utilization_score * 100).toFixed(2)}%` : "",
    }));

    const doctorRows = doctors.map((d) => {
      const isPlaceholder = (d.provider_name || "").trim().toLowerCase() === "placeholder";
      return {
        "Name": d.provider_name || "",
        "Email": d.email || "",
        "Station": d.station_id || "",
        "Specialty": d.specialty || "",
        "Created At": fmtDate(d.created_at),
        "Launched Date": isPlaceholder ? "Not Launched" : d.launched_date ? fmtDate(d.launched_date) : "",
        "Status": doctorStatusLabel(d),
      };
    });

    const wb = XLSX.utils.book_new();
    const weeklyWs = XLSX.utils.json_to_sheet(weeklyRows);
    weeklyWs["!cols"] = [{ wch: 14 }, { wch: 16 }, { wch: 14 }, { wch: 16 }];
    XLSX.utils.book_append_sheet(wb, weeklyWs, "Weekly Utilization");

    const doctorsWs = XLSX.utils.json_to_sheet(doctorRows);
    doctorsWs["!cols"] = [{ wch: 24 }, { wch: 28 }, { wch: 14 }, { wch: 16 }, { wch: 14 }, { wch: 14 }, { wch: 10 }];
    XLSX.utils.book_append_sheet(wb, doctorsWs, "Doctor Drill-Down");

    XLSX.writeFile(wb, `visn_utilization_VISN${visn}_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const runRiseSearch = async (new_visn) => {
    if (!new_visn) return;
    const isCurrent = riseSearchGuard.begin();
    setRiseReportLoading(true);
    setRiseReportError(null);
    try {
      const res = await fetchVisnUtilization({ new_visn });
      if (!isCurrent()) return;
      if (res?.error) {
        setRiseReportError(res.error);
        setRiseReport(null);
      } else {
        setRiseReport(res);
      }
    } catch (e) {
      if (!isCurrent()) return;
      console.error("Failed to load RISE utilization report:", e);
      setRiseReportError("Failed to load RISE utilization report");
      setRiseReport(null);
    } finally {
      if (isCurrent()) setRiseReportLoading(false);
    }
  };

  const downloadRiseUtilizationXlsx = async () => {
    if (!riseReport) return;
    const { new_visn, weekly, doctors } = riseReport;
    const XLSX = await ensureXLSX();

    const weeklyRows = [...weekly].reverse().map((w) => ({
      "Week": w.week,
      "Total Recordings": w.total_recordings,
      "Total Patients": w.total_patients,
      "Utilization Score": w.utilization_score != null ? `${(w.utilization_score * 100).toFixed(2)}%` : "",
    }));

    const doctorRows = doctors.map((d) => {
      const isPlaceholder = (d.provider_name || "").trim().toLowerCase() === "placeholder";
      return {
        "Name": d.provider_name || "",
        "Email": d.email || "",
        "Station": d.station_id || "",
        "Specialty": d.specialty || "",
        "Created At": fmtDate(d.created_at),
        "Launched Date": isPlaceholder ? "Not Launched" : d.launched_date ? fmtDate(d.launched_date) : "",
        "Status": doctorStatusLabel(d),
      };
    });

    const wb = XLSX.utils.book_new();
    const weeklyWs = XLSX.utils.json_to_sheet(weeklyRows);
    weeklyWs["!cols"] = [{ wch: 14 }, { wch: 16 }, { wch: 14 }, { wch: 16 }];
    XLSX.utils.book_append_sheet(wb, weeklyWs, "Weekly Utilization");

    const doctorsWs = XLSX.utils.json_to_sheet(doctorRows);
    doctorsWs["!cols"] = [{ wch: 24 }, { wch: 28 }, { wch: 14 }, { wch: 16 }, { wch: 14 }, { wch: 14 }, { wch: 10 }];
    XLSX.utils.book_append_sheet(wb, doctorsWs, "Doctor Drill-Down");

    XLSX.writeFile(wb, `rise_utilization_RISE${new_visn}_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  /* ── User Leaderboard export — top recording providers, current filter
     combination (period + specialty/VISN/station) baked into the column
     header and filename so an exported file is self-describing. ── */
  const leaderboardRecordingsLabel = leaderboardCumulative ? "Recordings (Cumulative)" : "Recordings (Last 30 Days)";
  const downloadLeaderboardXlsx = async () => {
    if (!leaderboardData.length) return;
    const XLSX = await ensureXLSX();
    const rows = leaderboardData.map((r) => ({
      "Rank": r.rank,
      "Name": r.provider_name || "",
      "Email": r.email || "",
      "Station": r.station_id || "",
      "Site Name": r.site_name || "",
      "VISN": r.visn || "",
      "Specialty": r.specialty || "",
      [leaderboardRecordingsLabel]: r.total_recordings,
    }));
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = [{ wch: 6 }, { wch: 24 }, { wch: 28 }, { wch: 10 }, { wch: 24 }, { wch: 6 }, { wch: 16 }, { wch: 22 }];
    XLSX.utils.book_append_sheet(wb, ws, "Leaderboard");
    const suffix = [
      leaderboardCumulative ? "cumulative" : "30d",
      leaderboardSpecialtyFilter ? leaderboardSpecialtyFilter.replace(/[^a-z0-9]+/gi, "_") : null,
      leaderboardVisnFilter ? `VISN${leaderboardVisnFilter}` : null,
      leaderboardStationFilter || null,
    ].filter(Boolean).join("_");
    XLSX.writeFile(wb, `recording_leaderboard_${suffix}_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  /* ── Per-row rough breakdown in the Breakdown tab's table — scoped to one
     station / VISN / specialty via the same group_by/group_key the row came from ── */
  const toggleRowDetail = (groupBy, groupKey, type) => {
    const cacheKey = `${groupBy}|${groupKey}|${type}`;
    const isSame = rowDetailSelection && rowDetailSelection.groupBy === groupBy
      && rowDetailSelection.groupKey === groupKey && rowDetailSelection.type === type;
    if (isSame) { setRowDetailSelection(null); return; }
    setRowDetailSelection({ groupBy, groupKey, type });
    setRowDetailError(null);
    if (!rowDetailData[cacheKey]) {
      setRowDetailLoading(true);
      fetchActivityDetail(type, { groupBy, groupKey })
        .then((res) => {
          if (res && res.error) throw new Error(res.error);
          setRowDetailData((prev) => ({ ...prev, [cacheKey]: res.data || [] }));
        })
        .catch((e) => {
          console.error("Failed to load row-level activity detail:", e);
          setRowDetailError("Couldn't load this breakdown — the server may need a restart to pick up this feature.");
        })
        .finally(() => setRowDetailLoading(false));
    }
  };

  /* ── Auto-select first station on load ── */
  useEffect(() => {
    if (!loading && groupedStations.length > 0 && weeks.length > 0 && !hasAutoSelected.current) {
      hasAutoSelected.current = true;
      const first = [...groupedStations].sort((a, b) => {
        const diff = activeCount(b) - activeCount(a);
        return diff !== 0 ? diff : (a.station_id || "").localeCompare(b.station_id || "");
      })[0];
      if (first) selectStation(first);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, groupedStations, weeks]);

  /* ── Auto-select first row on tab switch ── */
  useEffect(() => {
    if (loading) return;
    if (dashInner === "by-station") {
      const first = [...filtered].sort((a, b) => {
        const lic_a = licenseCount(a), act_a = activeCount(a), pct_a = lic_a > 0 ? act_a / lic_a : 0;
        const lic_b = licenseCount(b), act_b = activeCount(b), pct_b = lic_b > 0 ? act_b / lic_b : 0;
        let diff = 0;
        if (stationSort.key === "launched") diff = act_b - act_a;
        else if (stationSort.key === "total") diff = lic_b - lic_a;
        else if (stationSort.key === "adoption") diff = pct_b - pct_a;
        else diff = (a.station_id || "").localeCompare(b.station_id || "");
        if (stationSort.dir === "asc") diff = -diff;
        return diff !== 0 ? diff : (a.station_id || "").localeCompare(b.station_id || "");
      })[0];
      if (first && first.station_id !== selectedStation?.station_id) selectStation(first);
    } else if (dashInner === "by-visn") {
      const first = [...visnAgg].sort((a, b) => {
        const pct_a = a.licensed > 0 ? a.activated / a.licensed : 0;
        const pct_b = b.licensed > 0 ? b.activated / b.licensed : 0;
        let diff = 0;
        if (visnSort.key === "launched") diff = b.activated - a.activated;
        else if (visnSort.key === "total") diff = b.licensed - a.licensed;
        else if (visnSort.key === "adoption") diff = pct_b - pct_a;
        else diff = a.visn.localeCompare(b.visn, undefined, { numeric: true });
        if (visnSort.dir === "asc") diff = -diff;
        return diff !== 0 ? diff : a.visn.localeCompare(b.visn, undefined, { numeric: true });
      })[0];
      if (first && first.visn !== selectedVisn?.visn) selectVisn(first);
    } else if (dashInner === "by-rise") {
      const first = [...riseAgg].sort((a, b) => {
        const pct_a = a.licensed > 0 ? a.activated / a.licensed : 0;
        const pct_b = b.licensed > 0 ? b.activated / b.licensed : 0;
        let diff = 0;
        if (riseSort.key === "launched") diff = b.activated - a.activated;
        else if (riseSort.key === "total") diff = b.licensed - a.licensed;
        else if (riseSort.key === "adoption") diff = pct_b - pct_a;
        else diff = a.rise.localeCompare(b.rise, undefined, { numeric: true });
        if (riseSort.dir === "asc") diff = -diff;
        return diff !== 0 ? diff : a.rise.localeCompare(b.rise, undefined, { numeric: true });
      })[0];
      if (first && first.riseKey !== selectedRise?.riseKey) selectRise(first);
    } else if (dashInner === "by-specialty") {
      const first = [...specialtyAgg].sort((a, b) => {
        const pct_a = a.licensed > 0 ? a.activated / a.licensed : 0;
        const pct_b = b.licensed > 0 ? b.activated / b.licensed : 0;
        let diff = 0;
        if (specialtySort.key === "launched") diff = b.activated - a.activated;
        else if (specialtySort.key === "total") diff = b.licensed - a.licensed;
        else if (specialtySort.key === "adoption") diff = pct_b - pct_a;
        else diff = a.specialty.localeCompare(b.specialty);
        if (specialtySort.dir === "asc") diff = -diff;
        return diff !== 0 ? diff : a.specialty.localeCompare(b.specialty);
      })[0];
      if (first && first.specialty !== selectedSpecialtyAgg?.specialty) selectSpecialtyAgg(first);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashInner]);

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: T.textDim }}>Loading dashboard...</div>;

  /* ── Shared UI ── */
  const bgPill = {
    display: "inline-flex", alignItems: "center", gap: 6,
    padding: "7px 14px", borderRadius: 20,
    border: `1px solid ${T.border}`, background: T.bg,
    color: T.text, fontSize: 13, fontWeight: 500, fontFamily: T.font,
    cursor: "pointer", userSelect: "none",
  };
  const bgPillActive = { ...bgPill, background: T.accentSoft, borderColor: T.accent, color: T.accent };
  const dropStyle = {
    position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 200,
    background: T.bg, border: `1px solid ${T.border}`,
    borderRadius: T.radiusSm, padding: "6px 0",
    minWidth: 200, maxHeight: 260, overflowY: "auto",
    boxShadow: T.shadow,
  };
  const checkRowSt = (active) => ({
    padding: "7px 12px", display: "flex", alignItems: "center", gap: 8,
    cursor: "pointer", fontSize: 13, color: T.text,
    background: active ? T.accentSoft : "transparent",
  });
  const checkBoxSt = (checked) => ({
    width: 14, height: 14, borderRadius: 3, flexShrink: 0,
    border: `1.5px solid ${checked ? T.accent : T.border}`,
    background: checked ? T.accent : "transparent",
    display: "flex", alignItems: "center", justifyContent: "center",
  });
  const Tick = () => <svg width="9" height="9" viewBox="0 0 12 12" fill="none" stroke="#fff" strokeWidth="2.5"><path d="M2 6l3 3 5-5"/></svg>;
  const Chevron = () => <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={T.textDim} strokeWidth="2.5"><path d="M6 9l6 6 6-6"/></svg>;

  const toggleRise      = (v)  => { setRiseFilters((p)      => p.includes(v)  ? p.filter((x) => x !== v)  : [...p, v]);  setStationPage(1); };
  const toggleVisn      = (v)  => { setVisnFilters((p)      => p.includes(v)  ? p.filter((x) => x !== v)  : [...p, v]);  setStationPage(1); };
  const toggleSpecialty = (sp) => { setDashSpecialties((p)  => p.includes(sp) ? p.filter((x) => x !== sp) : [...p, sp]); setStationPage(1); };
  const clearAll = () => { setRiseFilters([]); setVisnFilters([]); setDashSpecialties([]); setSearch(""); setStationPage(1); };

  const riseOptions = distinctRiseNumbers(stations);
  const visnOptions = visnsForRise(stations, riseFilters);

  const risePillLabel = riseFilters.length === 0 ? "RISE"
    : riseFilters.length === 1 ? `RISE ${riseFilters[0]}`
    : `${riseFilters.length} RISEs`;
  const visnPillLabel = visnFilters.length === 0 ? "VISN"
    : visnFilters.length === 1 ? `VISN ${visnFilters[0]}`
    : `${visnFilters.length} VISNs`;
  const specPillLabel = dashSpecialties.length === 0 ? "Specialty"
    : dashSpecialties.length === 1 ? dashSpecialties[0]
    : `${dashSpecialties.length} Specialties`;

  const displaySpecialties = availableSpecialties ?? specialties;
  const hasAnyFilter = riseFilters.length > 0 || visnFilters.length > 0 || dashSpecialties.length > 0 || search;

  const RisePill = ({ id }) => (
    <div style={{ position: "relative" }} onMouseDown={(e) => e.stopPropagation()}>
      <div style={riseFilters.length > 0 ? bgPillActive : bgPill}
        onMouseDown={(e) => { e.stopPropagation(); setOpenDropdown(openDropdown === id ? null : id); }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
        <span>{risePillLabel}</span><Chevron />
      </div>
      {openDropdown === id && (
        <div style={dropStyle}>
          {riseOptions.length === 0 ? (
            <div style={{ padding: "10px 12px", fontSize: 13, color: T.textDim }}>No RISE values</div>
          ) : riseOptions.map((v) => {
            const val = String(v), checked = riseFilters.includes(val);
            return (
              <div key={v} style={checkRowSt(checked)} onMouseDown={(e) => e.stopPropagation()} onClick={() => toggleRise(val)}>
                <div style={checkBoxSt(checked)}>{checked && <Tick />}</div>
                <span>RISE {v}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  const VisnPill = ({ id }) => (
    <div style={{ position: "relative" }} onMouseDown={(e) => e.stopPropagation()}>
      <div style={visnFilters.length > 0 ? bgPillActive : bgPill}
        onMouseDown={(e) => { e.stopPropagation(); setOpenDropdown(openDropdown === id ? null : id); }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
        <span>{visnPillLabel}</span><Chevron />
      </div>
      {openDropdown === id && (
        <div style={dropStyle}>
          {visnOptions.length === 0 ? (
            <div style={{ padding: "10px 12px", fontSize: 13, color: T.textDim }}>
              {riseFilters.length > 0 ? "No VISNs in selected RISE" : "No VISNs"}
            </div>
          ) : visnOptions.map((v) => {
            const val = String(v), checked = visnFilters.includes(val);
            return (
              <div key={v} style={checkRowSt(checked)} onMouseDown={(e) => e.stopPropagation()} onClick={() => toggleVisn(val)}>
                <div style={checkBoxSt(checked)}>{checked && <Tick />}</div>
                <span>VISN {v}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  const SpecPill = ({ id }) => (
    <div style={{ position: "relative" }} onMouseDown={(e) => e.stopPropagation()}>
      <div style={dashSpecialties.length > 0 ? bgPillActive : bgPill}
        onMouseDown={(e) => { e.stopPropagation(); setOpenDropdown(openDropdown === id ? null : id); }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2a4 4 0 014 4c0 2-2 3.5-4 6-2-2.5-4-4-4-6a4 4 0 014-4z"/><path d="M4 21c0-3.5 3.5-6 8-6s8 2.5 8 6"/></svg>
        <span>{specPillLabel}</span><Chevron />
      </div>
      {openDropdown === id && (
        <div style={dropStyle}>
          {!mapReady ? (
            <div style={{ padding: "10px 12px", fontSize: 13, color: T.textDim }}>Loading…</div>
          ) : displaySpecialties.length === 0 ? (
            <div style={{ padding: "10px 12px", fontSize: 13, color: T.textDim }}>No specialties in selected RISE / VISN</div>
          ) : displaySpecialties.map((sp) => {
            const checked = dashSpecialties.includes(sp);
            return (
              <div key={sp} style={checkRowSt(checked)} onMouseDown={(e) => e.stopPropagation()} onClick={() => toggleSpecialty(sp)}>
                <div style={checkBoxSt(checked)}>{checked && <Tick />}</div>
                <span>{sp}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* ── License summary row (Provisioned / Launched / Active) ──
           Top tier of the page hierarchy: bigger cards + a drop shadow so
           they visually float above everything below, which stays flat
           (border only, no shadow) to read as supporting detail. */}
      <div style={{ padding: "0 16px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: T.textDim, textTransform: "uppercase", letterSpacing: 1.6 }}>Overview</div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: 14, marginBottom: 20 }}>
      {/* ── Total Licensed card (collapsible monthly report) ── */}
      <div style={{ display: "flex", background: T.surface, border: `1px solid ${T.border}`, borderRadius: T.radius, overflow: "hidden", boxShadow: "0 6px 20px rgba(124,58,237,0.12)" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Title bar */}
          <div style={{ display: "flex", alignItems: "center", padding: "18px 22px 0" }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1.4, color: T.accent }}>Provisioned Licenses</div>
          </div>

          {/* Numbers body — single headline number collapsed, full breakdown expanded */}
          {provisionedExpanded ? (
            <CardMetricList metrics={[
              { label: "30 Day Increase", value: growth30d.provisioned_30d, title: "Licenses provisioned in the last 30 days." },
            ]} />
          ) : (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "38px 22px 44px" }}>
              <div style={{ fontSize: 58, fontWeight: 700, color: T.accent, lineHeight: 1 }}>{totalLicensed.toLocaleString()}</div>
            </div>
          )}
        </div>
        <CardExpandRail expanded={provisionedExpanded} onToggle={() => setProvisionedExpanded((v) => !v)} title="Provisioned license breakdown" />
      </div>
      {/* ── License to Launch card ── */}
      <div style={{ display: "flex", background: T.surface, border: `1px solid ${T.border}`, borderRadius: T.radius, overflow: "hidden", boxShadow: "0 6px 20px rgba(124,58,237,0.12)" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Title bar */}
          <div style={{ display: "flex", alignItems: "center", padding: "18px 22px 0" }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1.4, color: T.accent }}>Launched Licenses</div>
          </div>

          {/* Numbers body — single headline number collapsed, full breakdown expanded */}
          {launchedExpanded ? (
            <CardMetricList metrics={[
              { label: "30 Day Increase", value: growth30d.launched_30d, title: "Licenses launched in the last 30 days." },
            ]} />
          ) : (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "38px 22px 44px" }}>
              <div style={{ fontSize: 58, fontWeight: 700, color: T.accent, lineHeight: 1 }}>{activeLicensed.toLocaleString()}</div>
            </div>
          )}
        </div>
        <CardExpandRail expanded={launchedExpanded} onToggle={() => setLaunchedExpanded((v) => !v)} title="Launched license breakdown" />
      </div>
      {/* ── Active Licenses card ── */}
      <div style={{ display: "flex", background: T.surface, border: `1px solid ${T.border}`, borderRadius: T.radius, overflow: "hidden", boxShadow: "0 6px 20px rgba(124,58,237,0.12)" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Title bar */}
          <div style={{ display: "flex", alignItems: "center", padding: "18px 22px 0" }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1.4, color: T.accent }}>Active Licenses</div>
          </div>

          {/* Numbers body — single headline number collapsed, full breakdown expanded.
               Add more rows to `metrics` any time (e.g. new-this-month) — no layout work needed. */}
          {activeExpanded ? (
            <CardMetricList metrics={[
              { label: "30 Day Increase", value: activitySummary.new_active_licenses_30d, title: "Users newly active (first-ever encounter) in the last 30 days." },
            ]} />
          ) : (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "38px 22px 44px" }}>
              <div style={{ fontSize: 58, fontWeight: 700, color: T.accent, lineHeight: 1 }}>{activitySummary.active_licenses.toLocaleString()}</div>
            </div>
          )}
        </div>
        <CardExpandRail expanded={activeExpanded} onToggle={() => setActiveExpanded((v) => !v)} title="Active license breakdown" />
      </div>
      </div>{/* end 3-card summary row */}
      </div>{/* end Overview horizontal inset */}

      {/* ── Snapshots (+ remaining) + engagement row — deliberately more compact than the row below ── */}
      <div style={{ padding: "0 16px" }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: T.textDim, textTransform: "uppercase", letterSpacing: 1.6, marginBottom: 8 }}>Snapshots</div>
      <div style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr", gap: 12, marginBottom: 20, alignItems: "stretch" }}>
        {/* Progress — cumulative Provisioned / Launched / Active lines,
            month over month, instead of a single point-in-time bar; shows
            the trajectory rather than just today's snapshot. */}
        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: T.radius, padding: "16px 20px", display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1.4, color: T.accent }}>Trend</div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: T.textMuted }}>
                <span style={{ width: 8, height: 8, borderRadius: 2, background: T.accent, display: "inline-block" }} />Provisioned
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: T.textMuted }}>
                <span style={{ width: 8, height: 8, borderRadius: 2, background: T.blue, display: "inline-block" }} />Launched
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: T.textMuted }}>
                <span style={{ width: 8, height: 8, borderRadius: 2, background: T.green, display: "inline-block" }} />Active
              </div>
            </div>
          </div>
          {(() => {
            const ABBR = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
            const byMonth = {};
            licenseGrowth.forEach((r) => { byMonth[r.month] ||= {}; byMonth[r.month].provisioned = parseInt(r.new_count, 10) || 0; });
            launchedGrowth.forEach((r) => { byMonth[r.month] ||= {}; byMonth[r.month].launched = parseInt(r.new_count, 10) || 0; });
            activeGrowth.forEach((r) => { byMonth[r.month] ||= {}; byMonth[r.month].active = parseInt(r.new_count, 10) || 0; });
            const months = Object.keys(byMonth).sort();
            if (months.length === 0) return <div style={{ padding: "20px 0", textAlign: "center", fontSize: 13, color: T.textDim }}>No monthly data yet</div>;

            // Running totals — each month's new_count is a delta, so the
            // line for this card (cumulative, not month-over-month) is a
            // running sum and can only climb.
            let cumProv = 0, cumLaunch = 0, cumActive = 0;
            const series = months.map((m) => {
              cumProv += byMonth[m].provisioned || 0;
              cumLaunch += byMonth[m].launched || 0;
              cumActive += byMonth[m].active || 0;
              return { month: m, provisioned: cumProv, launched: cumLaunch, active: cumActive };
            });

            // Anchor the most recent point to the live Overview cards as a
            // safety net. /reports/licensed-growth, /reports/launched-growth,
            // and /reports/active-growth are now all true "as of month-end"
            // snapshots scoped the same way their matching live card is, so
            // in practice the last point already equals the live number
            // before this override runs — but keeping the override means a
            // future edit to either side can't silently make the chart end
            // somewhere different from what the cards up top say, without at
            // least being caught here.
            if (series.length > 0) {
              const last = series[series.length - 1];
              series[series.length - 1] = {
                ...last,
                provisioned: totalLicensed,
                launched: activeLicensed,
                active: activitySummary.active_licenses,
              };
            }

            const maxVal = Math.max(...series.flatMap((s) => [s.provisioned, s.launched, s.active]), 1);
            const W = 640, H = 110, PAD_X = 14, PAD_Y = 10;
            const n = series.length;
            const xStep = n > 1 ? (W - PAD_X * 2) / (n - 1) : 0;
            const xFor = (i) => PAD_X + i * xStep;
            const yFor = (v) => H - PAD_Y - (v / maxVal) * (H - PAD_Y * 2);
            const pathFor = (key) => series.map((s, i) => `${i === 0 ? "M" : "L"}${xFor(i).toFixed(1)},${yFor(s[key]).toFixed(1)}`).join(" ");
            const hov = hoveredDot !== null ? series[hoveredDot] : null;

            return (
              <div style={{ position: "relative", border: `1px solid ${T.border}`, borderRadius: T.radiusSm, padding: "10px 10px 4px", background: T.surface2 }}>
                <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} style={{ display: "block", overflow: "visible" }}>
                  <path d={pathFor("provisioned")} fill="none" stroke={T.accent} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
                  <path d={pathFor("launched")} fill="none" stroke={T.blue} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
                  <path d={pathFor("active")} fill="none" stroke={T.green} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
                  {series.map((s, i) => {
                    const isHov = hoveredDot === i;
                    return (
                      <g key={s.month}>
                        <rect x={xFor(i) - (xStep || W) / 2} y={0} width={xStep || W} height={H} fill="transparent"
                          onMouseEnter={() => setHoveredDot(i)} onMouseLeave={() => setHoveredDot(null)} />
                        <circle cx={xFor(i)} cy={yFor(s.provisioned)} r={isHov ? 4.5 : 2.5} fill={T.accent} style={{ pointerEvents: "none", transition: "r 0.15s" }} />
                        <circle cx={xFor(i)} cy={yFor(s.launched)} r={isHov ? 4.5 : 2.5} fill={T.blue} style={{ pointerEvents: "none", transition: "r 0.15s" }} />
                        <circle cx={xFor(i)} cy={yFor(s.active)} r={isHov ? 4.5 : 2.5} fill={T.green} style={{ pointerEvents: "none", transition: "r 0.15s" }} />
                      </g>
                    );
                  })}
                </svg>

                {hov && (() => {
                  const mo = parseInt(hov.month.slice(4, 6), 10) - 1;
                  const yr = hov.month.slice(2, 4);
                  return (
                    <div style={{
                      position: "absolute", left: `${(xFor(hoveredDot) / W) * 100}%`, top: 0,
                      transform: "translate(-50%, -100%)", background: "#2D2A5E", color: "#fff", borderRadius: 6,
                      padding: "5px 9px", fontSize: 11, whiteSpace: "nowrap", zIndex: 20, pointerEvents: "none",
                    }}>
                      <div style={{ fontWeight: 600 }}>{ABBR[mo]} '{yr}</div>
                      <div style={{ opacity: 0.8 }}>{hov.provisioned.toLocaleString()} provisioned</div>
                      <div style={{ opacity: 0.8 }}>{hov.launched.toLocaleString()} launched</div>
                      <div style={{ opacity: 0.8 }}>{hov.active.toLocaleString()} active</div>
                    </div>
                  );
                })()}

                {/* Month labels */}
                <div style={{ display: "flex", justifyContent: "space-between", padding: "2px 0 6px" }}>
                  {series.map((s, i) => {
                    const mo = parseInt(s.month.slice(4, 6), 10) - 1;
                    const isLast = i === series.length - 1;
                    return (
                      <span key={s.month} style={{ fontSize: 9, color: (i % 3 === 0 || isLast) ? T.textDim : "transparent", letterSpacing: 0.2 }}>
                        {ABBR[mo]}
                      </span>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </div>

        {/* Active engagement — At Risk / Power Users / Avg Daily Active, moved out of the Active Licenses card;
            Remaining Not-Launched / Remaining Inactive folded in from the Progress card since both read off that
            card's bar but are stat chips like the rest of this row. */}
        <div style={{ position: "relative", background: T.surface, border: `1px solid ${T.border}`, borderRadius: T.radius, padding: "16px 20px" }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1.4, color: T.accent, marginBottom: 12 }}>Engagement</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
            <div
              onClick={() => toggleActivityDetail("at_risk")}
              onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 0 0 2px rgba(124,58,237,0.3)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; }}
              style={{ background: activityDetailType === "at_risk" ? T.accentSoft : T.surface2, borderRadius: 8, padding: "10px 12px", cursor: "pointer", border: `1px solid ${activityDetailType === "at_risk" ? T.accent : "transparent"}`, transition: "box-shadow 0.15s, border-color 0.15s" }}
            >
              <div style={{ fontSize: 22, fontWeight: 700, color: T.red, lineHeight: 1 }}>{activitySummary.at_risk_count.toLocaleString()}</div>
              <div style={{ fontSize: 11, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.6, marginTop: 4 }}>At Risk</div>
              <div style={{ fontSize: 10, color: T.textDim, letterSpacing: 0.4 }}>14+ days idle</div>
            </div>
            <div
              onClick={() => toggleActivityDetail("power_users")}
              onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 0 0 2px rgba(124,58,237,0.3)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; }}
              style={{ background: activityDetailType === "power_users" ? T.accentSoft : T.surface2, borderRadius: 8, padding: "10px 12px", cursor: "pointer", border: `1px solid ${activityDetailType === "power_users" ? T.accent : "transparent"}`, transition: "box-shadow 0.15s, border-color 0.15s" }}
            >
              <div style={{ fontSize: 22, fontWeight: 700, color: T.orange, lineHeight: 1 }}>{activitySummary.power_user_count.toLocaleString()}</div>
              <div style={{ fontSize: 11, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.6, marginTop: 4 }}>Power</div>
              <div style={{ fontSize: 10, color: T.textDim, letterSpacing: 0.4 }}>&gt;15/wk</div>
            </div>
            <div style={{ background: T.surface2, borderRadius: 8, padding: "10px 12px" }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: T.text, lineHeight: 1 }}>{Number(activitySummary.avg_dau).toLocaleString(undefined, { maximumFractionDigits: 1 })}</div>
              <div style={{ fontSize: 11, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.6, marginTop: 4 }}>DAU</div>
              <div style={{ fontSize: 10, color: T.textDim, letterSpacing: 0.4 }}>30 day trailing</div>
            </div>
          </div>

          {/* Second row — Remaining Not-Launched / Remaining Inactive get their
              own 2-col grid so they split the card's full width evenly, instead
              of sitting cramped in two of the three columns above. */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10, marginTop: 10 }}>
            <div
              onClick={() => toggleActivityDetail("not_launched")}
              onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 0 0 2px rgba(124,58,237,0.3)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; }}
              style={{ background: activityDetailType === "not_launched" ? T.accentSoft : T.surface2, borderRadius: 8, padding: "10px 12px", cursor: "pointer", border: `1px solid ${activityDetailType === "not_launched" ? T.accent : "transparent"}`, transition: "box-shadow 0.15s, border-color 0.15s" }}
            >
              <div style={{ fontSize: 22, fontWeight: 700, color: T.text, lineHeight: 1 }}>{(totalLicensed - activeLicensed).toLocaleString()}</div>
              <div style={{ fontSize: 11, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.6, marginTop: 4 }}>Remaining Not-Launched</div>
            </div>
            <div
              onClick={() => toggleActivityDetail("inactive")}
              onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 0 0 2px rgba(124,58,237,0.3)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; }}
              style={{ background: activityDetailType === "inactive" ? T.accentSoft : T.surface2, borderRadius: 8, padding: "10px 12px", cursor: "pointer", border: `1px solid ${activityDetailType === "inactive" ? T.accent : "transparent"}`, transition: "box-shadow 0.15s, border-color 0.15s" }}
            >
              <div style={{ fontSize: 22, fontWeight: 700, color: T.text, lineHeight: 1 }}>{activitySummary.inactive_licenses.toLocaleString()}</div>
              <div style={{ fontSize: 11, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.6, marginTop: 4 }}>Remaining Inactive</div>
            </div>
          </div>

          {/* Popup — At Risk / Power Users detail, attached to and scrolling
              with this card (position: absolute against the card's own
              position: relative) instead of a viewport-fixed portal, so it
              can't end up detached or clipped by the viewport edge. */}
          {activityDetailType && (
            <div style={{
              position: "absolute", top: "calc(100% + 8px)", left: 0, right: 0, zIndex: 20,
              background: T.surface, border: `1px solid ${T.border}`, borderRadius: T.radius, boxShadow: T.shadow, overflow: "hidden",
            }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "8px 12px", borderBottom: `1px solid ${T.border}` }}>
                <span style={{ fontSize: 11, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.6 }}>
                  {ACTIVITY_DETAIL_LABELS[activityDetailType]} {activityDetailType === "not_launched" || activityDetailType === "inactive" ? "list" : "breakdown"}
                </span>
                <button onClick={() => setActivityDetailType(null)} style={{ background: "none", border: "none", cursor: "pointer", color: T.textDim, fontSize: 18, lineHeight: 1 }}>×</button>
              </div>
              {activityDetailLoading && !activityDetailData[activityDetailType] ? (
                <div style={{ padding: 16, textAlign: "center", fontSize: 13, color: T.textDim }}>Loading...</div>
              ) : activityDetailError ? (
                <div style={{ padding: 16, textAlign: "center", fontSize: 13, color: T.red }}>{activityDetailError}</div>
              ) : activityDetailType === "not_launched" || activityDetailType === "inactive" ? (() => {
                const rows = activityDetailData[activityDetailType] || [];
                if (rows.length === 0) {
                  return <div style={{ padding: 16, textAlign: "center", fontSize: 13, color: T.textDim }}>No providers found</div>;
                }
                return (
                  <>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "8px 12px", borderBottom: `1px solid ${T.border}` }}>
                      <span style={{ fontSize: 11, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.6 }}>
                        {rows.length.toLocaleString()} provider{rows.length === 1 ? "" : "s"}
                      </span>
                      <Btn sm variant="ghost" onClick={() => downloadActivityDetailXlsx(activityDetailType)}>
                        Download full list (XLSX)
                      </Btn>
                    </div>
                    <div style={{ maxHeight: 260, overflowY: "auto" }}>
                      {rows.map((p, i) => (
                        <div key={p.doctor_user_id} style={{ display: "flex", flexDirection: "column", gap: 2, padding: "8px 12px", borderTop: i > 0 ? `1px solid ${T.border}` : "none" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: 13, color: T.text }}>
                            <span>{p.provider_name || "—"}</span>
                            <span style={{ color: T.textDim }}>{p.station_id || ""}</span>
                          </div>
                          <div style={{ fontSize: 11, color: T.textDim }}>{p.email || ""}</div>
                        </div>
                      ))}
                    </div>
                  </>
                );
              })() : (() => {
                const rows = activityDetailData[activityDetailType] || [];
                if (rows.length === 0) {
                  return <div style={{ padding: 16, textAlign: "center", fontSize: 13, color: T.textDim }}>No providers found</div>;
                }
                const { isAtRisk, counted, total } = computeActivityBuckets(activityDetailType, rows);
                const barColor = isAtRisk ? T.red : T.orange;
                return (
                  <>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "8px 12px", borderBottom: `1px solid ${T.border}` }}>
                      <span style={{ fontSize: 11, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.6 }}>
                        {total.toLocaleString()} {isAtRisk ? "at-risk" : "power"} provider{total === 1 ? "" : "s"} — rough breakdown
                      </span>
                      <Btn sm variant="ghost" onClick={() => downloadActivityDetailXlsx(activityDetailType)}>
                        Download full list (XLSX)
                      </Btn>
                    </div>
                    {counted.map((b, i) => (
                      <div key={b.label} style={{ padding: "10px 12px", borderTop: i > 0 ? `1px solid ${T.border}` : "none" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 5 }}>
                          <span style={{ color: T.text }}>{b.label}</span>
                          <span style={{ color: T.textDim, fontWeight: 600 }}>{b.count.toLocaleString()}</span>
                        </div>
                        <div style={{ height: 6, borderRadius: 3, background: T.surface2, overflow: "hidden" }}>
                          <div style={{ height: "100%", width: `${total > 0 ? (b.count / total) * 100 : 0}%`, background: barColor, borderRadius: 3 }} />
                        </div>
                      </div>
                    ))}
                  </>
                );
              })()}
            </div>
          )}
        </div>
      </div>
      </div>{/* end Snapshots horizontal inset */}

      {/* ── Unified tooling — Trends is now the only tab. User Flagging, Reports,
           and Breakdowns each folded into Trends under the metric filter they
           correspond to (Decommissioned, Provisioned, and Launched/Active
           respectively); User Lookup moved out of the tab bar entirely into a
           popup (see the icon on the right) so it stays reachable no matter
           which metric filter is selected. ── */}
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: T.radius, overflow: "hidden", margin: "0 16px" }}>
        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", padding: "10px 16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 3, flexWrap: "wrap", background: T.bg, border: `1px solid ${T.border}`, borderRadius: 999, padding: 3 }}>
            {/* Month Over Month License States — replaces the four separate
                Provisioned/Launched/Active/Decommissioned tabs that used to
                live in their own "Metrics Tracked" pill row. Now one tab
                (momMetric === "mom-license-states"); which of the four
                states show is a separate multi-select toggle inside its
                content (visibleLicenseStates, see below), so any combination
                can be on at once instead of only one at a time. */}
            {(() => {
              const isActive = momMetric === "mom-license-states";
              return (
                <button
                  onClick={() => setMomMetric("mom-license-states")}
                  title="Month Over Month License States"
                  style={{
                    display: "flex", alignItems: "center", gap: 6, fontSize: 13,
                    color: isActive ? T.text : T.textMuted,
                    fontWeight: isActive ? 700 : 500,
                    background: isActive ? T.accentSoft : "transparent",
                    border: `1px solid ${isActive ? T.accent : "transparent"}`,
                    borderRadius: 999, padding: "6px 12px", cursor: "pointer",
                    transition: "background 0.15s, border-color 0.15s",
                  }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="4" y1="20" x2="4" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="20" y1="20" x2="20" y2="14"/></svg>
                  Month Over Month License States
                </button>
              );
            })()}

            {/* Geography — City-level license coverage, same pill treatment as
                Utilization. Not a tracked metric either, just another lookup
                lens (by location instead of by station). */}
            {(() => {
              const isActive = momMetric === "geography";
              return (
                <button
                  onClick={() => setMomMetric("geography")}
                  style={{
                    display: "flex", alignItems: "center", gap: 6, fontSize: 13,
                    color: isActive ? T.text : T.textMuted,
                    fontWeight: isActive ? 700 : 500,
                    background: isActive ? T.accentSoft : "transparent",
                    border: `1px solid ${isActive ? T.accent : "transparent"}`,
                    borderRadius: 999, padding: "6px 12px", cursor: "pointer",
                    transition: "background 0.15s, border-color 0.15s",
                  }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity={isActive ? 1 : 0.55}>
                    <path d="M12 21s-7-6.5-7-11.5a7 7 0 1 1 14 0C19 14.5 12 21 12 21z" />
                    <circle cx="12" cy="9.5" r="2.2" />
                  </svg>
                  Geography
                </button>
              );
            })()}

            {/* Utilization — a station lookup tool, not a tracked metric like the
                four above. Same pill footprint (border width/padding/radius) as
                the metric buttons so it stays perfectly aligned with them, but
                swaps their flat color dot for a little ascending-bars "meter"
                glyph — a nod to what "utilization" actually means — and keeps
                a soft tinted fill at rest instead of pure transparent, so it
                reads as its own thing rather than a repeat of either the
                metric pills or the Doctor Lookup ghost button. */}
            {(() => {
              const isActive = momMetric === "all";
              return (
                <button
                  onClick={() => setMomMetric("all")}
                  style={{
                    display: "flex", alignItems: "center", gap: 6, fontSize: 13,
                    color: isActive ? T.text : T.textMuted,
                    fontWeight: isActive ? 700 : 500,
                    background: isActive ? T.accentSoft : "transparent",
                    border: `1px solid ${isActive ? T.accent : "transparent"}`,
                    borderRadius: 999, padding: "6px 12px", cursor: "pointer",
                    transition: "background 0.15s, border-color 0.15s",
                  }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                    <rect x="3" y="13" width="4.5" height="8" rx="1" fill={T.accent} opacity={isActive ? 1 : 0.55} />
                    <rect x="9.75" y="8" width="4.5" height="13" rx="1" fill={T.accent} opacity={isActive ? 1 : 0.55} />
                    <rect x="16.5" y="3" width="4.5" height="18" rx="1" fill={T.accent} opacity={isActive ? 1 : 0.55} />
                  </svg>
                  Engagement
                </button>
              );
            })()}

            {/* Leaderboard — top recording providers, trailing 30-day window. */}
            {(() => {
              const isActive = momMetric === "leaderboard";
              return (
                <button
                  onClick={() => setMomMetric("leaderboard")}
                  title="User Leaderboard"
                  style={{
                    display: "flex", alignItems: "center", gap: 6, fontSize: 13,
                    color: isActive ? T.text : T.textMuted,
                    fontWeight: isActive ? 700 : 500,
                    background: isActive ? T.accentSoft : "transparent",
                    border: `1px solid ${isActive ? T.accent : "transparent"}`,
                    borderRadius: 999, padding: "6px 12px", cursor: "pointer",
                    transition: "background 0.15s, border-color 0.15s",
                    fontFamily: T.font,
                  }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8 21h8"/><path d="M12 17v4"/><path d="M7 4h10v5a5 5 0 01-10 0z"/><path d="M7 6H4a2 2 0 002 4"/><path d="M17 6h3a2 2 0 01-2 4"/></svg>
                  Leaderboard
                </button>
              );
            })()}

            {/* Doctors — moved in alongside Utilization/Geography since all
                three are lookup views rather than tracked metrics, so it reads
                as a third option in that group instead of a stray button off
                on its own at the far right of the bar. Shortened from "Doctor
                Lookup" to match the one-word convention of its new siblings.
                Now a tab like its siblings (sets momMetric) instead of opening
                a popup — content renders inline under the card below. */}
            {(() => {
              const isActive = momMetric === "doctors";
              return (
                <button
                  onClick={() => setMomMetric("doctors")}
                  title="Doctor Lookup"
                  style={{
                    display: "flex", alignItems: "center", gap: 6, fontSize: 13,
                    color: isActive ? T.text : T.textMuted,
                    fontWeight: isActive ? 700 : 500,
                    background: isActive ? T.accentSoft : "transparent",
                    border: `1px solid ${isActive ? T.accent : "transparent"}`,
                    borderRadius: 999, padding: "6px 12px", cursor: "pointer",
                    transition: "background 0.15s, border-color 0.15s",
                    fontFamily: T.font,
                  }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                  Doctors
                </button>
              );
            })()}

            {/* User Flagging & Outreach — split out from the Doctors tab, same pill
                treatment as its siblings. Renamed (was "Email Outreach") and now
                also hosts the By Station/By VISN/By Specialty breakdown moved
                down from the Active tab. */}
            {(() => {
              const isActive = momMetric === "email-outreach";
              return (
                <button
                  onClick={() => setMomMetric("email-outreach")}
                  title="Outreach"
                  style={{
                    display: "flex", alignItems: "center", gap: 6, fontSize: 13,
                    color: isActive ? T.text : T.textMuted,
                    fontWeight: isActive ? 700 : 500,
                    background: isActive ? T.accentSoft : "transparent",
                    border: `1px solid ${isActive ? T.accent : "transparent"}`,
                    borderRadius: 999, padding: "6px 12px", cursor: "pointer",
                    transition: "background 0.15s, border-color 0.15s",
                    fontFamily: T.font,
                  }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16v16H4z"/><path d="M4 4l8 8 8-8"/></svg>
                  Outreach
                </button>
              );
            })()}
          </div>
        </div>

        {toolCategory === "trends" && (
          <div onClick={(e) => e.stopPropagation()} style={{ padding: "18px 24px 20px", display: "flex", flexDirection: "column", gap: 24 }}>
            {momMetric === "mom-license-states" && (
            <div>
              {/* Four independent switches — same light-switch track+knob design
                  as the Engagement tab's Launch Report / Utilization Report /
                  At-Risk & Power Users toggles. Unlike that group (mutually
                  exclusive, one shared state), these four are each their own
                  independent on/off toggle against visibleLicenseStates, so
                  any combination can be on at once and show together on the
                  bar chart and the monthly breakdowns below. */}
              <div style={{ marginBottom: 20, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 24 }}>
                {[
                  { key: "provisioned", label: "Provisioned" },
                  { key: "launched", label: "Launched" },
                  { key: "active", label: "Active" },
                  { key: "decommissioned", label: "Decommissioned" },
                ].map(({ key, label }) => {
                  const isOn = visibleLicenseStates[key];
                  const toggle = () => setVisibleLicenseStates((prev) => {
                    // Don't allow switching off the last remaining state — an
                    // empty chart with nothing toggled on isn't useful, so at
                    // least one must always stay on.
                    const nextVal = !prev[key];
                    if (!nextVal && Object.values(prev).filter(Boolean).length <= 1) return prev;
                    return { ...prev, [key]: nextVal };
                  });
                  return (
                    <div key={key} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div
                        role="switch"
                        aria-checked={isOn}
                        onClick={toggle}
                        style={{
                          position: "relative", width: 36, height: 20, borderRadius: 999, flexShrink: 0,
                          background: isOn ? T.accent : T.border, cursor: "pointer", transition: "background 0.2s",
                        }}
                      >
                        <div style={{
                          position: "absolute", top: 2,
                          left: isOn ? 18 : 2,
                          width: 16, height: 16, borderRadius: "50%", background: "#fff",
                          boxShadow: "0 1px 3px rgba(0,0,0,0.3)", transition: "left 0.2s ease",
                        }} />
                      </div>
                      <span
                        onClick={toggle}
                        style={{
                          fontSize: 13,
                          fontWeight: isOn ? 600 : 500,
                          color: isOn ? T.text : T.textMuted,
                          opacity: isOn ? 1 : 0.55,
                          transition: "opacity 0.2s, font-weight 0.2s",
                          cursor: "pointer", userSelect: "none",
                        }}
                      >
                        {label}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* Combined Provisioned + Launched + Active month-over-month bar chart —
                  moved here from the top-level Trends row so all trend charts live
                  together under Tools › Trends. Borderless (background only) to sit
                  flush with the rest of the tab content instead of reading as its own
                  boxed card. The metric filter lets a single series be isolated instead
                  of always showing all three bars per month. */}
              <div style={{ background: T.surface, borderRadius: T.radius, padding: "16px 20px" }}>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1.4, color: T.accent, marginBottom: 12 }}>Month Over Month</div>
                {(() => {
                  const ABBR = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
                  const byMonth = {};
                  licenseGrowth.forEach((r) => { byMonth[r.month] ||= {}; byMonth[r.month].provisioned = parseInt(r.new_count, 10) || 0; });
                  launchedGrowth.forEach((r) => { byMonth[r.month] ||= {}; byMonth[r.month].launched = parseInt(r.new_count, 10) || 0; });
                  activeGrowth.forEach((r) => { byMonth[r.month] ||= {}; byMonth[r.month].active = parseInt(r.new_count, 10) || 0; });
                  decommissionedGrowth.forEach((r) => { byMonth[r.month] ||= {}; byMonth[r.month].decommissioned = parseInt(r.new_count, 10) || 0; });
                  const months = Object.keys(byMonth).sort();
                  if (months.length === 0) return <div style={{ padding: "20px 0", textAlign: "center", fontSize: 13, color: T.textDim }}>No monthly data yet</div>;
                  const visible = Object.keys(visibleLicenseStates).filter((k) => visibleLicenseStates[k]);
                  const maxVal = Math.max(...months.flatMap((m) => visible.map((k) => Math.abs(byMonth[m][k] || 0))), 1);
                  const BAR_H = 60;
                  return (
                    <div style={{ display: "flex", alignItems: "flex-end", gap: 4, height: BAR_H + 20 }}>
                      {months.map((m, i) => {
                        const provVal = Math.max(0, byMonth[m].provisioned || 0);
                        const launchVal = byMonth[m].launched || 0;
                        const activeVal = byMonth[m].active || 0;
                        const decommVal = byMonth[m].decommissioned || 0;
                        const isHov = hoveredBar === i;
                        const isLast = i === months.length - 1;
                        const mo = parseInt(m.slice(4, 6), 10) - 1;
                        const yr = m.slice(2, 4);
                        const isYearStart = mo === 0;
                        return (
                          <div key={m} style={{
                            flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4, position: "relative", cursor: "default",
                            // Zebra-band alternating months so each month's group of
                            // three bars reads as one unit even when packed tightly,
                            // with a stronger tint + border on whichever is hovered.
                            background: isHov ? T.accentSoft : (i % 2 === 0 ? "transparent" : T.surface2),
                            borderRadius: 6,
                            padding: "4px 2px 2px",
                            // A slightly stronger left border marks each new calendar
                            // year, so long ranges don't blur into one undifferentiated strip.
                            borderLeft: isYearStart && i > 0 ? `1px dashed ${T.borderHover}` : "1px solid transparent",
                            transition: "background 0.15s",
                          }}
                            onMouseEnter={() => setHoveredBar(i)} onMouseLeave={() => setHoveredBar(null)}>
                            {isHov && (
                              <div style={{ position: "absolute", bottom: "calc(100% + 4px)", left: "50%", transform: "translateX(-50%)", background: "#2D2A5E", color: "#fff", borderRadius: 6, padding: "5px 9px", fontSize: 11, whiteSpace: "nowrap", zIndex: 20, pointerEvents: "none" }}>
                                <div style={{ fontWeight: 600 }}>{ABBR[mo]} '{yr}</div>
                                {visible.includes("provisioned") && <div style={{ opacity: 0.8 }}>{provVal >= 0 ? "+" : ""}{provVal.toLocaleString()} provisioned</div>}
                                {visible.includes("launched") && <div style={{ opacity: 0.8 }}>{launchVal >= 0 ? "+" : ""}{launchVal.toLocaleString()} launched</div>}
                                {visible.includes("active") && <div style={{ opacity: 0.8 }}>{activeVal >= 0 ? "+" : ""}{activeVal.toLocaleString()} activated</div>}
                                {visible.includes("decommissioned") && <div style={{ opacity: 0.8 }}>{decommVal.toLocaleString()} decommissioned</div>}
                              </div>
                            )}
                            <div style={{ display: "flex", alignItems: "flex-end", gap: 2, width: "100%", height: BAR_H }}>
                              {visible.includes("provisioned") && (
                                <div style={{
                                  flex: 1, height: Math.max(3, Math.round((Math.abs(provVal) / maxVal) * BAR_H)),
                                  borderRadius: "3px 3px 0 0",
                                  background: provVal < 0 ? (isHov ? "rgba(220,38,38,0.55)" : "rgba(220,38,38,0.22)") : isHov ? T.accentHover : T.accent,
                                  transition: "background 0.15s, height 0.2s",
                                }} />
                              )}
                              {visible.includes("launched") && (
                                <div style={{
                                  flex: 1, height: Math.max(3, Math.round((Math.abs(launchVal) / maxVal) * BAR_H)),
                                  borderRadius: "3px 3px 0 0",
                                  background: launchVal < 0 ? (isHov ? "rgba(220,38,38,0.55)" : "rgba(220,38,38,0.22)") : isHov ? "rgba(43,127,212,0.85)" : T.blue,
                                  transition: "background 0.15s, height 0.2s",
                                }} />
                              )}
                              {visible.includes("active") && (
                                <div style={{
                                  flex: 1, height: Math.max(3, Math.round((Math.abs(activeVal) / maxVal) * BAR_H)),
                                  borderRadius: "3px 3px 0 0",
                                  background: activeVal < 0 ? (isHov ? "rgba(220,38,38,0.55)" : "rgba(220,38,38,0.22)") : isHov ? "rgba(5,150,105,0.85)" : T.green,
                                  transition: "background 0.15s, height 0.2s",
                                }} />
                              )}
                              {visible.includes("decommissioned") && (
                                <div style={{
                                  flex: 1, height: Math.max(3, Math.round((Math.abs(decommVal) / maxVal) * BAR_H)),
                                  borderRadius: "3px 3px 0 0",
                                  background: isHov ? "rgba(94,98,120,0.85)" : T.textMuted,
                                  transition: "background 0.15s, height 0.2s",
                                }} />
                              )}
                            </div>
                            <div style={{ fontSize: 9, fontWeight: isHov ? 700 : 400, color: T.textDim, letterSpacing: 0.2, lineHeight: 1, whiteSpace: "nowrap" }}>
                              {ABBR[mo]}{isYearStart ? ` '${yr}` : ""}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            </div>
            )}

            {/* Utilization tab: platform-wide usage snapshot — replaces the
                time-series Month Over Month chart above (that's about
                Provisioned/Launched/Active trends, not utilization) with the
                same activitySummary numbers already computed for the Active
                tab's definitions text, just surfaced as stats here since this
                tab is about usage depth, not license counts. */}
            {momMetric === "all" && (
              <div style={{ background: T.surface, borderRadius: T.radius, padding: "16px 20px" }}>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1.4, color: T.accent, marginBottom: 4 }}>Platform Utilization</div>
                <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 12 }}>
                  Each number below is <span style={{ color: T.accent, fontWeight: 600 }}>Active</span> / <span style={{ color: T.textMuted, fontWeight: 600 }}>Licensed</span> — Active means at least one recorded encounter; Licensed means it has a license at all, active or not.
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(110px, 1fr))", gap: 12 }}>
                  {[
                    // Active = activitySummary.active_licenses (has a recorded
                    // encounter); Licensed = every license across all raw
                    // stations, active or not — always computed off the full
                    // `stations` list, not any tab's search/VISN/specialty
                    // filters, so this stays a true platform-wide snapshot.
                    ["License", activitySummary.active_licenses, stations.reduce((sum, s) => sum + licenseCount(s), 0)],
                    // Station counts use the rolled-up groupedStations groups.
                    // Active here means "has at least one recording-based active
                    // license" (via groupActiveLicenses, sourced from the
                    // activity-breakdown endpoint), matching the License row's
                    // definition above — NOT activeCount/activated_to_date
                    // ("launched"), which used to make this row disagree with
                    // License. Licensed still means "has at least one license
                    // at all", from licenseCount.
                    ["Station", groupedStations.filter((s) => groupActiveLicenses(s) > 0).length, groupedStations.filter((s) => licenseCount(s) > 0).length],
                    // Site counts use the raw (un-rolled-up) station records,
                    // same recording-based Active definition as Station above,
                    // but looked up in siteActiveLicensesById — keyed by each
                    // doctor's raw, un-canonicalized station_id — so a
                    // multi-site station's individual sites can actually
                    // differ from one another and from the group total.
                    ["Site", stations.filter((s) => (siteActiveLicensesById[s.station_id] ?? 0) > 0).length, stations.filter((s) => licenseCount(s) > 0).length],
                  ].map(([label, activeVal, licensedVal]) => (
                    <div key={label} style={{ background: T.surface2, borderRadius: T.radiusSm, padding: "10px 12px" }}>
                      <div style={{ fontSize: 11, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.6 }}>{label}</div>
                      <div style={{ fontSize: 20, fontWeight: 700, marginTop: 4 }}>
                        <span style={{ color: T.accent }} title="Active">{activeVal.toLocaleString()}</span>
                        <span style={{ color: T.textDim, fontWeight: 400 }}>/</span>
                        <span style={{ color: T.textMuted }} title="Licensed">{licensedVal.toLocaleString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Geography tab: same top-slot stat-card layout as Utilization above —
                a global snapshot card sitting above the panel with the search box,
                not tied to whatever's currently typed into that search. */}
            {momMetric === "geography" && (
              <div style={{ background: T.surface, borderRadius: T.radius, padding: "16px 20px" }}>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1.4, color: T.accent, marginBottom: 12 }}>Location Coverage</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(110px, 1fr))", gap: 12 }}>
                  {[
                    ["Licensed", stations.reduce((sum, s) => sum + licenseCount(s), 0)],
                    ["States/Territories", geoStateAgg.length],
                    ["Cities", geoCityAgg.length],
                  ].map(([label, value]) => (
                    <div key={label} style={{ background: T.surface2, borderRadius: T.radiusSm, padding: "10px 12px" }}>
                      <div style={{ fontSize: 11, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.6 }}>{label}</div>
                      <div style={{ fontSize: 20, fontWeight: 700, color: T.text, marginTop: 4 }}>{value.toLocaleString()}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Provisioned trend — same layout as Launched/Active below: a short
                definition right under the bar chart, then the button that opens
                the monthly table in a popup. */}
            {momMetric === "mom-license-states" && visibleLicenseStates.provisioned && (
              <div>
                <div style={{ padding: "18px 20px 20px", display: "flex", flexDirection: "column", gap: 16 }}>
                  <div style={{ fontSize: 13, color: T.textMuted, lineHeight: 1.6 }}>
                    A <strong style={{ color: T.text }}>provisioned</strong> license is a VA-issued license record that's been activated for a provider. These stats are updated per report cycle: for each month, the total reflects every provisioned license as of month-end, excluding decommissioned providers. <strong style={{ color: T.text }}>New Provisioned</strong> = that month-end total minus the prior month's total.
                  </div>
                  <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
                    {licenseGrowth.length === 0 ? (
                      <div style={{ fontSize: 13, color: T.textDim }}>No provisioning history yet</div>
                    ) : (
                      <>
                        <table style={{ width: "auto", maxWidth: 360, borderCollapse: "collapse", fontSize: 12 }}>
                          <tbody>
                            {cumulativeGrowthRows(licenseGrowth, totalLicensed).slice(0, 3).map((r) => {
                              const mo = parseInt(r.month.slice(4, 6), 10);
                              const yr = r.month.slice(0, 4);
                              return (
                                <tr key={r.month}>
                                  <td style={{ padding: "3px 10px 3px 0", color: T.textDim }}>{MONTH_NAMES[mo]} {yr}</td>
                                  <td style={{ padding: "3px 10px", textAlign: "right", fontWeight: 600, color: T.accent }}>{r.total.toLocaleString()}</td>
                                  <td style={{ padding: "3px 0", textAlign: "right", color: T.textMuted }}>{r.delta >= 0 ? "+" : ""}{r.delta.toLocaleString()}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                        <div style={{ width: 1, alignSelf: "stretch", background: T.border }} />
                        <button
                          onClick={() => setProvisionedTableOpen(true)}
                          style={{ whiteSpace: "nowrap", background: T.accentSoft, border: `1px solid ${T.accent}`, borderRadius: T.radiusSm, color: T.accent, fontSize: 13, padding: "4px 12px", cursor: "pointer", fontFamily: T.font, fontWeight: 600 }}
                        >
                          View monthly table ({licenseGrowth.length} months)
                        </button>
                      </>
                    )}
                    <div style={{ width: 1, alignSelf: "stretch", background: T.border }} />
                    {/* Monthly report export — moved here from the Reports tab so it sits
                        alongside the Provisioned metric it exports, now sharing this same
                        line as the "View monthly table" button instead of its own row
                        below. Always shown regardless of licenseGrowth, unlike the table
                        button/mini-table which need history to have anything to show. */}
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: T.textDim, whiteSpace: "nowrap" }}>Monthly report export:</span>
                      <select value={reportYear} onChange={(e) => setReportYear(e.target.value)} style={{ ...S.filterSelect, width: 86 }}>
                        {["2026","2025","2024"].map(y => <option key={y} value={y}>{y}</option>)}
                      </select>
                      <select value={reportMonth} onChange={(e) => setReportMonth(e.target.value)} style={{ ...S.filterSelect, width: 126 }}>
                        {MONTH_NAMES.slice(1).map((name, i) => <option key={i+1} value={String(i+1)}>{name}</option>)}
                      </select>
                      <span style={{ fontSize: 12, color: T.textDim, minWidth: 60 }}>
                        {exportCountLoading ? "…" : exportCount !== null ? `${exportCount.toLocaleString()} licenses` : ""}
                      </span>
                      <button onClick={() => exportXlsx()} disabled={exportingXlsx}
                        style={{ fontSize: 13, padding: "6px 14px", borderRadius: T.radiusSm, background: T.accent, color: "#fff", border: "none", cursor: exportingXlsx ? "default" : "pointer", fontFamily: T.font, fontWeight: 600, opacity: exportingXlsx ? 0.6 : 1 }}>
                        {exportingXlsx ? "Preparing…" : "↓ Export"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Launched trend / Active trend — only surface once that state is
                toggled on above; when shown, keep it to just the definition
                text plus a button that opens the monthly table in a popup
                (mirrors the other modals in this file) instead of an inline table. */}
            {momMetric === "mom-license-states" && visibleLicenseStates.launched && (
              <div>
                <div style={{ padding: "18px 20px 20px", display: "flex", flexDirection: "column", gap: 16 }}>
                  <div style={{ fontSize: 13, color: T.textMuted, lineHeight: 1.6 }}>
                    A <strong style={{ color: T.text }}>launched</strong> user is a provisioned license holder who has logged in to the platform. These stats are updated weekly: for each month, the activated count is summed across every station as of the last report week on or before month-end. <strong style={{ color: T.text }}>New Launches</strong> = that month-end total minus the prior month's total.
                  </div>
                  {launchedGrowth.length === 0 ? (
                    <div style={{ padding: 20, textAlign: "center", fontSize: 13, color: T.textDim }}>No launch history yet</div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 20 }}>
                      <table style={{ width: "auto", maxWidth: 360, borderCollapse: "collapse", fontSize: 12 }}>
                        <tbody>
                          {cumulativeGrowthRows(launchedGrowth, activeLicensed).slice(0, 3).map((r) => {
                            const mo = parseInt(r.month.slice(4, 6), 10);
                            const yr = r.month.slice(0, 4);
                            return (
                              <tr key={r.month}>
                                <td style={{ padding: "3px 10px 3px 0", color: T.textDim }}>{MONTH_NAMES[mo]} {yr}</td>
                                <td style={{ padding: "3px 10px", textAlign: "right", fontWeight: 600, color: T.accent }}>{r.total.toLocaleString()}</td>
                                <td style={{ padding: "3px 0", textAlign: "right", color: T.textMuted }}>{r.delta >= 0 ? "+" : ""}{r.delta.toLocaleString()}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      <div style={{ width: 1, alignSelf: "stretch", background: T.border }} />
                      <button
                        onClick={() => setLaunchedTableOpen(true)}
                        style={{ whiteSpace: "nowrap", background: T.accentSoft, border: `1px solid ${T.accent}`, borderRadius: T.radiusSm, color: T.accent, fontSize: 13, padding: "4px 12px", cursor: "pointer", fontFamily: T.font, fontWeight: 600 }}
                      >
                        View monthly table ({launchedGrowth.length} months)
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {momMetric === "mom-license-states" && visibleLicenseStates.active && (
              <div>
                <div style={{ padding: "18px 20px 20px", display: "flex", flexDirection: "column", gap: 16 }}>
                  <div>
                    <div style={{ fontSize: 13, color: T.textMuted, lineHeight: 1.6 }}>
                      Tracks provider engagement from recorded encounters. <strong style={{ color: T.text }}>Active Licenses</strong> is every clinician with at least one recorded encounter in provider activity. <strong style={{ color: T.red }}>At Risk</strong> clinicians haven't recorded an encounter in the last 14 calendar days. <strong style={{ color: T.orange }}>Power Users</strong> recorded more than 15 recordings in the trailing 7-day window.
                    </div>
                    <div style={{ fontSize: 12, color: T.textDim, marginTop: 6 }}>
                      Avg Daily Active Users = sum of each day's distinct active clinicians in a month ÷ calendar days in that month.
                    </div>
                    <div style={{ fontSize: 12, color: T.textDim, marginTop: 3 }}>
                      AVG Daily recordings = sum of each day's recordings (recordings_count from provider activity) in a month ÷ calendar days in that month.
                    </div>
                  </div>
                  {activitySummary.dau_history.length === 0 ? (
                    <div style={{ padding: 20, textAlign: "center", fontSize: 13, color: T.textDim }}>No activity history yet</div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 20 }}>
                      <table style={{ width: "auto", maxWidth: 360, borderCollapse: "collapse", fontSize: 12 }}>
                        <tbody>
                          {[...activitySummary.dau_history].reverse().slice(0, 3).map((r) => {
                            const mo = parseInt(r.month.slice(4, 6), 10);
                            const yr = r.month.slice(0, 4);
                            return (
                              <tr key={r.month}>
                                <td style={{ padding: "3px 10px 3px 0", color: T.textDim }}>{MONTH_NAMES[mo]} {yr}</td>
                                <td style={{ padding: "3px 0", textAlign: "right", fontWeight: 600, color: T.accent }}>{Number(r.avg_dau).toLocaleString(undefined, { maximumFractionDigits: 1 })} avg DAU</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      <div style={{ width: 1, alignSelf: "stretch", background: T.border }} />
                      {/* Distinct active users PER month — a doctor who recorded on
                          5 different days in March counts once here, not 5 (unlike
                          the avg DAU table to the left, which averages daily counts
                          across the month). Sourced from dau_history.distinct_active_users
                          (recordings_count > 0, same "Active" definition as
                          active_licenses elsewhere). */}
                      <table style={{ width: "auto", maxWidth: 360, borderCollapse: "collapse", fontSize: 12 }}>
                        <tbody>
                          {[...activitySummary.dau_history].reverse().slice(0, 3).map((r) => {
                            const mo = parseInt(r.month.slice(4, 6), 10);
                            const yr = r.month.slice(0, 4);
                            return (
                              <tr key={r.month}>
                                <td style={{ padding: "3px 10px 3px 0", color: T.textDim }}>{MONTH_NAMES[mo]} {yr}</td>
                                <td style={{ padding: "3px 0", textAlign: "right", fontWeight: 600, color: T.text }}>{Number(r.distinct_active_users ?? 0).toLocaleString()} distinct active</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      <div style={{ width: 1, alignSelf: "stretch", background: T.border }} />
                      <button
                        onClick={() => setActiveTableOpen(true)}
                        style={{ whiteSpace: "nowrap", background: T.accentSoft, border: `1px solid ${T.accent}`, borderRadius: T.radiusSm, color: T.accent, fontSize: 13, padding: "4px 12px", cursor: "pointer", fontFamily: T.font, fontWeight: 600 }}
                      >
                        View monthly table ({activitySummary.dau_history.length} months)
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Decommissioned trend — same layout as Provisioned/Launched/Active:
                definition text, last-three-months preview, button to the full
                monthly table popup. */}
            {momMetric === "mom-license-states" && visibleLicenseStates.decommissioned && (
              <div>
                <div style={{ padding: "18px 20px 20px", display: "flex", flexDirection: "column", gap: 16 }}>
                  <div style={{ fontSize: 13, color: T.textMuted, lineHeight: 1.6 }}>
                    A <strong style={{ color: T.text }}>decommissioned</strong> provider is one who's gone quiet for too long and lost their active license. This is also subject to a doctor moving stations — a transfer can show up here as a decommission at the old station even though the provider is still active elsewhere, so a spike isn't always a true drop-off. <strong style={{ color: T.text }}>New Decommissioned</strong> = providers whose coverage ended that month.
                  </div>
                  {decommissionedGrowth.length === 0 ? (
                    <div style={{ padding: 20, textAlign: "center", fontSize: 13, color: T.textDim }}>No decommission history yet</div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 20 }}>
                      <table style={{ width: "auto", maxWidth: 360, borderCollapse: "collapse", fontSize: 12 }}>
                        <tbody>
                          {cumulativeGrowthRows(decommissionedGrowth, decommissionedTotal).slice(0, 3).map((r) => {
                            const mo = parseInt(r.month.slice(4, 6), 10);
                            const yr = r.month.slice(0, 4);
                            return (
                              <tr key={r.month}>
                                <td style={{ padding: "3px 10px 3px 0", color: T.textDim }}>{MONTH_NAMES[mo]} {yr}</td>
                                <td style={{ padding: "3px 10px", textAlign: "right", fontWeight: 600, color: T.accent }}>{r.total.toLocaleString()}</td>
                                <td style={{ padding: "3px 0", textAlign: "right", color: T.textMuted }}>{r.delta >= 0 ? "+" : ""}{r.delta.toLocaleString()}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      <div style={{ width: 1, alignSelf: "stretch", background: T.border }} />
                      <button
                        onClick={() => setDecommissionedTableOpen(true)}
                        style={{ whiteSpace: "nowrap", background: T.accentSoft, border: `1px solid ${T.accent}`, borderRadius: T.radiusSm, color: T.accent, fontSize: 13, padding: "4px 12px", cursor: "pointer", fontFamily: T.font, fontWeight: 600 }}
                      >
                        View monthly table ({decommissionedGrowth.length} months)
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
            {/* Three independent toggles — one each for the Launch Report,
                the Utilization Report, and the At-Risk & Power User
                Breakdown (moved here from the User Flagging & Outreach tab)
                — rather than a single shared slider. All three are bound to
                the same `launchUtilView` state, so turning one on always
                reads as turning the others off: clicking any switch sets
                that view active, which automatically shows the other
                switches' tracks/knobs in their "off" position. */}
            {momMetric === "all" && (
              <div style={{ padding: "0 20px", marginTop: 4, marginBottom: 4, display: "flex", alignItems: "center", gap: 24 }}>
                {[
                  { key: "launch", label: "Launch Report" },
                  { key: "utilization", label: "Utilization Report" },
                  { key: "breakdown", label: "At-Risk & Power Users" },
                ].map(({ key, label }) => {
                  const isOn = launchUtilView === key;
                  return (
                    <div key={key} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div
                        role="switch"
                        aria-checked={isOn}
                        onClick={() => setLaunchUtilView(key)}
                        style={{
                          position: "relative", width: 36, height: 20, borderRadius: 999, flexShrink: 0,
                          background: isOn ? T.accent : T.border, cursor: "pointer", transition: "background 0.2s",
                        }}
                      >
                        <div style={{
                          position: "absolute", top: 2,
                          left: isOn ? 18 : 2,
                          width: 16, height: 16, borderRadius: "50%", background: "#fff",
                          boxShadow: "0 1px 3px rgba(0,0,0,0.3)", transition: "left 0.2s ease",
                        }} />
                      </div>
                      <span
                        onClick={() => setLaunchUtilView(key)}
                        style={{
                          fontSize: 13,
                          fontWeight: isOn ? 600 : 500,
                          color: isOn ? T.text : T.textMuted,
                          opacity: isOn ? 1 : 0.55,
                          transition: "opacity 0.2s, font-weight 0.2s",
                          cursor: "pointer", userSelect: "none",
                        }}
                      >
                        {label}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Station Utilization search box. Look up one station
                and see its doctors' weekly recordings/patients/utilization, plus a
                dropdown of the doctors themselves. */}
            {momMetric === "all" && launchUtilView === "utilization" && (
              <div style={{ padding: "0 20px" }}>
                <div style={{
                  display: "flex", gap: 10, alignItems: "flex-start",
                  background: T.accentSoft, border: `1px solid ${T.accent}`, borderRadius: T.radiusSm,
                  padding: "10px 14px", marginBottom: 16,
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 2 }}>
                    <circle cx="11" cy="11" r="7" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                  <div style={{ fontSize: 13, color: T.text, lineHeight: 1.6 }}>
                    {utilizationSearchMode === "station"
                      ? "Look up a single station to see its weekly recordings, patients, and utilization score, plus the full roster of doctors assigned there. Search by station ID or name below — the preview shows the most recent weeks, with a link to the full history and doctor drill-down, and a button to export both as one XLSX."
                      : utilizationSearchMode === "visn"
                      ? "Pick a VISN to see its weekly recordings, patients, and utilization score across every provider in it, plus the full doctor roster. The preview shows the most recent weeks, with a link to the full history and doctor drill-down, and a button to export both as one XLSX."
                      : utilizationSearchMode === "rise"
                      ? "Pick a RISE to see its weekly recordings, patients, and utilization score across every provider in it, plus the full doctor roster. One RISE can cover multiple VISNs. The preview shows the most recent weeks, with a link to the full history and doctor drill-down, and a button to export both as one XLSX."
                      : "Pick a specialty to see its weekly recordings, patients, and utilization score across every provider in it, plus the full doctor roster. The preview shows the most recent weeks, with a link to the full history and doctor drill-down, and a button to export both as one XLSX."}
                  </div>
                </div>

                <div ref={containerRef} style={{ display: "flex", gap: 0, alignItems: "stretch" }}>
                  {/* left: tabs + search / select + browsable list */}
                  <div style={{ flex: rightPanelWidth ? 1 : 3, minWidth: 0, overflow: "hidden", display: "flex", flexDirection: "column", marginRight: 0 }}>
                    {/* ── Tabs ── */}
                    {/* Own toggle for the Utilization Report, kept in sync with the
                        Launch Report's tabs below: picking Station/VISN/Specialty
                        here also switches Launch Report to the matching tab, so
                        flipping the light-switch between the two reports lands on
                        the same grouping instead of resetting. */}
                    <div style={{ display: "flex", gap: 0, borderBottom: `1px solid ${T.border}` }}>
                      {ENGAGEMENT_GROUP_TABS.map(({ util, launch, label }) => (
                        <button
                          key={util}
                          onClick={() => {
                            setUtilizationSearchMode(util);
                            setDashInner(launch);
                          }}
                          style={{ ...S.innertab(utilizationSearchMode === util) }}
                        >{label}</button>
                      ))}
                    </div>

                {utilizationSearchMode === "station" && (
                <>
                  <div style={{ display: "flex", flexWrap: "wrap", rowGap: 8, gap: 8, padding: "12px 16px", borderBottom: `1px solid ${T.border}`, alignItems: "center", position: "relative" }}>
                    <input
                      ref={stationSearchInputRef}
                      style={{ ...S.filterInput, width: 240 }}
                      placeholder="Station ID or name..."
                      value={stationSearchInput}
                      onChange={(e) => {
                        const val = e.target.value;
                        setStationSearchInput(val);
                        setStationSearchSuggestOpen(true);
                        updateStationSearchDropdownPos();
                        if (!val.trim()) {
                          // Cleared the search box — reset the report view entirely
                          // rather than leaving the last station's data on screen.
                          setStationReport(null);
                          setStationReportError(null);
                          setStationWeeklyModalOpen(false);
                          setStationDoctorsModalOpen(false);
                        }
                      }}
                      onFocus={() => { setStationSearchSuggestOpen(true); updateStationSearchDropdownPos(); }}
                      onBlur={() => { setTimeout(() => setStationSearchSuggestOpen(false), 150); }}
                      onKeyDown={(e) => { if (e.key === "Enter") { setStationSearchSuggestOpen(false); submitStationSearch(); } else if (e.key === "Escape") setStationSearchSuggestOpen(false); }}
                    />
                    <button
                      onClick={submitStationSearch}
                      disabled={stationReportLoading || !stationSearchInput.trim()}
                      style={{ fontSize: 13, padding: "8px 16px", borderRadius: T.radiusSm, background: T.accent, color: "#fff", border: "none", cursor: stationReportLoading ? "default" : "pointer", fontFamily: T.font, fontWeight: 600, opacity: stationReportLoading || !stationSearchInput.trim() ? 0.6 : 1 }}
                    >
                      {stationReportLoading ? "Loading…" : "Submit"}
                    </button>
                    {stationSearchSuggestOpen && stationSearchInput.trim() && stationSearchDropdownPos && createPortal(
                      (() => {
                        const q = stationSearchInput.trim().toLowerCase();
                        // Search every individual raw site, not just the rolled-up
                        // group — so typing a satellite clinic's own name/ID (e.g.
                        // "Franklin VA Clinic" / "637GA") surfaces that exact site
                        // directly instead of only its parent group ("637").
                        const siteCandidates = groupedStations.flatMap((g) =>
                          g.sub_sites.map((site) => ({ ...site, city: g.city, groupId: g.station_id, groupCount: g.subsite_count }))
                        );
                        const matches = siteCandidates.filter((site) =>
                          (site.station_id || "").toLowerCase().includes(q) ||
                          (site.site_name  || "").toLowerCase().includes(q) ||
                          (site.city       || "").toLowerCase().includes(q)
                        ).slice(0, 8);
                        return (
                          <div style={{
                            position: "fixed", top: stationSearchDropdownPos.top, left: stationSearchDropdownPos.left, width: stationSearchDropdownPos.width,
                            zIndex: 1000,
                            background: T.surface, border: `1px solid ${T.border}`, borderRadius: T.radiusSm,
                            boxShadow: "0 6px 16px rgba(0,0,0,0.15)", maxHeight: 260, overflowY: "auto",
                          }}>
                            {matches.length === 0 ? (
                              <div style={{ padding: "10px 12px", fontSize: 13, color: T.textDim }}>No matches for "{stationSearchInput}"</div>
                            ) : (
                              matches.map((site, i) => (
                                <div key={site.station_id}
                                  onMouseDown={(e) => e.preventDefault()} // keep input focus so onBlur doesn't fire before this click registers
                                  onClick={() => {
                                    setStationSearchInput(site.station_id);
                                    setStationSearchSuggestOpen(false);
                                    runStationSearch(site.station_id, { exact: true });
                                  }}
                                  style={{
                                    padding: "8px 12px", cursor: "pointer",
                                    borderBottom: i < matches.length - 1 ? `1px solid ${T.border}` : "none",
                                    background: T.surface,
                                  }}
                                  onMouseEnter={(e) => { e.currentTarget.style.background = T.accentSoft; }}
                                  onMouseLeave={(e) => { e.currentTarget.style.background = T.surface; }}
                                >
                                  <div style={{ fontSize: 13, color: T.text }}>{site.site_name || site.station_id}</div>
                                  <div style={{ fontSize: 11, color: T.textMuted, marginTop: 1 }}>
                                    {site.station_id}{site.city ? ` · ${site.city}` : ""}
                                    {site.groupCount > 1 && site.station_id !== site.groupId && ` · part of station ${site.groupId}`}
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        );
                      })(),
                      document.body
                    )}
                  </div>

                  {/* Browse every station — same table shape as the Launch
                      Report's By Station list, but selecting a row runs the
                      Utilization search (fetches weekly + doctor roster for
                      that station) instead of opening a side detail panel. */}
                  {(() => {
                    const perPage = 10;
                    const toggleUtilStSort = (key) => {
                      setUtilStationSort((s) => ({ key, dir: s.key === key && s.dir === "desc" ? "asc" : "desc" }));
                      setUtilStationPage(1);
                    };
                    const sorted = [...stationsWithLic].sort((a, b) => {
                      const lic_a = licenseCount(a), act_a = groupActiveLicenses(a), pct_a = lic_a > 0 ? act_a / lic_a : 0;
                      const lic_b = licenseCount(b), act_b = groupActiveLicenses(b), pct_b = lic_b > 0 ? act_b / lic_b : 0;
                      let diff = 0;
                      if (utilStationSort.key === "launched") diff = act_b - act_a;
                      else if (utilStationSort.key === "total") diff = lic_b - lic_a;
                      else if (utilStationSort.key === "adoption") diff = pct_b - pct_a;
                      else diff = (a.station_id || "").localeCompare(b.station_id || "");
                      if (utilStationSort.dir === "asc") diff = -diff;
                      return diff !== 0 ? diff : (a.station_id || "").localeCompare(b.station_id || "");
                    });
                    const totalPages = Math.max(1, Math.ceil(sorted.length / perPage));
                    const page = Math.min(utilStationPage, totalPages);
                    const paged = sorted.slice((page - 1) * perPage, page * perPage);
                    const SortTh = ({ label, sortKey }) => {
                      const active = utilStationSort.key === sortKey;
                      return (
                        <th onClick={() => toggleUtilStSort(sortKey)} style={{ ...S.th, color: active ? T.accent : T.text, cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}>
                          {label} <span style={{ fontSize: 9, opacity: active ? 1 : 0.3 }}>{active && utilStationSort.dir === "asc" ? "▲" : "▼"}</span>
                        </th>
                      );
                    };
                    const selectFromList = (st) => {
                      // st.station_id is a (possibly rolled-up) group key here,
                      // not a single raw site — search WITHOUT exact so the
                      // report returns the group's combined data (matching the
                      // "All sites" option in the report's own site filter),
                      // same total the Active column just summed across the
                      // group's sub-sites. exact:true is only for picking one
                      // specific raw site (typeahead suggestions, site filter).
                      setStationSearchInput(st.station_id);
                      setStationSearchSuggestOpen(false);
                      runStationSearch(st.station_id);
                    };
                    return !activityBreakdownData.station_id ? (
                      <div style={{ padding: 32, textAlign: "center", fontSize: 13, color: T.textDim }}>Loading stations...</div>
                    ) : (
                      <div style={{ ...S.tableWrap, border: "none", borderRadius: 0, boxShadow: "none" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                          <colgroup>
                            <col style={{ width: "28%" }} /><col style={{ width: "24%" }} /><col style={{ width: "24%" }} /><col style={{ width: "24%" }} />
                          </colgroup>
                          <thead><tr>
                            <SortTh label="Station" sortKey="station" />
                            <SortTh label="Active" sortKey="launched" />
                            <SortTh label="Licensed" sortKey="total" />
                            <SortTh label="Adoption %" sortKey="adoption" />
                          </tr></thead>
                          <tbody>
                            {paged.map((st) => {
                              const lic = licenseCount(st);
                              const act = groupActiveLicenses(st);
                              const pct = lic > 0 ? Math.round(act / lic * 100) : 0;
                              const isSelected = stationReport?.station?.station_id === st.station_id;
                              return (
                                <tr key={st.station_id} onClick={() => selectFromList(st)} style={{ cursor: "pointer", transition: "background 0.1s", background: isSelected ? T.accentSoft : "" }} onMouseEnter={(e) => e.currentTarget.style.background = T.accentSoft} onMouseLeave={(e) => e.currentTarget.style.background = isSelected ? T.accentSoft : ""}>
                                  <td style={{ ...S.td, overflow: "hidden" }}>
                                    <div style={{ ...S.mono, color: isSelected ? T.accent : undefined }}>{st.station_id}</div>
                                    {st.site_name && (
                                      <div style={{ fontSize: 11, color: T.textDim, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{st.site_name}</div>
                                    )}
                                  </td>
                                  <td style={S.td}>{act}</td>
                                  <td style={S.td}>{lic}</td>
                                  <td style={S.td}>{pct}%</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 20px", borderTop: `1px solid ${T.border}` }}>
                          <span style={{ fontSize: 13, color: T.textMuted }}>Showing {sorted.length === 0 ? 0 : (page - 1) * perPage + 1}–{Math.min(page * perPage, sorted.length)} of {sorted.length} stations</span>
                          <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                            <Btn sm variant="ghost" onClick={() => setUtilStationPage((p) => Math.max(1, p - 1))} style={{ opacity: page <= 1 ? 0.4 : 1 }}>Prev</Btn>
                            <span style={{ fontSize: 13, color: T.textMuted, padding: "0 8px" }}>Page {page} of {totalPages}</span>
                            <Btn sm variant="ghost" onClick={() => setUtilStationPage((p) => Math.min(totalPages, p + 1))} style={{ opacity: page >= totalPages ? 0.4 : 1 }}>Next</Btn>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                  </>
                  )}

                {/* By VISN — same weekly utilization + doctor roster report as
                    By Station above, scoped by VISN instead. VISN is a small
                    fixed list already loaded (`visns`), so a plain <select>
                    replaces the station search's typeahead entirely. */}
                {utilizationSearchMode === "visn" && (
                <>
                  <div style={{ display: "flex", gap: 8, padding: "12px 16px", borderBottom: `1px solid ${T.border}`, alignItems: "center" }}>
                  <select
                    value={visnUtilSelected}
                    onChange={(e) => {
                      const val = e.target.value;
                      setVisnUtilSelected(val);
                      setVisnWeeklyModalOpen(false);
                      setVisnDoctorsModalOpen(false);
                      if (val) runVisnSearch(val);
                      else { setVisnReport(null); setVisnReportError(null); }
                    }}
                    style={{ ...S.filterSelect, width: 260 }}
                  >
                    <option value="">Choose a VISN…</option>
                    {visnOptions.map((v) => <option key={v} value={v}>VISN {v}</option>)}
                  </select>
                  </div>

                  {/* Browse every VISN — same table shape as the Launch
                      Report's By VISN list, but selecting a row runs the
                      Utilization search instead of opening a side panel. */}
                  {(() => {
                    const perPage = 10;
                    const toggleUtilVisnSort = (key) => {
                      setUtilVisnSort((s) => ({ key, dir: s.key === key && s.dir === "desc" ? "asc" : "desc" }));
                      setUtilVisnPage(1);
                    };
                    const sorted = visnAgg
                      .filter((v) => v.licensed > 0)
                      .sort((a, b) => {
                        const rawA = a.visn.replace(/^VISN /, ""), rawB = b.visn.replace(/^VISN /, "");
                        const act_a = visnActiveLicensesByNum[rawA] ?? 0, pct_a = a.licensed > 0 ? act_a / a.licensed : 0;
                        const act_b = visnActiveLicensesByNum[rawB] ?? 0, pct_b = b.licensed > 0 ? act_b / b.licensed : 0;
                        let diff = 0;
                        if (utilVisnSort.key === "launched") diff = act_b - act_a;
                        else if (utilVisnSort.key === "total") diff = b.licensed - a.licensed;
                        else if (utilVisnSort.key === "adoption") diff = pct_b - pct_a;
                        else diff = a.visn.localeCompare(b.visn, undefined, { numeric: true });
                        if (utilVisnSort.dir === "asc") diff = -diff;
                        return diff !== 0 ? diff : a.visn.localeCompare(b.visn, undefined, { numeric: true });
                      });
                    const totalPages = Math.max(1, Math.ceil(sorted.length / perPage));
                    const page = Math.min(utilVisnPage, totalPages);
                    const paged = sorted.slice((page - 1) * perPage, page * perPage);
                    const selectFromList = (v) => {
                      const raw = v.visn.replace(/^VISN /, "");
                      setVisnUtilSelected(raw);
                      setVisnWeeklyModalOpen(false);
                      setVisnDoctorsModalOpen(false);
                      runVisnSearch(raw);
                    };
                    return !activityBreakdownData.visn ? (
                      <div style={{ padding: 32, textAlign: "center", fontSize: 13, color: T.textDim }}>Loading VISNs...</div>
                    ) : (
                      <div style={{ ...S.tableWrap, border: "none", borderRadius: 0, boxShadow: "none" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                          <colgroup>
                            <col style={{ width: "40%" }} /><col style={{ width: "20%" }} /><col style={{ width: "20%" }} /><col style={{ width: "20%" }} />
                          </colgroup>
                          <thead><tr>
                            {[["VISN", "visn"], ["Active", "launched"], ["Licensed", "total"], ["Adoption %", "adoption"]].map(([label, key]) => {
                              const active = utilVisnSort.key === key;
                              return (
                                <th key={key} onClick={() => toggleUtilVisnSort(key)} style={{ ...S.th, color: active ? T.accent : T.text, cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}>
                                  {label} <span style={{ fontSize: 9, opacity: active ? 1 : 0.3 }}>{active && utilVisnSort.dir === "asc" ? "▲" : "▼"}</span>
                                </th>
                              );
                            })}
                          </tr></thead>
                          <tbody>
                            {paged.map((v) => {
                              const raw = v.visn.replace(/^VISN /, "");
                              const act = visnActiveLicensesByNum[raw] ?? 0;
                              const pct = v.licensed > 0 ? Math.round(act / v.licensed * 100) : 0;
                              const isSelected = visnReport?.visn === raw;
                              return (
                                <tr key={v.visn} onClick={() => selectFromList(v)} style={{ cursor: "pointer", transition: "background 0.1s", background: isSelected ? T.accentSoft : "" }} onMouseEnter={(e) => e.currentTarget.style.background = T.accentSoft} onMouseLeave={(e) => e.currentTarget.style.background = isSelected ? T.accentSoft : ""}>
                                  <td style={S.td}>{v.visn}</td>
                                  <td style={S.td}>{act}</td>
                                  <td style={S.td}>{v.licensed}</td>
                                  <td style={S.td}>{pct}%</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 20px", borderTop: `1px solid ${T.border}` }}>
                          <span style={{ fontSize: 13, color: T.textMuted }}>Showing {sorted.length === 0 ? 0 : (page - 1) * perPage + 1}–{Math.min(page * perPage, sorted.length)} of {sorted.length} VISNs</span>
                          <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                            <Btn sm variant="ghost" onClick={() => setUtilVisnPage((p) => Math.max(1, p - 1))} style={{ opacity: page <= 1 ? 0.4 : 1 }}>Prev</Btn>
                            <span style={{ fontSize: 13, color: T.textMuted, padding: "0 8px" }}>Page {page} of {totalPages}</span>
                            <Btn sm variant="ghost" onClick={() => setUtilVisnPage((p) => Math.min(totalPages, p + 1))} style={{ opacity: page >= totalPages ? 0.4 : 1 }}>Next</Btn>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                  </>
                  )}

                {utilizationSearchMode === "rise" && (
                <>
                  <div style={{ display: "flex", gap: 8, padding: "12px 16px", borderBottom: `1px solid ${T.border}`, alignItems: "center" }}>
                  <select
                    value={riseUtilSelected}
                    onChange={(e) => {
                      const val = e.target.value;
                      setRiseUtilSelected(val);
                      setRiseWeeklyModalOpen(false);
                      setRiseDoctorsModalOpen(false);
                      if (val) runRiseSearch(val);
                      else { setRiseReport(null); setRiseReportError(null); }
                    }}
                    style={{ ...S.filterSelect, width: 260 }}
                  >
                    <option value="">Choose a RISE…</option>
                    {riseOptions.map((v) => <option key={v} value={v}>RISE {v}</option>)}
                  </select>
                  </div>

                  {(() => {
                    const perPage = 10;
                    const toggleUtilRiseSort = (key) => {
                      setUtilRiseSort((s) => ({ key, dir: s.key === key && s.dir === "desc" ? "asc" : "desc" }));
                      setUtilRisePage(1);
                    };
                    const sorted = riseAgg
                      .filter((v) => v.licensed > 0 && v.riseKey !== UNASSIGNED_RISE)
                      .sort((a, b) => {
                        const act_a = riseActiveLicensesByNum[a.riseKey] ?? 0, pct_a = a.licensed > 0 ? act_a / a.licensed : 0;
                        const act_b = riseActiveLicensesByNum[b.riseKey] ?? 0, pct_b = b.licensed > 0 ? act_b / b.licensed : 0;
                        let diff = 0;
                        if (utilRiseSort.key === "launched") diff = act_b - act_a;
                        else if (utilRiseSort.key === "total") diff = b.licensed - a.licensed;
                        else if (utilRiseSort.key === "adoption") diff = pct_b - pct_a;
                        else diff = a.rise.localeCompare(b.rise, undefined, { numeric: true });
                        if (utilRiseSort.dir === "asc") diff = -diff;
                        return diff !== 0 ? diff : a.rise.localeCompare(b.rise, undefined, { numeric: true });
                      });
                    const totalPages = Math.max(1, Math.ceil(sorted.length / perPage));
                    const page = Math.min(utilRisePage, totalPages);
                    const paged = sorted.slice((page - 1) * perPage, page * perPage);
                    const selectFromList = (v) => {
                      setRiseUtilSelected(v.riseKey);
                      setRiseWeeklyModalOpen(false);
                      setRiseDoctorsModalOpen(false);
                      runRiseSearch(v.riseKey);
                    };
                    return !activityBreakdownData.new_visn ? (
                      <div style={{ padding: 32, textAlign: "center", fontSize: 13, color: T.textDim }}>Loading RISE…</div>
                    ) : (
                      <div style={{ ...S.tableWrap, border: "none", borderRadius: 0, boxShadow: "none" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                          <colgroup>
                            <col style={{ width: "40%" }} /><col style={{ width: "20%" }} /><col style={{ width: "20%" }} /><col style={{ width: "20%" }} />
                          </colgroup>
                          <thead><tr>
                            {[["RISE", "rise"], ["Active", "launched"], ["Licensed", "total"], ["Adoption %", "adoption"]].map(([label, key]) => {
                              const active = utilRiseSort.key === key;
                              return (
                                <th key={key} onClick={() => toggleUtilRiseSort(key)} style={{ ...S.th, color: active ? T.accent : T.text, cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}>
                                  {label} <span style={{ fontSize: 9, opacity: active ? 1 : 0.3 }}>{active && utilRiseSort.dir === "asc" ? "▲" : "▼"}</span>
                                </th>
                              );
                            })}
                          </tr></thead>
                          <tbody>
                            {paged.map((v) => {
                              const act = riseActiveLicensesByNum[v.riseKey] ?? 0;
                              const pct = v.licensed > 0 ? Math.round(act / v.licensed * 100) : 0;
                              const isSelected = String(riseReport?.new_visn) === v.riseKey;
                              return (
                                <tr key={v.riseKey} onClick={() => selectFromList(v)} style={{ cursor: "pointer", transition: "background 0.1s", background: isSelected ? T.accentSoft : "" }} onMouseEnter={(e) => e.currentTarget.style.background = T.accentSoft} onMouseLeave={(e) => e.currentTarget.style.background = isSelected ? T.accentSoft : ""}>
                                  <td style={S.td}>
                                    <div>{v.rise}</div>
                                    {v.visns.length > 0 && (
                                      <div style={{ fontSize: 11, color: T.textDim }}>VISN {v.visns.join(", ")}</div>
                                    )}
                                  </td>
                                  <td style={S.td}>{act}</td>
                                  <td style={S.td}>{v.licensed}</td>
                                  <td style={S.td}>{pct}%</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 20px", borderTop: `1px solid ${T.border}` }}>
                          <span style={{ fontSize: 13, color: T.textMuted }}>Showing {sorted.length === 0 ? 0 : (page - 1) * perPage + 1}–{Math.min(page * perPage, sorted.length)} of {sorted.length} RISEs</span>
                          <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                            <Btn sm variant="ghost" onClick={() => setUtilRisePage((p) => Math.max(1, p - 1))} style={{ opacity: page <= 1 ? 0.4 : 1 }}>Prev</Btn>
                            <span style={{ fontSize: 13, color: T.textMuted, padding: "0 8px" }}>Page {page} of {totalPages}</span>
                            <Btn sm variant="ghost" onClick={() => setUtilRisePage((p) => Math.min(totalPages, p + 1))} style={{ opacity: page >= totalPages ? 0.4 : 1 }}>Next</Btn>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                  </>
                  )}

                {/* By Specialty — same weekly utilization + doctor roster report as
                    By Station above, scoped by specialty instead. Specialty is a
                    small fixed list already loaded (`specialties`), so a plain
                    <select> replaces the station search's typeahead entirely. */}
                {utilizationSearchMode === "specialty" && (
                <>
                  <div style={{ display: "flex", gap: 8, padding: "12px 16px", borderBottom: `1px solid ${T.border}`, alignItems: "center" }}>
                  <select
                    value={specialtyUtilSelected}
                    onChange={(e) => {
                      const val = e.target.value;
                      setSpecialtyUtilSelected(val);
                      setSpecialtyWeeklyModalOpen(false);
                      setSpecialtyDoctorsModalOpen(false);
                      if (val) runSpecialtySearch(val);
                      else { setSpecialtyReport(null); setSpecialtyReportError(null); }
                    }}
                    style={{ ...S.filterSelect, width: 260 }}
                  >
                    <option value="">Choose a specialty…</option>
                    {specialties.map((sp) => <option key={sp} value={sp}>{sp}</option>)}
                  </select>
                  </div>

                  {/* Browse every specialty — same table shape as the Launch
                      Report's By Specialty list, but selecting a row runs the
                      Utilization search instead of opening a side panel. */}
                  {(() => {
                    const perPage = 10;
                    const toggleUtilSpSort = (key) => {
                      setUtilSpecialtySort((s) => ({ key, dir: s.key === key && s.dir === "desc" ? "asc" : "desc" }));
                      setUtilSpecialtyPage(1);
                    };
                    const sorted = specialtyAgg
                      .filter((sp) => sp.licensed > 0)
                      .sort((a, b) => {
                        const act_a = specialtyActiveLicensesByName[a.specialty] ?? 0, pct_a = a.licensed > 0 ? act_a / a.licensed : 0;
                        const act_b = specialtyActiveLicensesByName[b.specialty] ?? 0, pct_b = b.licensed > 0 ? act_b / b.licensed : 0;
                        let diff = 0;
                        if (utilSpecialtySort.key === "launched") diff = act_b - act_a;
                        else if (utilSpecialtySort.key === "total") diff = b.licensed - a.licensed;
                        else if (utilSpecialtySort.key === "adoption") diff = pct_b - pct_a;
                        else diff = a.specialty.localeCompare(b.specialty);
                        if (utilSpecialtySort.dir === "asc") diff = -diff;
                        return diff !== 0 ? diff : a.specialty.localeCompare(b.specialty);
                      });
                    const totalPages = Math.max(1, Math.ceil(sorted.length / perPage));
                    const page = Math.min(utilSpecialtyPage, totalPages);
                    const paged = sorted.slice((page - 1) * perPage, page * perPage);
                    const selectFromList = (sp) => {
                      setSpecialtyUtilSelected(sp.specialty);
                      setSpecialtyWeeklyModalOpen(false);
                      setSpecialtyDoctorsModalOpen(false);
                      runSpecialtySearch(sp.specialty);
                    };
                    return !mapReady || specialtySummaryLoading || !activityBreakdownData.specialty ? (
                      <div style={{ padding: 32, textAlign: "center", fontSize: 13, color: T.textDim }}>Loading specialties...</div>
                    ) : (
                      <div style={{ ...S.tableWrap, border: "none", borderRadius: 0, boxShadow: "none" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                          <colgroup>
                            <col style={{ width: "40%" }} /><col style={{ width: "20%" }} /><col style={{ width: "20%" }} /><col style={{ width: "20%" }} />
                          </colgroup>
                          <thead><tr>
                            {[["Specialty", "specialty"], ["Active", "launched"], ["Licensed", "total"], ["Adoption %", "adoption"]].map(([label, key]) => {
                              const active = utilSpecialtySort.key === key;
                              return (
                                <th key={key} onClick={() => toggleUtilSpSort(key)} style={{ ...S.th, color: active ? T.accent : T.text, cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}>
                                  {label} <span style={{ fontSize: 9, opacity: active ? 1 : 0.3 }}>{active && utilSpecialtySort.dir === "asc" ? "▲" : "▼"}</span>
                                </th>
                              );
                            })}
                          </tr></thead>
                          <tbody>
                            {paged.map((sp) => {
                              const act = specialtyActiveLicensesByName[sp.specialty] ?? 0;
                              const pct = sp.licensed > 0 ? Math.round(act / sp.licensed * 100) : 0;
                              const isSelected = specialtyReport?.specialty === sp.specialty;
                              return (
                                <tr key={sp.specialty} onClick={() => selectFromList(sp)} style={{ cursor: "pointer", transition: "background 0.1s", background: isSelected ? T.accentSoft : "" }} onMouseEnter={(e) => e.currentTarget.style.background = T.accentSoft} onMouseLeave={(e) => e.currentTarget.style.background = isSelected ? T.accentSoft : ""}>
                                  <td style={S.td}>{sp.specialty}</td>
                                  <td style={S.td}>{act}</td>
                                  <td style={S.td}>{sp.licensed}</td>
                                  <td style={S.td}>{pct}%</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 20px", borderTop: `1px solid ${T.border}` }}>
                          <span style={{ fontSize: 13, color: T.textMuted }}>Showing {sorted.length === 0 ? 0 : (page - 1) * perPage + 1}–{Math.min(page * perPage, sorted.length)} of {sorted.length} specialties</span>
                          <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                            <Btn sm variant="ghost" onClick={() => setUtilSpecialtyPage((p) => Math.max(1, p - 1))} style={{ opacity: page <= 1 ? 0.4 : 1 }}>Prev</Btn>
                            <span style={{ fontSize: 13, color: T.textMuted, padding: "0 8px" }}>Page {page} of {totalPages}</span>
                            <Btn sm variant="ghost" onClick={() => setUtilSpecialtyPage((p) => Math.min(totalPages, p + 1))} style={{ opacity: page >= totalPages ? 0.4 : 1 }}>Next</Btn>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                  </>
                  )}
                  </div>

                  {/* ── Drag divider ── */}
                  <div
                    onMouseDown={onDividerMouseDown}
                    style={{
                      width: 12, flexShrink: 0, cursor: "col-resize", display: "flex", alignItems: "center", justifyContent: "center",
                      zIndex: 10, position: "relative",
                    }}
                  >
                    <div style={{ width: 3, height: 40, borderRadius: 99, background: T.border, transition: "background 0.15s" }}
                      onMouseEnter={e => e.currentTarget.style.background = T.accent}
                      onMouseLeave={e => e.currentTarget.style.background = T.border}
                    />
                  </div>

                  {/* ── Detail panel ── */}
                  <div style={{ width: rightPanelWidth ?? undefined, flex: rightPanelWidth ? "none" : 2, flexShrink: 0, minWidth: 0, overflow: "hidden", borderLeft: `1px solid ${T.border}`, display: "flex", flexDirection: "column" }}>
                    {utilizationSearchMode === "station" ? (() => {
                      if (!stationReport && !stationReportLoading && !stationReportError) {
                        return (
                          <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: T.textDim, fontSize: 13, gap: 8, padding: 24, textAlign: "center" }}>
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 9h6M9 12h6M9 15h4"/></svg>
                            <div>Select a station to view its utilization report</div>
                          </div>
                        );
                      }
                      if (stationReportLoading && !stationReport) {
                        return (
                          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: T.textDim, fontSize: 13, padding: 24 }}>Loading…</div>
                        );
                      }
                      return (
                        <div style={{ padding: 16, overflowY: "auto", display: "flex", flexDirection: "column", gap: 16 }}>
                  {stationReportError && (
                    <div style={{ fontSize: 13, color: T.red }}>{stationReportError}</div>
                  )}

                  {stationReport && (() => {
                    const { station, weekly, doctors } = stationReport;
                    // If this station belongs to a rolled-up group (e.g. "637"
                    // covers both 637 itself and satellite clinic 637GA), offer
                    // pills to drill into one specific sub-site instead of the
                    // group's combined data.
                    const group = groupedStations.find((g) => g.station_id === station.station_id || (g.station_ids || []).includes(station.station_id));
                    const showSiteFilter = group && group.subsite_count > 1;
                    return (
                      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
                          <div>
                            <div style={{ fontSize: 13, color: T.textMuted }}>
                              <strong style={{ color: T.text }}>{station.site_name || station.station_id}</strong>
                              {" · "}
                              <StationIdentity station={station} titleStyle={{ fontSize: 13, fontWeight: 600, color: T.text }} />
                            </div>
                            {/* Active/Licensed for this station's own doctor roster —
                                both scoped to is_current_period doctors only. The left
                                list's Active column (groupActiveLicenses, from
                                /reports/activity-breakdown) is now ALSO scoped to
                                is_current_period on the backend (doctor_info there
                                gained the same PROVIDER_CURRENT_PERIOD_WHERE check
                                /stations' licensed_to_date already used), so this side
                                stays in sync with it. Decommissioned (stale
                                report_month) doctors still show in the Drill-Down
                                table below, just excluded from both counts here and
                                labeled Decommissioned in their Status column. */}
                            <div style={{ fontSize: 12, color: T.textDim, marginTop: 2 }}>
                              <span style={{ color: T.accent, fontWeight: 600 }}>{doctors.filter((d) => d.is_current_period && d.has_recordings).length.toLocaleString()}</span> active / <span style={{ color: T.textMuted, fontWeight: 600 }}>{doctors.filter((d) => d.is_current_period).length.toLocaleString()}</span> licensed
                            </div>
                          </div>
                          <button
                            onClick={downloadStationUtilizationXlsx}
                            style={{ fontSize: 13, padding: "5px 12px", borderRadius: T.radiusSm, background: T.accent, color: "#fff", border: "none", cursor: "pointer", fontFamily: T.font, fontWeight: 500, whiteSpace: "nowrap" }}
                          >
                            ⬇ Export XLSX
                          </button>
                        </div>

                        {showSiteFilter && (
                          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                            <span style={{ fontSize: 11, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.6 }}>Filter to site:</span>
                            <select
                              value={station.station_id === group.station_id ? "" : station.station_id}
                              onChange={(e) => {
                                const val = e.target.value;
                                if (!val) runStationSearch(group.station_id);
                                else runStationSearch(val, { exact: true });
                              }}
                              style={{ ...S.filterSelect, minWidth: 260 }}
                            >
                              <option value="">All sites ({group.subsite_count})</option>
                              {group.sub_sites.map((site) => (
                                <option key={site.station_id} value={site.station_id}>
                                  {site.station_id}{site.site_name ? ` — ${site.site_name}` : ""}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}

                        {/* Weekly recordings / patients / utilization score — preview of the
                            most recent weeks inline, full history behind "View full table" */}
                        <div>
                          <div style={{ fontSize: 12, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8 }}>Weekly Utilization</div>
                          {weekly.length === 0 ? (
                            <div style={{ padding: 20, textAlign: "center", fontSize: 13, color: T.textDim }}>No activity recorded for this station yet</div>
                          ) : (() => {
                            const PREVIEW_COUNT = 3;
                            const rows = [...weekly].reverse();
                            const previewRows = rows.slice(0, PREVIEW_COUNT);
                            return (
                              <>
                                <div style={{ border: `1px solid ${T.border}`, borderRadius: T.radiusSm, overflow: "hidden" }}>
                                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                    <thead><tr>{["Week", "Total Recordings", "Total Patients", "Utilization Score"].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
                                    <tbody>
                                      {previewRows.map((w, i) => (
                                        <tr key={w.week} style={{ background: i % 2 === 0 ? T.surface : T.surface2 }}>
                                          <td style={S.td}>{w.week}</td>
                                          <td style={{ ...S.td, fontWeight: 600, color: T.accent }}>{w.total_recordings.toLocaleString()}</td>
                                          <td style={S.td}>{w.total_patients.toLocaleString()}</td>
                                          <td style={S.td}>{w.utilization_score != null ? `${(w.utilization_score * 100).toFixed(2)}%` : "—"}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                                {rows.length > PREVIEW_COUNT && (
                                  <button
                                    onClick={() => setStationWeeklyModalOpen(true)}
                                    style={{ marginTop: 8, alignSelf: "flex-start", background: T.accentSoft, border: `1px solid ${T.accent}`, borderRadius: T.radiusSm, color: T.accent, fontSize: 13, padding: "4px 12px", cursor: "pointer", fontFamily: T.font, fontWeight: 600 }}
                                  >
                                    View full table ({rows.length} weeks)
                                  </button>
                                )}
                              </>
                            );
                          })()}
                        </div>

                        {/* Doctor drill-down — name/email/specialty/created/launched,
                            preview of the first few doctors inline, full roster behind
                            "View full table" */}
                        <div>
                          <div style={{ fontSize: 12, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8 }}>Doctor Drill-Down</div>
                          {doctors.length === 0 ? (
                            <div style={{ padding: 20, textAlign: "center", fontSize: 13, color: T.textDim }}>No doctors found for this station</div>
                          ) : (() => {
                            const PREVIEW_COUNT = 3;
                            const previewDoctors = doctors.slice(0, PREVIEW_COUNT);
                            return (
                              <>
                                <div style={{ border: `1px solid ${T.border}`, borderRadius: T.radiusSm, overflow: "hidden" }}>
                                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                    <thead><tr>{["Name", "Email", "Specialty", "Created At", "Launched Date", "Status"].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
                                    <tbody>
                                      {previewDoctors.map((d, i) => (
                                        <tr key={d.doctor_user_id} style={{ background: i % 2 === 0 ? T.surface : T.surface2 }}>
                                          <td style={S.td}>{d.provider_name || "—"}</td>
                                          <td style={{ ...S.td, color: T.textMuted }}>{d.email || "—"}</td>
                                          <td style={{ ...S.td, color: T.textMuted }}>{d.specialty || "—"}</td>
                                          <td style={{ ...S.td, color: T.textMuted }}>{fmtDate(d.created_at)}</td>
                                          <td style={{ ...S.td, color: T.textMuted }}>{(d.provider_name || "").trim().toLowerCase() === "placeholder" ? "Not Launched" : d.launched_date ? fmtDate(d.launched_date) : "—"}</td>
                                          <td style={S.td}>
                                            <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, fontWeight: 600, background: doctorStatusStyle(d).bg, color: doctorStatusStyle(d).color }}>
                                              {doctorStatusLabel(d)}
                                            </span>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                                {doctors.length > PREVIEW_COUNT && (
                                  <button
                                    onClick={() => setStationDoctorsModalOpen(true)}
                                    style={{ marginTop: 8, alignSelf: "flex-start", background: T.accentSoft, border: `1px solid ${T.accent}`, borderRadius: T.radiusSm, color: T.accent, fontSize: 13, padding: "4px 12px", cursor: "pointer", fontFamily: T.font, fontWeight: 600 }}
                                  >
                                    View full table ({doctors.length} doctors
                                    {doctors.filter((d) => !d.is_current_period).length > 0 ? `, ${doctors.filter((d) => !d.is_current_period).length} decommissioned` : ""})
                                  </button>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    );
                  })()}
                        </div>
                      );
                    })() : utilizationSearchMode === "visn" ? (() => {
                      if (!visnReport && !visnReportLoading && !visnReportError) {
                        return (
                          <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: T.textDim, fontSize: 13, gap: 8, padding: 24, textAlign: "center" }}>
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 9h6M9 12h6M9 15h4"/></svg>
                            <div>Select a VISN to view its utilization report</div>
                          </div>
                        );
                      }
                      if (visnReportLoading && !visnReport) {
                        return (
                          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: T.textDim, fontSize: 13, padding: 24 }}>Loading…</div>
                        );
                      }
                      return (
                        <div style={{ padding: 16, overflowY: "auto", display: "flex", flexDirection: "column", gap: 16 }}>
                  {visnReportError && (
                    <div style={{ fontSize: 13, color: T.red }}>{visnReportError}</div>
                  )}

                  {visnReport && !visnReportLoading && (() => {
                    const { visn, weekly, doctors } = visnReport;
                    return (
                      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
                          <div>
                            <div style={{ fontSize: 13, color: T.textMuted }}>
                              <strong style={{ color: T.text }}>VISN {visn}</strong> · {doctors.length.toLocaleString()} providers
                            </div>
                            {/* Active/Licensed for this VISN's own doctor roster —
                                both scoped to is_current_period, same reasoning as the
                                station/specialty reports above. */}
                            <div style={{ fontSize: 12, color: T.textDim, marginTop: 2 }}>
                              <span style={{ color: T.accent, fontWeight: 600 }}>{doctors.filter((d) => d.is_current_period && d.has_recordings).length.toLocaleString()}</span> active / <span style={{ color: T.textMuted, fontWeight: 600 }}>{doctors.filter((d) => d.is_current_period).length.toLocaleString()}</span> licensed
                            </div>
                          </div>
                          <button
                            onClick={downloadVisnUtilizationXlsx}
                            style={{ fontSize: 13, padding: "5px 12px", borderRadius: T.radiusSm, background: T.accent, color: "#fff", border: "none", cursor: "pointer", fontFamily: T.font, fontWeight: 500, whiteSpace: "nowrap" }}
                          >
                            ⬇ Export XLSX
                          </button>
                        </div>

                        {/* Weekly recordings / patients / utilization score — preview of the
                            most recent weeks inline, full history behind "View full table" */}
                        <div>
                          <div style={{ fontSize: 12, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8 }}>Weekly Utilization</div>
                          {weekly.length === 0 ? (
                            <div style={{ padding: 20, textAlign: "center", fontSize: 13, color: T.textDim }}>No activity recorded for this VISN yet</div>
                          ) : (() => {
                            const PREVIEW_COUNT = 3;
                            const rows = [...weekly].reverse();
                            const previewRows = rows.slice(0, PREVIEW_COUNT);
                            return (
                              <>
                                <div style={{ border: `1px solid ${T.border}`, borderRadius: T.radiusSm, overflow: "hidden" }}>
                                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                    <thead><tr>{["Week", "Total Recordings", "Total Patients", "Utilization Score"].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
                                    <tbody>
                                      {previewRows.map((w, i) => (
                                        <tr key={w.week} style={{ background: i % 2 === 0 ? T.surface : T.surface2 }}>
                                          <td style={S.td}>{w.week}</td>
                                          <td style={{ ...S.td, fontWeight: 600, color: T.accent }}>{w.total_recordings.toLocaleString()}</td>
                                          <td style={S.td}>{w.total_patients.toLocaleString()}</td>
                                          <td style={S.td}>{w.utilization_score != null ? `${(w.utilization_score * 100).toFixed(2)}%` : "—"}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                                {rows.length > PREVIEW_COUNT && (
                                  <button
                                    onClick={() => setVisnWeeklyModalOpen(true)}
                                    style={{ marginTop: 8, alignSelf: "flex-start", background: T.accentSoft, border: `1px solid ${T.accent}`, borderRadius: T.radiusSm, color: T.accent, fontSize: 13, padding: "4px 12px", cursor: "pointer", fontFamily: T.font, fontWeight: 600 }}
                                  >
                                    View full table ({rows.length} weeks)
                                  </button>
                                )}
                              </>
                            );
                          })()}
                        </div>

                        {/* Doctor drill-down — name/email/station/specialty/created/launched,
                            preview of the first few doctors inline, full roster behind
                            "View full table" */}
                        <div>
                          <div style={{ fontSize: 12, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8 }}>Doctor Drill-Down</div>
                          {doctors.length === 0 ? (
                            <div style={{ padding: 20, textAlign: "center", fontSize: 13, color: T.textDim }}>No doctors found for this VISN</div>
                          ) : (() => {
                            const PREVIEW_COUNT = 3;
                            const previewDoctors = doctors.slice(0, PREVIEW_COUNT);
                            return (
                              <>
                                <div style={{ border: `1px solid ${T.border}`, borderRadius: T.radiusSm, overflow: "hidden" }}>
                                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                    <thead><tr>{["Name", "Email", "Station", "Specialty", "Created At", "Launched Date", "Status"].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
                                    <tbody>
                                      {previewDoctors.map((d, i) => (
                                        <tr key={d.doctor_user_id} style={{ background: i % 2 === 0 ? T.surface : T.surface2 }}>
                                          <td style={S.td}>{d.provider_name || "—"}</td>
                                          <td style={{ ...S.td, color: T.textMuted }}>{d.email || "—"}</td>
                                          <td style={{ ...S.td, color: T.textMuted }}>{d.station_id || "—"}</td>
                                          <td style={{ ...S.td, color: T.textMuted }}>{d.specialty || "—"}</td>
                                          <td style={{ ...S.td, color: T.textMuted }}>{fmtDate(d.created_at)}</td>
                                          <td style={{ ...S.td, color: T.textMuted }}>{(d.provider_name || "").trim().toLowerCase() === "placeholder" ? "Not Launched" : d.launched_date ? fmtDate(d.launched_date) : "—"}</td>
                                          <td style={S.td}>
                                            <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, fontWeight: 600, background: doctorStatusStyle(d).bg, color: doctorStatusStyle(d).color }}>
                                              {doctorStatusLabel(d)}
                                            </span>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                                {doctors.length > PREVIEW_COUNT && (
                                  <button
                                    onClick={() => setVisnDoctorsModalOpen(true)}
                                    style={{ marginTop: 8, alignSelf: "flex-start", background: T.accentSoft, border: `1px solid ${T.accent}`, borderRadius: T.radiusSm, color: T.accent, fontSize: 13, padding: "4px 12px", cursor: "pointer", fontFamily: T.font, fontWeight: 600 }}
                                  >
                                    View full table ({doctors.length} doctors
                                    {doctors.filter((d) => !d.is_current_period).length > 0 ? `, ${doctors.filter((d) => !d.is_current_period).length} decommissioned` : ""})
                                  </button>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    );
                  })()}
                        </div>
                      );
                    })() : utilizationSearchMode === "rise" ? (() => {
                      if (!riseReport && !riseReportLoading && !riseReportError) {
                        return (
                          <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: T.textDim, fontSize: 13, gap: 8, padding: 24, textAlign: "center" }}>
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 9h6M9 12h6M9 15h4"/></svg>
                            <div>Select a RISE to view its utilization report</div>
                          </div>
                        );
                      }
                      if (riseReportLoading && !riseReport) {
                        return (
                          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: T.textDim, fontSize: 13, padding: 24 }}>Loading…</div>
                        );
                      }
                      return (
                        <div style={{ padding: 16, overflowY: "auto", display: "flex", flexDirection: "column", gap: 16 }}>
                  {riseReportError && (
                    <div style={{ fontSize: 13, color: T.red }}>{riseReportError}</div>
                  )}

                  {riseReport && !riseReportLoading && (() => {
                    const { new_visn, weekly, doctors } = riseReport;
                    const childVisns = riseAgg.find((r) => r.riseKey === String(new_visn))?.visns || [];
                    return (
                      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
                          <div>
                            <div style={{ fontSize: 13, color: T.textMuted }}>
                              <strong style={{ color: T.text }}>{formatRiseLabel(new_visn)}</strong>
                              {childVisns.length > 0 ? ` · VISN ${childVisns.join(", ")}` : ""}
                              {" · "}{doctors.length.toLocaleString()} providers
                            </div>
                            <div style={{ fontSize: 12, color: T.textDim, marginTop: 2 }}>
                              <span style={{ color: T.accent, fontWeight: 600 }}>{doctors.filter((d) => d.is_current_period && d.has_recordings).length.toLocaleString()}</span> active / <span style={{ color: T.textMuted, fontWeight: 600 }}>{doctors.filter((d) => d.is_current_period).length.toLocaleString()}</span> licensed
                            </div>
                          </div>
                          <button
                            onClick={downloadRiseUtilizationXlsx}
                            style={{ fontSize: 13, padding: "5px 12px", borderRadius: T.radiusSm, background: T.accent, color: "#fff", border: "none", cursor: "pointer", fontFamily: T.font, fontWeight: 500, whiteSpace: "nowrap" }}
                          >
                            ⬇ Export XLSX
                          </button>
                        </div>

                        <div>
                          <div style={{ fontSize: 12, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8 }}>Weekly Utilization</div>
                          {weekly.length === 0 ? (
                            <div style={{ padding: 20, textAlign: "center", fontSize: 13, color: T.textDim }}>No activity recorded for this RISE yet</div>
                          ) : (() => {
                            const PREVIEW_COUNT = 3;
                            const rows = [...weekly].reverse();
                            const previewRows = rows.slice(0, PREVIEW_COUNT);
                            return (
                              <>
                                <div style={{ border: `1px solid ${T.border}`, borderRadius: T.radiusSm, overflow: "hidden" }}>
                                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                    <thead><tr>{["Week", "Total Recordings", "Total Patients", "Utilization Score"].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
                                    <tbody>
                                      {previewRows.map((w, i) => (
                                        <tr key={w.week} style={{ background: i % 2 === 0 ? T.surface : T.surface2 }}>
                                          <td style={S.td}>{w.week}</td>
                                          <td style={{ ...S.td, fontWeight: 600, color: T.accent }}>{w.total_recordings.toLocaleString()}</td>
                                          <td style={S.td}>{w.total_patients.toLocaleString()}</td>
                                          <td style={S.td}>{w.utilization_score != null ? `${(w.utilization_score * 100).toFixed(2)}%` : "—"}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                                {rows.length > PREVIEW_COUNT && (
                                  <button
                                    onClick={() => setRiseWeeklyModalOpen(true)}
                                    style={{ marginTop: 8, alignSelf: "flex-start", background: T.accentSoft, border: `1px solid ${T.accent}`, borderRadius: T.radiusSm, color: T.accent, fontSize: 13, padding: "4px 12px", cursor: "pointer", fontFamily: T.font, fontWeight: 600 }}
                                  >
                                    View full table ({rows.length} weeks)
                                  </button>
                                )}
                              </>
                            );
                          })()}
                        </div>

                        <div>
                          <div style={{ fontSize: 12, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8 }}>Doctor Drill-Down</div>
                          {doctors.length === 0 ? (
                            <div style={{ padding: 20, textAlign: "center", fontSize: 13, color: T.textDim }}>No doctors found for this RISE</div>
                          ) : (() => {
                            const PREVIEW_COUNT = 3;
                            const previewDoctors = doctors.slice(0, PREVIEW_COUNT);
                            return (
                              <>
                                <div style={{ border: `1px solid ${T.border}`, borderRadius: T.radiusSm, overflow: "hidden" }}>
                                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                    <thead><tr>{["Name", "Email", "Station", "Specialty", "Created At", "Launched Date", "Status"].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
                                    <tbody>
                                      {previewDoctors.map((d, i) => (
                                        <tr key={d.doctor_user_id} style={{ background: i % 2 === 0 ? T.surface : T.surface2 }}>
                                          <td style={S.td}>{d.provider_name || "—"}</td>
                                          <td style={{ ...S.td, color: T.textMuted }}>{d.email || "—"}</td>
                                          <td style={{ ...S.td, color: T.textMuted }}>{d.station_id || "—"}</td>
                                          <td style={{ ...S.td, color: T.textMuted }}>{d.specialty || "—"}</td>
                                          <td style={{ ...S.td, color: T.textMuted }}>{fmtDate(d.created_at)}</td>
                                          <td style={{ ...S.td, color: T.textMuted }}>{(d.provider_name || "").trim().toLowerCase() === "placeholder" ? "Not Launched" : d.launched_date ? fmtDate(d.launched_date) : "—"}</td>
                                          <td style={S.td}>
                                            <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, fontWeight: 600, background: doctorStatusStyle(d).bg, color: doctorStatusStyle(d).color }}>
                                              {doctorStatusLabel(d)}
                                            </span>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                                {doctors.length > PREVIEW_COUNT && (
                                  <button
                                    onClick={() => setRiseDoctorsModalOpen(true)}
                                    style={{ marginTop: 8, alignSelf: "flex-start", background: T.accentSoft, border: `1px solid ${T.accent}`, borderRadius: T.radiusSm, color: T.accent, fontSize: 13, padding: "4px 12px", cursor: "pointer", fontFamily: T.font, fontWeight: 600 }}
                                  >
                                    View full table ({doctors.length} doctors
                                    {doctors.filter((d) => !d.is_current_period).length > 0 ? `, ${doctors.filter((d) => !d.is_current_period).length} decommissioned` : ""})
                                  </button>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    );
                  })()}
                        </div>
                      );
                    })() : (() => {
                      if (!specialtyReport && !specialtyReportLoading && !specialtyReportError) {
                        return (
                          <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: T.textDim, fontSize: 13, gap: 8, padding: 24, textAlign: "center" }}>
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 9h6M9 12h6M9 15h4"/></svg>
                            <div>Select a specialty to view its utilization report</div>
                          </div>
                        );
                      }
                      if (specialtyReportLoading && !specialtyReport) {
                        return (
                          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: T.textDim, fontSize: 13, padding: 24 }}>Loading…</div>
                        );
                      }
                      return (
                        <div style={{ padding: 16, overflowY: "auto", display: "flex", flexDirection: "column", gap: 16 }}>
                  {specialtyReportLoading && (
                    <div style={{ padding: 20, textAlign: "center", fontSize: 13, color: T.textDim }}>Loading…</div>
                  )}

                  {specialtyReportError && (
                    <div style={{ fontSize: 13, color: T.red }}>{specialtyReportError}</div>
                  )}

                  {specialtyReport && !specialtyReportLoading && (() => {
                    const { specialty, weekly, doctors } = specialtyReport;
                    return (
                      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
                          <div>
                            <div style={{ fontSize: 13, color: T.textMuted }}>
                              <strong style={{ color: T.text }}>{specialty}</strong> · {doctors.length.toLocaleString()} providers
                            </div>
                            {/* Active/Licensed for this specialty's own doctor roster —
                                both scoped to is_current_period, same reasoning as the
                                station report above. */}
                            <div style={{ fontSize: 12, color: T.textDim, marginTop: 2 }}>
                              <span style={{ color: T.accent, fontWeight: 600 }}>{doctors.filter((d) => d.is_current_period && d.has_recordings).length.toLocaleString()}</span> active / <span style={{ color: T.textMuted, fontWeight: 600 }}>{doctors.filter((d) => d.is_current_period).length.toLocaleString()}</span> licensed
                            </div>
                          </div>
                          <button
                            onClick={downloadSpecialtyUtilizationXlsx}
                            style={{ fontSize: 13, padding: "5px 12px", borderRadius: T.radiusSm, background: T.accent, color: "#fff", border: "none", cursor: "pointer", fontFamily: T.font, fontWeight: 500, whiteSpace: "nowrap" }}
                          >
                            ⬇ Export XLSX
                          </button>
                        </div>

                        {/* Weekly recordings / patients / utilization score — preview of the
                            most recent weeks inline, full history behind "View full table" */}
                        <div>
                          <div style={{ fontSize: 12, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8 }}>Weekly Utilization</div>
                          {weekly.length === 0 ? (
                            <div style={{ padding: 20, textAlign: "center", fontSize: 13, color: T.textDim }}>No activity recorded for this specialty yet</div>
                          ) : (() => {
                            const PREVIEW_COUNT = 3;
                            const rows = [...weekly].reverse();
                            const previewRows = rows.slice(0, PREVIEW_COUNT);
                            return (
                              <>
                                <div style={{ border: `1px solid ${T.border}`, borderRadius: T.radiusSm, overflow: "hidden" }}>
                                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                    <thead><tr>{["Week", "Total Recordings", "Total Patients", "Utilization Score"].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
                                    <tbody>
                                      {previewRows.map((w, i) => (
                                        <tr key={w.week} style={{ background: i % 2 === 0 ? T.surface : T.surface2 }}>
                                          <td style={S.td}>{w.week}</td>
                                          <td style={{ ...S.td, fontWeight: 600, color: T.accent }}>{w.total_recordings.toLocaleString()}</td>
                                          <td style={S.td}>{w.total_patients.toLocaleString()}</td>
                                          <td style={S.td}>{w.utilization_score != null ? `${(w.utilization_score * 100).toFixed(2)}%` : "—"}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                                {rows.length > PREVIEW_COUNT && (
                                  <button
                                    onClick={() => setSpecialtyWeeklyModalOpen(true)}
                                    style={{ marginTop: 8, alignSelf: "flex-start", background: T.accentSoft, border: `1px solid ${T.accent}`, borderRadius: T.radiusSm, color: T.accent, fontSize: 13, padding: "4px 12px", cursor: "pointer", fontFamily: T.font, fontWeight: 600 }}
                                  >
                                    View full table ({rows.length} weeks)
                                  </button>
                                )}
                              </>
                            );
                          })()}
                        </div>

                        {/* Doctor drill-down — name/email/station/created/launched,
                            preview of the first few doctors inline, full roster behind
                            "View full table" */}
                        <div>
                          <div style={{ fontSize: 12, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8 }}>Doctor Drill-Down</div>
                          {doctors.length === 0 ? (
                            <div style={{ padding: 20, textAlign: "center", fontSize: 13, color: T.textDim }}>No doctors found for this specialty</div>
                          ) : (() => {
                            const PREVIEW_COUNT = 3;
                            const previewDoctors = doctors.slice(0, PREVIEW_COUNT);
                            return (
                              <>
                                <div style={{ border: `1px solid ${T.border}`, borderRadius: T.radiusSm, overflow: "hidden" }}>
                                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                    <thead><tr>{["Name", "Email", "Station", "Created At", "Launched Date", "Status"].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
                                    <tbody>
                                      {previewDoctors.map((d, i) => (
                                        <tr key={d.doctor_user_id} style={{ background: i % 2 === 0 ? T.surface : T.surface2 }}>
                                          <td style={S.td}>{d.provider_name || "—"}</td>
                                          <td style={{ ...S.td, color: T.textMuted }}>{d.email || "—"}</td>
                                          <td style={{ ...S.td, color: T.textMuted }}>{d.station_id || "—"}</td>
                                          <td style={{ ...S.td, color: T.textMuted }}>{fmtDate(d.created_at)}</td>
                                          <td style={{ ...S.td, color: T.textMuted }}>{(d.provider_name || "").trim().toLowerCase() === "placeholder" ? "Not Launched" : d.launched_date ? fmtDate(d.launched_date) : "—"}</td>
                                          <td style={S.td}>
                                            <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, fontWeight: 600, background: doctorStatusStyle(d).bg, color: doctorStatusStyle(d).color }}>
                                              {doctorStatusLabel(d)}
                                            </span>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                                {doctors.length > PREVIEW_COUNT && (
                                  <button
                                    onClick={() => setSpecialtyDoctorsModalOpen(true)}
                                    style={{ marginTop: 8, alignSelf: "flex-start", background: T.accentSoft, border: `1px solid ${T.accent}`, borderRadius: T.radiusSm, color: T.accent, fontSize: 13, padding: "4px 12px", cursor: "pointer", fontFamily: T.font, fontWeight: 600 }}
                                  >
                                    View full table ({doctors.length} doctors
                                    {doctors.filter((d) => !d.is_current_period).length > 0 ? `, ${doctors.filter((d) => !d.is_current_period).length} decommissioned` : ""})
                                  </button>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    );
                  })()}
                        </div>
                      );
                    })()}
                  </div>
                </div>

              </div>
            )}

            {momMetric === "all" && launchUtilView === "launch" && (
              <div style={{ padding: "0 20px" }}>
                <div style={{
                  display: "flex", gap: 10, alignItems: "flex-start",
                  background: T.accentSoft, border: `1px solid ${T.accent}`, borderRadius: T.radiusSm,
                  padding: "10px 14px", marginBottom: 16,
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, marginTop: 2 }}>
                    <rect x="3" y="13" width="4.5" height="8" rx="1" fill={T.accent} />
                    <rect x="9.75" y="8" width="4.5" height="13" rx="1" fill={T.accent} opacity={0.75} />
                    <rect x="16.5" y="3" width="4.5" height="18" rx="1" fill={T.accent} opacity={0.5} />
                  </svg>
                  <div style={{ fontSize: 13, color: T.text, lineHeight: 1.6 }}>
                    Track launch progress by station, VISN, RISE, or specialty — each row shows how many licensed users have gone live out of the total licensed there. Sort any column, search to narrow the list, and click a row to open its detail panel with weekly activity and the full provider roster.
                  </div>
                </div>
      <div ref={containerRef} style={{ display: "flex", gap: 0, alignItems: "stretch" }}>
        {/* left: tabs + content */}
        <div style={{ flex: rightPanelWidth ? 1 : 3, minWidth: 0, overflow: "hidden", display: "flex", flexDirection: "column", marginRight: 0 }}>
          {/* ── Tabs ── */}
          {/* Own toggle for the Launch Report, kept in sync with the
              Utilization Report's tabs above: picking Station/VISN/Specialty
              here also switches Utilization Report to the matching tab, so
              flipping the light-switch between the two reports lands on the
              same grouping instead of resetting. */}
          <div style={{ display: "flex", gap: 0, borderBottom: `1px solid ${T.border}` }}>
            {ENGAGEMENT_GROUP_TABS.map(({ launch, util, label }) => (
              <button
                key={launch}
                onClick={() => {
                  setDashInner(launch);
                  setUtilizationSearchMode(util);
                }}
                style={{ ...S.innertab(dashInner === launch) }}
              >{label}</button>
            ))}
          </div>

          {/* ── By Station ── */}
          {dashInner === "by-station" && (
            <>
              <div style={{ display: "flex", flexWrap: "wrap", rowGap: 8, gap: 8, padding: "12px 16px", borderBottom: `1px solid ${T.border}`, alignItems: "center" }}>
                <RisePill id="bs-rise" />
                <VisnPill id="bs-visn" />
                <SpecPill id="bs-spec" />
                <input
                  style={{ ...S.filterInput, flex: 1, minWidth: 100, width: "auto", background: T.bg, border: `1px solid ${T.border}` }}
                  placeholder="Search stations..."
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setStationPage(1); }}
                />
                {hasAnyFilter && (
                  <button style={{ background: "none", border: "none", color: T.textMuted, cursor: "pointer", fontSize: 13, fontFamily: T.font, whiteSpace: "nowrap" }} onClick={clearAll}>Clear all</button>
                )}
              </div>
              {(() => {
                const perPage = 10;
                const toggleStSort = (key) => {
                  setStationSort((s) => ({ key, dir: s.key === key && s.dir === "desc" ? "asc" : "desc" }));
                  setStationPage(1);
                };
                // Only stations that actually have at least one license —
                // stationsWithLic already exists above for exactly this.
                const sorted = [...stationsWithLic].sort((a, b) => {
                  const lic_a = licenseCount(a), act_a = activeCount(a), pct_a = lic_a > 0 ? act_a / lic_a : 0;
                  const lic_b = licenseCount(b), act_b = activeCount(b), pct_b = lic_b > 0 ? act_b / lic_b : 0;
                  let diff = 0;
                  if (stationSort.key === "launched") diff = act_b - act_a;
                  else if (stationSort.key === "total") diff = lic_b - lic_a;
                  else if (stationSort.key === "adoption") diff = pct_b - pct_a;
                  else diff = (a.station_id || "").localeCompare(b.station_id || "");
                  if (stationSort.dir === "asc") diff = -diff;
                  return diff !== 0 ? diff : (a.station_id || "").localeCompare(b.station_id || "");
                });
                const totalPages = Math.max(1, Math.ceil(sorted.length / perPage));
                const page = Math.min(stationPage, totalPages);
                const paged = sorted.slice((page - 1) * perPage, page * perPage);
                const SortTh = ({ label, sortKey }) => {
                  const active = stationSort.key === sortKey;
                  return (
                    <th onClick={() => toggleStSort(sortKey)} style={{ ...S.th, color: active ? T.accent : T.text, cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}>
                      {label} <span style={{ fontSize: 9, opacity: active ? 1 : 0.3 }}>{active && stationSort.dir === "asc" ? "▲" : "▼"}</span>
                    </th>
                  );
                };
                return (
                  <div style={{ ...S.tableWrap, border: "none", borderRadius: 0, boxShadow: "none" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                      <colgroup>
                        <col style={{ width: "28%" }} /><col style={{ width: "24%" }} /><col style={{ width: "24%" }} /><col style={{ width: "24%" }} />
                      </colgroup>
                      <thead><tr>
                        <SortTh label="Station" sortKey="station" />
                        <SortTh label="Launched" sortKey="launched" />
                        <SortTh label="Total" sortKey="total" />
                        <SortTh label="Launch %" sortKey="adoption" />
                      </tr></thead>
                      <tbody>
                        {paged.map((st) => {
                          const lic = licenseCount(st);
                          const act = activeCount(st);
                          const pct = lic > 0 ? Math.round(act / lic * 100) : 0;
                          const isSelected = selectedStation?.station_id === st.station_id;
                          return (
                            <tr key={st.station_id} onClick={() => selectStation(st)} style={{ cursor: "pointer", transition: "all 0.15s", background: isSelected ? "linear-gradient(90deg, rgba(124,58,237,0.08), rgba(232,146,10,0.04))" : "", boxShadow: isSelected ? "inset 3px 0 0 #7c3aed" : "none" }} onMouseEnter={(e) => { e.currentTarget.style.background = "linear-gradient(90deg, rgba(124,58,237,0.06), rgba(232,146,10,0.03))"; }} onMouseLeave={(e) => { e.currentTarget.style.background = isSelected ? "linear-gradient(90deg, rgba(124,58,237,0.08), rgba(232,146,10,0.04))" : ""; }}>
                              <td style={{ ...S.td, overflow: "hidden" }}>
                                <div style={{ ...S.mono, color: isSelected ? T.accent : undefined }}>{st.station_id}</div>
                                {st.site_name && (
                                  <div style={{ fontSize: 11, color: T.textDim, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{st.site_name}</div>
                                )}
                              </td>
                              <td style={S.td}>{act}</td>
                              <td style={S.td}>{lic}</td>
                              <td style={S.td}>{pct}%</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 20px", borderTop: `1px solid ${T.border}` }}>
                      <span style={{ fontSize: 13, color: T.textMuted }}>Showing {(page - 1) * perPage + 1}–{Math.min(page * perPage, sorted.length)} of {sorted.length} station groups</span>
                      <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                        <Btn sm variant="ghost" onClick={() => setStationPage((p) => Math.max(1, p - 1))} style={{ opacity: page <= 1 ? 0.4 : 1 }}>Prev</Btn>
                        <span style={{ fontSize: 13, color: T.textMuted, padding: "0 8px" }}>Page {page} of {totalPages}</span>
                        <Btn sm variant="ghost" onClick={() => setStationPage((p) => Math.min(totalPages, p + 1))} style={{ opacity: page >= totalPages ? 0.4 : 1 }}>Next</Btn>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </>
          )}

          {/* ── By VISN ── */}
          {dashInner === "by-visn" && (() => {
            const toggleVisnSort = (key) => {
              setVisnSort((s) => ({ key, dir: s.key === key && s.dir === "desc" ? "asc" : "desc" }));
              setVisnPage(1);
            };
            const sortedVisns = visnAgg
              .filter((v) => v.licensed > 0)
              .filter((v) => !visnSearch || v.visn.toLowerCase().includes(visnSearch.toLowerCase()))
              .sort((a, b) => {
                const pct_a = a.licensed > 0 ? a.activated / a.licensed : 0;
                const pct_b = b.licensed > 0 ? b.activated / b.licensed : 0;
                let diff = 0;
                if (visnSort.key === "launched") diff = b.activated - a.activated;
                else if (visnSort.key === "total") diff = b.licensed - a.licensed;
                else if (visnSort.key === "adoption") diff = pct_b - pct_a;
                else diff = a.visn.localeCompare(b.visn, undefined, { numeric: true });
                if (visnSort.dir === "asc") diff = -diff;
                return diff !== 0 ? diff : a.visn.localeCompare(b.visn, undefined, { numeric: true });
              });
            const visnPerPage = 10;
            const visnTotalPages = Math.max(1, Math.ceil(sortedVisns.length / visnPerPage));
            const visnPg = Math.min(visnPage, visnTotalPages);
            const pagedVisns = sortedVisns.slice((visnPg - 1) * visnPerPage, visnPg * visnPerPage);
            return (
              <>
                <div style={{ display: "flex", flexWrap: "wrap", rowGap: 8, gap: 8, padding: "12px 16px", borderBottom: `1px solid ${T.border}`, alignItems: "center" }}>
                  <RisePill id="bv-rise" />
                  <VisnPill id="bv-visn" />
                  <SpecPill id="bv-spec" />
                  <input
                    style={{ ...S.filterInput, flex: 1, minWidth: 100, width: "auto", background: T.bg, border: `1px solid ${T.border}` }}
                    placeholder="Search VISNs..."
                    value={visnSearch}
                    onChange={(e) => { setVisnSearch(e.target.value); setVisnPage(1); }}
                  />
                  {(hasAnyFilter || visnSearch) && (
                    <button style={{ background: "none", border: "none", color: T.textMuted, cursor: "pointer", fontSize: 13, fontFamily: T.font }} onClick={() => { clearAll(); setVisnSearch(""); setVisnPage(1); }}>Clear all</button>
                  )}
                </div>
                <div style={{ ...S.tableWrap, border: "none", borderRadius: 0, boxShadow: "none" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                    <colgroup>
                      <col style={{ width: "28%" }} /><col style={{ width: "24%" }} /><col style={{ width: "24%" }} /><col style={{ width: "24%" }} />
                    </colgroup>
                    <thead><tr>
                      {[["VISN", "visn"], ["Launched", "launched"], ["Total", "total"], ["Launch %", "adoption"]].map(([label, key]) => {
                        const active = visnSort.key === key;
                        return (
                          <th key={key} onClick={() => toggleVisnSort(key)} style={{ ...S.th, color: active ? T.accent : T.text, cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}>
                            {label} <span style={{ fontSize: 9, opacity: active ? 1 : 0.3 }}>{active && visnSort.dir === "asc" ? "▲" : "▼"}</span>
                          </th>
                        );
                      })}
                    </tr></thead>
                    <tbody>
                      {pagedVisns.map((v) => {
                        const pct = v.licensed > 0 ? Math.round(v.activated / v.licensed * 100) : 0;
                        const isVisnSelected = selectedVisn?.visn === v.visn;
                        return (
                          <tr key={v.visn} onClick={() => selectVisn(v)} style={{ cursor: "pointer", transition: "background 0.1s", background: isVisnSelected ? T.accentSoft : "" }} onMouseEnter={(e) => e.currentTarget.style.background = T.accentSoft} onMouseLeave={(e) => e.currentTarget.style.background = isVisnSelected ? T.accentSoft : ""}>
                            <td style={S.td}>{v.visn}</td>
                            <td style={S.td}>{v.activated}</td>
                            <td style={S.td}>{v.licensed}</td>
                            <td style={S.td}>{pct}%</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 20px", borderTop: `1px solid ${T.border}` }}>
                    <span style={{ fontSize: 13, color: T.textMuted }}>Showing {(visnPg - 1) * visnPerPage + 1}–{Math.min(visnPg * visnPerPage, sortedVisns.length)} of {sortedVisns.length} VISNs</span>
                    <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                      <Btn sm variant="ghost" onClick={() => setVisnPage((p) => Math.max(1, p - 1))} style={{ opacity: visnPg <= 1 ? 0.4 : 1 }}>Prev</Btn>
                      <span style={{ fontSize: 13, color: T.textMuted, padding: "0 8px" }}>Page {visnPg} of {visnTotalPages}</span>
                      <Btn sm variant="ghost" onClick={() => setVisnPage((p) => Math.min(visnTotalPages, p + 1))} style={{ opacity: visnPg >= visnTotalPages ? 0.4 : 1 }}>Next</Btn>
                    </div>
                  </div>
                </div>
              </>
            );
          })()}

          {dashInner === "by-rise" && (() => {
            const toggleRiseSort = (key) => {
              setRiseSort((s) => ({ key, dir: s.key === key && s.dir === "desc" ? "asc" : "desc" }));
              setRisePage(1);
            };
            const sortedRises = riseAgg
              .filter((v) => v.licensed > 0)
              .filter((v) => !riseSearch || v.rise.toLowerCase().includes(riseSearch.toLowerCase()) || v.visns.some((n) => n.includes(riseSearch)))
              .sort((a, b) => {
                const pct_a = a.licensed > 0 ? a.activated / a.licensed : 0;
                const pct_b = b.licensed > 0 ? b.activated / b.licensed : 0;
                let diff = 0;
                if (riseSort.key === "launched") diff = b.activated - a.activated;
                else if (riseSort.key === "total") diff = b.licensed - a.licensed;
                else if (riseSort.key === "adoption") diff = pct_b - pct_a;
                else diff = a.rise.localeCompare(b.rise, undefined, { numeric: true });
                if (riseSort.dir === "asc") diff = -diff;
                return diff !== 0 ? diff : a.rise.localeCompare(b.rise, undefined, { numeric: true });
              });
            const risePerPage = 10;
            const riseTotalPages = Math.max(1, Math.ceil(sortedRises.length / risePerPage));
            const risePg = Math.min(risePage, riseTotalPages);
            const pagedRises = sortedRises.slice((risePg - 1) * risePerPage, risePg * risePerPage);
            return (
              <>
                <div style={{ display: "flex", flexWrap: "wrap", rowGap: 8, gap: 8, padding: "12px 16px", borderBottom: `1px solid ${T.border}`, alignItems: "center" }}>
                  <RisePill id="br-rise" />
                  <VisnPill id="br-visn" />
                  <SpecPill id="br-spec" />
                  <input
                    style={{ ...S.filterInput, flex: 1, minWidth: 100, width: "auto", background: T.bg, border: `1px solid ${T.border}` }}
                    placeholder="Search RISE..."
                    value={riseSearch}
                    onChange={(e) => { setRiseSearch(e.target.value); setRisePage(1); }}
                  />
                  {(hasAnyFilter || riseSearch) && (
                    <button style={{ background: "none", border: "none", color: T.textMuted, cursor: "pointer", fontSize: 13, fontFamily: T.font }} onClick={() => { clearAll(); setRiseSearch(""); setRisePage(1); }}>Clear all</button>
                  )}
                </div>
                <div style={{ ...S.tableWrap, border: "none", borderRadius: 0, boxShadow: "none" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                    <colgroup>
                      <col style={{ width: "28%" }} /><col style={{ width: "24%" }} /><col style={{ width: "24%" }} /><col style={{ width: "24%" }} />
                    </colgroup>
                    <thead><tr>
                      {[["RISE", "rise"], ["Launched", "launched"], ["Total", "total"], ["Launch %", "adoption"]].map(([label, key]) => {
                        const active = riseSort.key === key;
                        return (
                          <th key={key} onClick={() => toggleRiseSort(key)} style={{ ...S.th, color: active ? T.accent : T.text, cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}>
                            {label} <span style={{ fontSize: 9, opacity: active ? 1 : 0.3 }}>{active && riseSort.dir === "asc" ? "▲" : "▼"}</span>
                          </th>
                        );
                      })}
                    </tr></thead>
                    <tbody>
                      {pagedRises.map((v) => {
                        const pct = v.licensed > 0 ? Math.round(v.activated / v.licensed * 100) : 0;
                        const isRiseSelected = selectedRise?.riseKey === v.riseKey;
                        return (
                          <tr key={v.riseKey} onClick={() => selectRise(v)} style={{ cursor: "pointer", transition: "background 0.1s", background: isRiseSelected ? T.accentSoft : "" }} onMouseEnter={(e) => e.currentTarget.style.background = T.accentSoft} onMouseLeave={(e) => e.currentTarget.style.background = isRiseSelected ? T.accentSoft : ""}>
                            <td style={S.td}>
                              <div>{v.rise}</div>
                              {v.visns.length > 0 && (
                                <div style={{ fontSize: 11, color: T.textDim }}>VISN {v.visns.join(", ")}</div>
                              )}
                            </td>
                            <td style={S.td}>{v.activated}</td>
                            <td style={S.td}>{v.licensed}</td>
                            <td style={S.td}>{pct}%</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 20px", borderTop: `1px solid ${T.border}` }}>
                    <span style={{ fontSize: 13, color: T.textMuted }}>Showing {sortedRises.length === 0 ? 0 : (risePg - 1) * risePerPage + 1}–{Math.min(risePg * risePerPage, sortedRises.length)} of {sortedRises.length} RISEs</span>
                    <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                      <Btn sm variant="ghost" onClick={() => setRisePage((p) => Math.max(1, p - 1))} style={{ opacity: risePg <= 1 ? 0.4 : 1 }}>Prev</Btn>
                      <span style={{ fontSize: 13, color: T.textMuted, padding: "0 8px" }}>Page {risePg} of {riseTotalPages}</span>
                      <Btn sm variant="ghost" onClick={() => setRisePage((p) => Math.min(riseTotalPages, p + 1))} style={{ opacity: risePg >= riseTotalPages ? 0.4 : 1 }}>Next</Btn>
                    </div>
                  </div>
                </div>
              </>
            );
          })()}

          {/* ── By Specialty ── */}
          {dashInner === "by-specialty" && (() => {
            const toggleSpecialtySort = (key) => {
              setSpecialtySort((s) => ({ key, dir: s.key === key && s.dir === "desc" ? "asc" : "desc" }));
              setSpecialtyPage(1);
            };
            const sortedSpecialties = specialtyAgg
              .filter((sp) => sp.licensed > 0)
              .filter((sp) => !specialtySearch || sp.specialty.toLowerCase().includes(specialtySearch.toLowerCase()))
              .sort((a, b) => {
                const pct_a = a.licensed > 0 ? a.activated / a.licensed : 0;
                const pct_b = b.licensed > 0 ? b.activated / b.licensed : 0;
                let diff = 0;
                if (specialtySort.key === "launched") diff = b.activated - a.activated;
                else if (specialtySort.key === "total") diff = b.licensed - a.licensed;
                else if (specialtySort.key === "adoption") diff = pct_b - pct_a;
                else diff = a.specialty.localeCompare(b.specialty);
                if (specialtySort.dir === "asc") diff = -diff;
                return diff !== 0 ? diff : a.specialty.localeCompare(b.specialty);
              });
            const specialtyPerPage = 10;
            const specialtyTotalPages = Math.max(1, Math.ceil(sortedSpecialties.length / specialtyPerPage));
            const specialtyPg = Math.min(specialtyPage, specialtyTotalPages);
            const pagedSpecialties = sortedSpecialties.slice((specialtyPg - 1) * specialtyPerPage, specialtyPg * specialtyPerPage);
            return (
              <>
                <div style={{ display: "flex", gap: 8, padding: "12px 16px", borderBottom: `1px solid ${T.border}`, alignItems: "center" }}>
                  <RisePill id="bsp-rise" />
                  <VisnPill id="bsp-visn" />
                  <input
                    style={{ ...S.filterInput, flex: 1, width: "auto", background: T.bg, border: `1px solid ${T.border}` }}
                    placeholder="Search specialties..."
                    value={specialtySearch}
                    onChange={(e) => { setSpecialtySearch(e.target.value); setSpecialtyPage(1); }}
                  />
                  {(hasAnyFilter || specialtySearch) && (
                    <button style={{ background: "none", border: "none", color: T.textMuted, cursor: "pointer", fontSize: 13, fontFamily: T.font }} onClick={() => { clearAll(); setSpecialtySearch(""); setSpecialtyPage(1); }}>Clear all</button>
                  )}
                </div>
                {!mapReady || specialtySummaryLoading ? (
                  <div style={{ padding: 32, textAlign: "center", fontSize: 13, color: T.textDim }}>Loading specialties...</div>
                ) : (
                  <div style={{ ...S.tableWrap, border: "none", borderRadius: 0, boxShadow: "none" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                      <colgroup>
                        <col style={{ width: "40%" }} /><col style={{ width: "20%" }} /><col style={{ width: "20%" }} /><col style={{ width: "20%" }} />
                      </colgroup>
                      <thead><tr>
                        {[["Specialty", "specialty"], ["Launched", "launched"], ["Total", "total"], ["Launch %", "adoption"]].map(([label, key]) => {
                          const active = specialtySort.key === key;
                          return (
                            <th key={key} onClick={() => toggleSpecialtySort(key)} style={{ ...S.th, color: active ? T.accent : T.text, cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}>
                              {label} <span style={{ fontSize: 9, opacity: active ? 1 : 0.3 }}>{active && specialtySort.dir === "asc" ? "▲" : "▼"}</span>
                            </th>
                          );
                        })}
                      </tr></thead>
                      <tbody>
                        {pagedSpecialties.map((sp) => {
                          const pct = sp.licensed > 0 ? Math.round(sp.activated / sp.licensed * 100) : 0;
                          const isSpSelected = selectedSpecialtyAgg?.specialty === sp.specialty;
                          return (
                            <tr key={sp.specialty} onClick={() => selectSpecialtyAgg(sp)} style={{ cursor: "pointer", transition: "background 0.1s", background: isSpSelected ? T.accentSoft : "" }} onMouseEnter={(e) => e.currentTarget.style.background = T.accentSoft} onMouseLeave={(e) => e.currentTarget.style.background = isSpSelected ? T.accentSoft : ""}>
                              <td style={S.td}>{sp.specialty}</td>
                              <td style={S.td}>{sp.activated}</td>
                              <td style={S.td}>{sp.licensed}</td>
                              <td style={S.td}>{pct}%</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 20px", borderTop: `1px solid ${T.border}` }}>
                      <span style={{ fontSize: 13, color: T.textMuted }}>Showing {sortedSpecialties.length === 0 ? 0 : (specialtyPg - 1) * specialtyPerPage + 1}–{Math.min(specialtyPg * specialtyPerPage, sortedSpecialties.length)} of {sortedSpecialties.length} specialties</span>
                      <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                        <Btn sm variant="ghost" onClick={() => setSpecialtyPage((p) => Math.max(1, p - 1))} style={{ opacity: specialtyPg <= 1 ? 0.4 : 1 }}>Prev</Btn>
                        <span style={{ fontSize: 13, color: T.textMuted, padding: "0 8px" }}>Page {specialtyPg} of {specialtyTotalPages}</span>
                        <Btn sm variant="ghost" onClick={() => setSpecialtyPage((p) => Math.min(specialtyTotalPages, p + 1))} style={{ opacity: specialtyPg >= specialtyTotalPages ? 0.4 : 1 }}>Next</Btn>
                      </div>
                    </div>
                  </div>
                )}
              </>
            );
          })()}

        </div>

        {/* ── Drag divider ── */}
        <div
          onMouseDown={onDividerMouseDown}
          style={{
            width: 12, flexShrink: 0, cursor: "col-resize", display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 10, position: "relative",
          }}
        >
          <div style={{ width: 3, height: 40, borderRadius: 99, background: T.border, transition: "background 0.15s" }}
            onMouseEnter={e => e.currentTarget.style.background = T.accent}
            onMouseLeave={e => e.currentTarget.style.background = T.border}
          />
        </div>

        {/* ── Detail panel (station, VISN, or decommissioned user) ── */}
        <div style={{ width: rightPanelWidth ?? undefined, flex: rightPanelWidth ? "none" : 2, flexShrink: 0, minWidth: 0, overflow: "hidden", borderLeft: `1px solid ${T.border}`, display: "flex", flexDirection: "column" }}>
          {dashInner === "by-visn" ? (
            /* ── VISN detail ── */
            !selectedVisn ? (
              <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: T.textDim, fontSize: 13, gap: 8, padding: 24, textAlign: "center" }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 9h6M9 12h6M9 15h4"/></svg>
                <div>Select a VISN to view detail</div>
              </div>
            ) : (() => {
              const visnNum = selectedVisn.visn.replace("VISN ", "");
              const visnStations = filtered
                .filter((st) => String(st.visn) === visnNum)
                .sort((a, b) => {
                  const pa = licenseCount(a) > 0 ? activeCount(a) / licenseCount(a) : 0;
                  const pb = licenseCount(b) > 0 ? activeCount(b) / licenseCount(b) : 0;
                  return pb - pa;
                });
              const visnSpCount = mapReady
                ? Object.values(specialtyPrefixMap).filter((spPrefixes) => {
                    const visnPrefixes = new Set(visnStations.map((st) => String(st.station_id || "").trim().slice(0, 3)).filter(Boolean));
                    return [...spPrefixes].some((p) => visnPrefixes.has(p));
                  }).length
                : "—";
              return (
                <>
                  {/* VISN Header */}
                  <div style={{ padding: "14px 16px", borderBottom: `1px solid ${T.border}` }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 16, fontWeight: 600 }}>{selectedVisn.visn}</span>
                        <span style={{ fontSize: 13, padding: "2px 7px", borderRadius: 20, background: T.accentSoft, color: T.accent, fontWeight: 500 }}>{selectedVisn.stations} stations</span>
                      </div>
                      <button onClick={() => setSelectedVisn(null)} style={{ background: "none", border: "none", cursor: "pointer", color: T.textDim, fontSize: 20, lineHeight: 1 }}>×</button>
                    </div>
                    <div style={{ fontSize: 13, color: T.textDim, marginBottom: 12 }}>
                      {selectedVisn.activeStations} launched stations · {visnSpCount} specialties
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                      {[
                        { label: "Launched", value: selectedVisn.activated },
                        { label: "Total", value: selectedVisn.licensed },
                        { label: "Remaining", value: Math.max(0, selectedVisn.licensed - selectedVisn.activated) },
                      ].map(({ label, value }) => (
                        <div key={label} style={{ background: "#F8F7FF", borderRadius: 8, padding: "8px 10px", textAlign: "center" }}>
                          <div style={{ fontSize: 16, fontWeight: 500, color: "#111", lineHeight: 1 }}>{value}</div>
                          <div style={{ fontSize: 9, color: T.textDim, textTransform: "uppercase", letterSpacing: "0.5px", marginTop: 3 }}>{label}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* Stations in VISN */}
                  <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
                    <div style={{ padding: "10px 16px 8px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <span style={{ fontSize: 13, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.6px", color: T.textDim }}>Stations</span>
                      <span style={{ fontSize: 13, color: T.textDim }}>{visnStations.length} total</span>
                    </div>
                    <div style={{ flex: 1, overflowY: "auto" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                          <tr>{["Station", "Launch %", "Launched"].map((h) => <th key={h} style={{ ...S.th, color: T.text, fontSize: 13, padding: "7px 12px" }}>{h}</th>)}</tr>
                        </thead>
                        <tbody>
                          {visnStations.map((st) => {
                            const lic = licenseCount(st);
                            const act = activeCount(st);
                            const pct = lic > 0 ? Math.round(act / lic * 100) : 0;
                            return (
                              <tr key={st.station_id} style={{ transition: "background 0.1s" }} onMouseEnter={(e) => e.currentTarget.style.background = T.accentSoft} onMouseLeave={(e) => e.currentTarget.style.background = ""}>
                                <td style={{ ...S.td, ...S.mono, fontSize: 13, padding: "7px 12px" }}>{st.station_id}</td>
                                <td style={{ ...S.td, fontSize: 13, padding: "7px 12px" }}>{pct}%</td>
                                <td style={{ ...S.td, fontSize: 13, padding: "7px 12px" }}>{act}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              );
            })()
          ) : dashInner === "by-rise" ? (
            !selectedRise ? (
              <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: T.textDim, fontSize: 13, gap: 8, padding: 24, textAlign: "center" }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 9h6M9 12h6M9 15h4"/></svg>
                <div>Select a RISE to view detail</div>
              </div>
            ) : (() => {
              const riseStations = filtered
                .filter((st) => (selectedRise.riseKey === UNASSIGNED_RISE ? (st.new_visn == null || st.new_visn === "") : String(st.new_visn) === selectedRise.riseKey))
                .sort((a, b) => {
                  const pa = licenseCount(a) > 0 ? activeCount(a) / licenseCount(a) : 0;
                  const pb = licenseCount(b) > 0 ? activeCount(b) / licenseCount(b) : 0;
                  return pb - pa;
                });
              const riseSpCount = mapReady
                ? Object.values(specialtyPrefixMap).filter((spPrefixes) => {
                    const risePrefixes = new Set(riseStations.map((st) => String(st.station_id || "").trim().slice(0, 3)).filter(Boolean));
                    return [...spPrefixes].some((p) => risePrefixes.has(p));
                  }).length
                : "—";
              return (
                <>
                  <div style={{ padding: "14px 16px", borderBottom: `1px solid ${T.border}` }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 16, fontWeight: 600 }}>{selectedRise.rise}</span>
                        <span style={{ fontSize: 13, padding: "2px 7px", borderRadius: 20, background: T.accentSoft, color: T.accent, fontWeight: 500 }}>{selectedRise.stations} stations</span>
                      </div>
                      <button onClick={() => setSelectedRise(null)} style={{ background: "none", border: "none", cursor: "pointer", color: T.textDim, fontSize: 20, lineHeight: 1 }}>×</button>
                    </div>
                    <div style={{ fontSize: 13, color: T.textDim, marginBottom: 12 }}>
                      {selectedRise.visns.length > 0 ? `VISN ${selectedRise.visns.join(", ")} · ` : ""}
                      {selectedRise.activeStations} launched stations · {riseSpCount} specialties
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                      {[
                        { label: "Launched", value: selectedRise.activated },
                        { label: "Total", value: selectedRise.licensed },
                        { label: "Remaining", value: Math.max(0, selectedRise.licensed - selectedRise.activated) },
                      ].map(({ label, value }) => (
                        <div key={label} style={{ background: "#F8F7FF", borderRadius: 8, padding: "8px 10px", textAlign: "center" }}>
                          <div style={{ fontSize: 16, fontWeight: 500, color: "#111", lineHeight: 1 }}>{value}</div>
                          <div style={{ fontSize: 9, color: T.textDim, textTransform: "uppercase", letterSpacing: "0.5px", marginTop: 3 }}>{label}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
                    <div style={{ padding: "10px 16px 8px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <span style={{ fontSize: 13, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.6px", color: T.textDim }}>Stations</span>
                      <span style={{ fontSize: 13, color: T.textDim }}>{riseStations.length} total</span>
                    </div>
                    <div style={{ flex: 1, overflowY: "auto" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                          <tr>{["Station", "Launch %", "Launched"].map((h) => <th key={h} style={{ ...S.th, color: T.text, fontSize: 13, padding: "7px 12px" }}>{h}</th>)}</tr>
                        </thead>
                        <tbody>
                          {riseStations.map((st) => {
                            const lic = licenseCount(st);
                            const act = activeCount(st);
                            const pct = lic > 0 ? Math.round(act / lic * 100) : 0;
                            return (
                              <tr key={st.station_id} style={{ transition: "background 0.1s" }} onMouseEnter={(e) => e.currentTarget.style.background = T.accentSoft} onMouseLeave={(e) => e.currentTarget.style.background = ""}>
                                <td style={{ ...S.td, ...S.mono, fontSize: 13, padding: "7px 12px" }}>{st.station_id}</td>
                                <td style={{ ...S.td, fontSize: 13, padding: "7px 12px" }}>{pct}%</td>
                                <td style={{ ...S.td, fontSize: 13, padding: "7px 12px" }}>{act}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              );
            })()
          ) : dashInner === "by-specialty" ? (
            /* ── Specialty detail ── */
            !selectedSpecialtyAgg ? (
              <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: T.textDim, fontSize: 13, gap: 8, padding: 24, textAlign: "center" }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 9h6M9 12h6M9 15h4"/></svg>
                <div>Select a specialty to view detail</div>
              </div>
            ) : (() => {
              const spPrefixes = specialtyPrefixMap[selectedSpecialtyAgg.specialty] || new Set();
              const spStations = filtered
                .filter((st) => spPrefixes.has(String(st.station_id || "").trim().slice(0, 3)))
                .sort((a, b) => {
                  const pa = licenseCount(a) > 0 ? activeCount(a) / licenseCount(a) : 0;
                  const pb = licenseCount(b) > 0 ? activeCount(b) / licenseCount(b) : 0;
                  return pb - pa;
                });
              return (
                <>
                  {/* Specialty Header */}
                  <div style={{ padding: "14px 16px", borderBottom: `1px solid ${T.border}` }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 16, fontWeight: 600 }}>{selectedSpecialtyAgg.specialty}</span>
                        <span style={{ fontSize: 13, padding: "2px 7px", borderRadius: 20, background: T.accentSoft, color: T.accent, fontWeight: 500 }}>{selectedSpecialtyAgg.stations} stations</span>
                      </div>
                      <button onClick={() => setSelectedSpecialtyAgg(null)} style={{ background: "none", border: "none", cursor: "pointer", color: T.textDim, fontSize: 20, lineHeight: 1 }}>×</button>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginTop: 12 }}>
                      {[
                        { label: "Launched", value: selectedSpecialtyAgg.activated },
                        { label: "Total", value: selectedSpecialtyAgg.licensed },
                        { label: "Remaining", value: Math.max(0, selectedSpecialtyAgg.licensed - selectedSpecialtyAgg.activated) },
                      ].map(({ label, value }) => (
                        <div key={label} style={{ background: "#F8F7FF", borderRadius: 8, padding: "8px 10px", textAlign: "center" }}>
                          <div style={{ fontSize: 16, fontWeight: 500, color: "#111", lineHeight: 1 }}>{value}</div>
                          <div style={{ fontSize: 9, color: T.textDim, textTransform: "uppercase", letterSpacing: "0.5px", marginTop: 3 }}>{label}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* Stations offering this specialty */}
                  {(() => {
                    const spPerPage = 10;
                    const spTotalPages = Math.max(1, Math.ceil(spStations.length / spPerPage));
                    const spPg = Math.min(specialtyDetailPage, spTotalPages);
                    const pagedSpStations = spStations.slice((spPg - 1) * spPerPage, spPg * spPerPage);
                    return (
                      <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
                        <div style={{ padding: "10px 16px 8px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          <span style={{ fontSize: 13, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.6px", color: T.textDim }}>Stations</span>
                          <span style={{ fontSize: 13, color: T.textDim }}>{spStations.length} total</span>
                        </div>
                        <div style={{ flex: 1, overflowY: "auto" }}>
                          <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead>
                              <tr>{["Station", "Launch %", "Launched"].map((h) => <th key={h} style={{ ...S.th, color: T.text, fontSize: 13, padding: "7px 12px" }}>{h}</th>)}</tr>
                            </thead>
                            <tbody>
                              {pagedSpStations.map((st) => {
                                const lic = licenseCount(st);
                                const act = activeCount(st);
                                const pct = lic > 0 ? Math.round(act / lic * 100) : 0;
                                return (
                                  <tr key={st.station_id} style={{ transition: "background 0.1s" }} onMouseEnter={(e) => e.currentTarget.style.background = T.accentSoft} onMouseLeave={(e) => e.currentTarget.style.background = ""}>
                                    <td style={{ ...S.td, ...S.mono, fontSize: 13, padding: "7px 12px" }}>{st.station_id}</td>
                                    <td style={{ ...S.td, fontSize: 13, padding: "7px 12px" }}>{pct}%</td>
                                    <td style={{ ...S.td, fontSize: 13, padding: "7px 12px" }}>{act}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 16px", borderTop: `1px solid ${T.border}` }}>
                          <span style={{ fontSize: 13, color: T.textDim }}>{spStations.length === 0 ? "0" : `${(spPg - 1) * spPerPage + 1}–${Math.min(spPg * spPerPage, spStations.length)}`} of {spStations.length}</span>
                          <div style={{ display: "flex", gap: 4 }}>
                            <Btn sm variant="ghost" onClick={() => setSpecialtyDetailPage((p) => Math.max(1, p - 1))} style={{ opacity: spPg <= 1 ? 0.4 : 1 }}>Prev</Btn>
                            <Btn sm variant="ghost" onClick={() => setSpecialtyDetailPage((p) => Math.min(spTotalPages, p + 1))} style={{ opacity: spPg >= spTotalPages ? 0.4 : 1 }}>Next</Btn>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </>
              );
            })()
          ) : (
            /* ── Station detail ── */
            !selectedStation ? (
            <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: T.textDim, fontSize: 13, gap: 8, padding: 24, textAlign: "center" }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 9h6M9 12h6M9 15h4"/></svg>
              <div>Select a station to view detail</div>
            </div>
          ) : (
            <>
              {/* Header */}
              <div style={{ padding: "14px 16px", borderBottom: `1px solid ${T.border}` }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <StationIdentity station={selectedStation} titleStyle={{ fontSize: 16, fontWeight: 600 }} />
                  </div>
                  <button onClick={() => { setSelectedStation(null); setStationProviders([]); setStationWeekly([]); }} style={{ background: "none", border: "none", cursor: "pointer", color: T.textDim, fontSize: 20, lineHeight: 1 }}>×</button>
                </div>
                <div style={{ fontSize: 13, color: T.textDim, marginBottom: 12 }}>
                  {selectedStation.city || "—"} · {selectedStation.subsite_count} sub-sites
                </div>
                {/* Quick metrics */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                  {[
                    { label: "Launched", value: activeCount(selectedStation) },
                    { label: "Total", value: licenseCount(selectedStation) },
                    { label: "Remaining", value: Math.max(0, licenseCount(selectedStation) - activeCount(selectedStation)) },
                  ].map(({ label, value }) => (
                    <div key={label} style={{ background: "#F8F7FF", borderRadius: 8, padding: "8px 10px", textAlign: "center" }}>
                      <div style={{ fontSize: 16, fontWeight: 500, color: "#111", lineHeight: 1 }}>{value}</div>
                      <div style={{ fontSize: 9, color: T.textDim, textTransform: "uppercase", letterSpacing: "0.5px", marginTop: 3 }}>{label}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Weekly bars */}
              <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}` }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.6px", color: T.textDim }}>Last 4 weeks</div>
                  <button onClick={async () => {
                    setWeeklyHistoryModal({ station: selectedStation, weeks: [], loading: true });
                    try {
                      const res = await fetchStationWeeklyHistory({ station_id: selectedStation.station_id });
                      setWeeklyHistoryModal({ station: selectedStation, weeks: res.weeks || [], loading: false });
                    } catch { setWeeklyHistoryModal((m) => ({ ...m, loading: false })); }
                  }} style={{ background: T.accentSoft, border: `1px solid ${T.accent}`, borderRadius: T.radiusSm, color: T.accent, fontSize: 13, padding: "3px 10px", cursor: "pointer", fontFamily: T.font, fontWeight: 600 }}>Full history</button>
                </div>
                {loadingDetail ? (
                  <div style={{ fontSize: 13, color: T.textDim, textAlign: "center", padding: "16px 0" }}>Loading...</div>
                ) : stationWeekly.length === 0 ? (
                  <div style={{ fontSize: 13, color: T.textDim, textAlign: "center", padding: "16px 0" }}>No weekly data</div>
                ) : (() => {
                  const counts = stationWeekly.map((w) => w.activated || 0);
                  const maxCount = Math.max(...counts, 1);
                  const barColors = ["#C4BAE8", "#9B76E1", "#7B5EDB", "#5B4BD5"];
                  const fmtWeek = (iso) => {
                    const d = new Date(iso + "T12:00:00Z");
                    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
                  };
                  return (
                    <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 80 }}>
                      {stationWeekly.map((w, i) => {
                        const count = counts[i];
                        const heightPct = maxCount > 0 ? (count / maxCount) * 100 : 0;
                        return (
                          <div key={w.week} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 3, height: "100%", justifyContent: "flex-end" }}>
                            <div style={{ fontSize: 13, fontWeight: 500, color: count > 0 ? (barColors[i] || T.accent) : T.textDim }}>{count > 0 ? count : "—"}</div>
                            <div style={{ width: "100%", height: `${Math.max(heightPct, 4)}%`, background: barColors[i] || T.accent, borderRadius: "3px 3px 0 0", minHeight: 4, opacity: count > 0 ? 1 : 0.2 }} />
                            <div style={{ fontSize: 13, color: T.textDim, whiteSpace: "nowrap" }}>{fmtWeek(w.week)}</div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>

              {/* Provider table */}
              <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
                {/* Header with counts + show all button */}
                <div style={{ padding: "10px 16px 8px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span style={{ fontSize: 13, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.6px", color: T.textDim }}>Provider launch status</span>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <span style={{ fontSize: 13, color: T.textDim }}>{stationProviders.length} total</span>
                    <button
                      onClick={() => setProviderDetailModal({ station: selectedStation, providers: stationProviders })}
                      style={{ background: T.accentSoft, border: `1px solid ${T.accent}`, borderRadius: T.radiusSm, color: T.accent, fontSize: 13, padding: "3px 10px", cursor: "pointer", fontFamily: T.font, fontWeight: 600 }}
                    >Show all</button>
                  </div>
                </div>
                {/* Search */}
                <div style={{ padding: "8px 12px", borderBottom: `1px solid ${T.border}` }}>
                  <input
                    style={{ ...S.filterInput, width: "100%", background: T.bg, border: `1px solid ${T.border}`, fontSize: 13, padding: "6px 10px 6px 28px", backgroundSize: "12px" }}
                    placeholder="Search providers..."
                    value={providerSearch}
                    onChange={(e) => { setProviderSearch(e.target.value); setProviderPage(1); }}
                  />
                </div>
                {loadingDetail ? (
                  <div style={{ fontSize: 13, color: T.textDim, textAlign: "center", padding: 16 }}>Loading...</div>
                ) : (() => {
                  const q = providerSearch.toLowerCase();
                  const filteredProviders = stationProviders.filter((p) => {
                    if (!q) return true;
                    const name = (p.provider_name || p.full_name || p.secid || "").toLowerCase();
                    const sp = (p.specialty || "").toLowerCase();
                    const email = (p.email || "").toLowerCase();
                    return name.includes(q) || sp.includes(q) || email.includes(q);
                  });
                  const perPg = 5;
                  const totalPgs = Math.max(1, Math.ceil(filteredProviders.length / perPg));
                  const pg = Math.min(providerPage, totalPgs);
                  const pagedProviders = filteredProviders.slice((pg - 1) * perPg, pg * perPg);
                  return (
                    <>
                      <div style={{ flex: 1, overflowY: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                          <thead>
                            <tr>{["Provider", "Specialty", "Status"].map((h) => <th key={h} style={{ ...S.th, color: T.text, fontSize: 13, padding: "7px 12px" }}>{h}</th>)}</tr>
                          </thead>
                          <tbody>
                            {pagedProviders.length === 0 ? (
                              <tr><td colSpan={3} style={{ ...S.td, textAlign: "center", color: T.textDim, fontSize: 13, padding: 20 }}>{providerSearch ? "No matches" : "No providers found"}</td></tr>
                            ) : pagedProviders.map((p, i) => {
                              const name = p.provider_name || p.full_name || p.secid || `Provider ${i + 1}`;
                              const specialty = p.specialty || "—";
                              const isActive = !!p.is_launched;
                              return (
                                <tr key={p.secid || i} style={{ transition: "background 0.1s" }} onMouseEnter={(e) => e.currentTarget.style.background = T.accentSoft} onMouseLeave={(e) => e.currentTarget.style.background = ""}>
                                  <td style={{ ...S.td, fontSize: 13, padding: "7px 12px", maxWidth: 90, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</td>
                                  <td style={{ ...S.td, fontSize: 13, padding: "7px 12px", color: T.textMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{specialty}</td>
                                  <td style={{ ...S.td, fontSize: 13, padding: "7px 12px" }}>
                                    <span style={{ fontSize: 13, padding: "1px 7px", borderRadius: 20, fontWeight: 500, background: isActive ? "rgba(10,160,110,0.1)" : "rgba(212,138,0,0.1)", color: isActive ? T.green : T.orange }}>
                                      {isActive ? "Launched" : "Remaining"}
                                    </span>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      {totalPgs > 1 && (
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", borderTop: `1px solid ${T.border}` }}>
                          <span style={{ fontSize: 13, color: T.textDim }}>{(pg - 1) * perPg + 1}–{Math.min(pg * perPg, filteredProviders.length)} of {filteredProviders.length}</span>
                          <div style={{ display: "flex", gap: 4 }}>
                            <Btn sm variant="ghost" onClick={() => setProviderPage((p) => Math.max(1, p - 1))} style={{ opacity: pg <= 1 ? 0.4 : 1 }}>Prev</Btn>
                            <Btn sm variant="ghost" onClick={() => setProviderPage((p) => Math.min(totalPgs, p + 1))} style={{ opacity: pg >= totalPgs ? 0.4 : 1 }}>Next</Btn>
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </>
          ))}
        </div>
      </div>
              </div>
            )}

            {/* Geography — license coverage by state and city. A simpler cousin
                of the offline license_geographical_mapping.html report: no
                Organization breakdown or per-provider detail table (Doctor
                Lookup and By Station already cover that drill-down). Built
                from the raw `stations` list (state comes from stations.
                street_address_state), NOT groupedStations — geography is a
                pure city/state rollup and shouldn't inherit the station
                rollup rules (base + sub-sites) built for a different reason. */}
            {momMetric === "geography" && (() => {
              const q = geoCitySearch.trim().toLowerCase();
              const stateRows = geoStateAgg
                .filter((s) => !q || s.state.toLowerCase().includes(q))
                .sort((a, b) => b.licensed - a.licensed || a.state.localeCompare(b.state));
              const cityRows = geoCityAgg
                .filter((c) => !q || c.state.toLowerCase().includes(q) || c.city.toLowerCase().includes(q))
                .sort((a, b) => a.state.localeCompare(b.state) || b.licensed - a.licensed || a.city.localeCompare(b.city));

              return (
                <div style={{ padding: "0 20px" }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 10 }}>License Coverage by State/Territory & City</div>
                  <div style={{ fontSize: 13, color: T.textMuted, lineHeight: 1.6, marginBottom: 16 }}>
                    Every station's state/territory and city, with its licensed and launched counts.
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <input
                        style={{ ...S.filterInput, width: 240 }}
                        placeholder="Search state/territory or city..."
                        value={geoCitySearch}
                        onChange={(e) => setGeoCitySearch(e.target.value)}
                      />
                      <button
                        onClick={async () => {
                          const XLSX = await ensureXLSX();
                          const wb = XLSX.utils.book_new();
                          const stateWs = XLSX.utils.json_to_sheet(stateRows.map((s) => ({
                            "State/Territory": s.state, "Cities": s.cities, "Sites": s.sites,
                            "Licensed": s.licensed,
                          })));
                          stateWs["!cols"] = [{ wch: 8 }, { wch: 10 }, { wch: 10 }, { wch: 10 }];
                          // Excel sheet (tab) names can't contain "/", so this one stays
                          // hyphenated even though the column header above uses a slash.
                          XLSX.utils.book_append_sheet(wb, stateWs, "State-Territory Coverage");
                          const cityWs = XLSX.utils.json_to_sheet(cityRows.map((c) => ({
                            "State/Territory": c.state, "City": c.city, "Sites": c.sites,
                            "Licensed": c.licensed,
                          })));
                          cityWs["!cols"] = [{ wch: 8 }, { wch: 22 }, { wch: 10 }, { wch: 10 }];
                          XLSX.utils.book_append_sheet(wb, cityWs, "City Coverage");
                          XLSX.writeFile(wb, `license_coverage_geography_${new Date().toISOString().slice(0, 10)}.xlsx`);
                        }}
                        style={{ fontSize: 13, padding: "8px 16px", borderRadius: T.radiusSm, background: T.accent, color: "#fff", border: "none", cursor: "pointer", fontFamily: T.font, fontWeight: 600, whiteSpace: "nowrap" }}
                      >
                        ⬇ Export XLSX
                      </button>
                    </div>

                    <div>
                      <div style={{ fontSize: 12, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8 }}>State/Territory Coverage</div>
                      {stateRows.length === 0 ? (
                        <div style={{ padding: 20, textAlign: "center", fontSize: 13, color: T.textDim }}>No states/territories match "{geoCitySearch}"</div>
                      ) : (
                        <div style={{ ...S.tableWrap, maxHeight: 320, overflowY: "auto" }}>
                          <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead style={{ position: "sticky", top: 0, background: T.surface, zIndex: 1 }}>
                              <tr>{["State/Territory", "Cities", "Sites", "Licensed"].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr>
                            </thead>
                            <tbody>
                              {stateRows.map((s, i) => (
                                <tr key={s.state} style={{ background: i % 2 === 0 ? T.surface : T.surface2 }}>
                                  <td style={S.td}>{s.state}</td>
                                  <td style={S.td}>{s.cities}</td>
                                  <td style={S.td}>{s.sites}</td>
                                  <td style={{ ...S.td, fontWeight: 600, color: T.accent }}>{s.licensed.toLocaleString()}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>

                    <div>
                      <div style={{ fontSize: 12, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8 }}>City Coverage</div>
                      {cityRows.length === 0 ? (
                        <div style={{ padding: 20, textAlign: "center", fontSize: 13, color: T.textDim }}>No cities match "{geoCitySearch}"</div>
                      ) : (
                        <div style={{ ...S.tableWrap, maxHeight: 320, overflowY: "auto" }}>
                          <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead style={{ position: "sticky", top: 0, background: T.surface, zIndex: 1 }}>
                              <tr>{["State/Territory", "City", "Sites", "Licensed"].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr>
                            </thead>
                            <tbody>
                              {cityRows.map((c, i) => (
                                <tr key={`${c.state}|${c.city}`} style={{ background: i % 2 === 0 ? T.surface : T.surface2 }}>
                                  <td style={S.td}>{c.state}</td>
                                  <td style={S.td}>{c.city}</td>
                                  <td style={S.td}>{c.sites}</td>
                                  <td style={{ ...S.td, fontWeight: 600, color: T.accent }}>{c.licensed.toLocaleString()}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Doctors — moved inline from the old modal popup so it sits under
                the tab bar card like every other tab here instead of
                floating over the page. Email Outreach Check split out into
                its own tab (below), so this is just the single/paste lookup now. */}
            {momMetric === "doctors" && (
              <div style={{ padding: "0 20px" }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 10 }}>Doctor Lookup</div>
                <div style={{ fontSize: 13, color: T.textMuted, lineHeight: 1.6, marginBottom: 16 }}>
                  Search a single provider by name, SECID, or email, or paste a list to check many at once.
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  {/* Single provider lookup — typeahead: suggests similar names from
                      provider_activations as you type. Picking a suggestion submits
                      it immediately (using the data already returned with that
                      suggestion, no extra round-trip) and shows its full details. */}
                  <div onClick={(e) => e.stopPropagation()} style={{ position: "relative" }}>
                    <div style={{ fontSize: 13, color: T.textMuted, marginBottom: 8 }}>Single Lookup</div>
                    <input
                      ref={providerLookupInputRef}
                      value={providerLookup}
                      onChange={(e) => { setProviderLookup(e.target.value); setProviderLookupSuggestOpen(true); setProviderLookupSelected(null); updateProviderLookupDropdownPos(); }}
                      onFocus={() => { if (providerLookupResults !== null) { setProviderLookupSuggestOpen(true); updateProviderLookupDropdownPos(); } }}
                      onBlur={() => { setTimeout(() => setProviderLookupSuggestOpen(false), 150); }}
                      onKeyDown={(e) => { if (e.key === "Escape") setProviderLookupSuggestOpen(false); }}
                      placeholder="Start typing a name, SECID, or email…"
                      style={{ ...S.filterInput, width: "100%", boxSizing: "border-box", backgroundImage: "none", padding: "6px 10px", fontSize: 13 }}
                    />
                    {providerLookupSuggestOpen && providerLookup.trim() && providerLookupResults !== null && providerLookupDropdownPos && createPortal(
                      <div style={{
                        position: "fixed", top: providerLookupDropdownPos.top, left: providerLookupDropdownPos.left, width: providerLookupDropdownPos.width,
                        zIndex: 1000,
                        background: T.surface, border: `1px solid ${T.border}`, borderRadius: T.radiusSm,
                        boxShadow: "0 6px 16px rgba(0,0,0,0.15)", maxHeight: 260, overflowY: "auto",
                      }}>
                        {providerLookupLoading ? (
                          <div style={{ padding: "10px 12px", fontSize: 13, color: T.textDim }}>Searching…</div>
                        ) : providerLookupResults.length === 0 ? (
                          <div style={{ padding: "10px 12px", fontSize: 13, color: T.textDim }}>No matches for "{providerLookup}"</div>
                        ) : (
                          providerLookupResults.map((r, i) => {
                            const subtext = [r.station_id, r.specialty].filter(Boolean).join(" · ");
                            return (
                              <div key={i}
                                onMouseDown={(e) => e.preventDefault()} // keep input focus so onBlur doesn't fire before this click registers
                                onClick={() => {
                                  setProviderLookup(r.provider_name || r.secid || "");
                                  setProviderLookupSuggestOpen(false);
                                  setProviderLookupSelected(r); // submit immediately — no re-fetch needed
                                  fetchProviderLookupActivity(r);
                                }}
                                style={{
                                  padding: "8px 12px", cursor: "pointer",
                                  borderBottom: i < providerLookupResults.length - 1 ? `1px solid ${T.border}` : "none",
                                  background: T.surface,
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = T.accentSoft; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = T.surface; }}
                              >
                                <div style={{ fontSize: 13, color: T.text }}>{r.provider_name || r.secid || "—"}</div>
                                {subtext && <div style={{ fontSize: 11, color: T.textMuted, marginTop: 1 }}>{subtext}</div>}
                              </div>
                            );
                          })
                        )}
                      </div>,
                      document.body
                    )}

                    {providerLookupSelected && (() => {
                      const r = providerLookupSelected;
                      const now = new Date();
                      const currentYYYYMM = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}`;
                      const isLeavingThisMonth = r.is_decommissioned && r.decommission_period === currentYYYYMM;
                      const statusLabel = !r.is_decommissioned ? "Active" : isLeavingThisMonth ? "Leaving this month" : "Decommissioned";
                      const statusColor = !r.is_decommissioned ? T.green : isLeavingThisMonth ? T.orange : T.textMuted;
                      const statusBg = !r.is_decommissioned ? "rgba(34,197,94,0.12)" : isLeavingThisMonth ? "rgba(212,138,0,0.12)" : "rgba(120,120,140,0.1)";
                      // last_note_date/cumulative_recordings/monthly_recordings arrive a
                      // beat later via fetchProviderLookupActivity — undefined (not yet
                      // loaded) is shown as "…", 0 is shown as an actual 0.
                      const activityLoaded = r.cumulative_recordings !== undefined;
                      return (
                        <div style={{ marginTop: 8, border: `1px solid ${T.border}`, borderRadius: T.radiusSm, padding: "10px 12px", display: "flex", flexWrap: "wrap", gap: "4px 16px", alignItems: "center" }}>
                          <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{r.provider_name || r.secid || "—"}</span>
                          <span style={{ fontSize: 12, color: T.textMuted }}>{r.station_id || "—"}</span>
                          <span style={{ fontSize: 12, color: T.textMuted }}>{r.visn ? `VISN ${r.visn}` : "—"}</span>
                          <span style={{ fontSize: 12, color: T.textMuted }}>{r.specialty || "—"}</span>
                          <span style={{ fontSize: 12, color: T.textMuted }}>
                            {r.date_activated ? new Date(r.date_activated).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}
                          </span>
                          <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, fontWeight: 600, background: statusBg, color: statusColor }}>
                            {statusLabel}
                          </span>
                          <span style={{ width: 1, height: 14, background: T.border }} />
                          <span style={{ fontSize: 12, color: T.textMuted }}>
                            Last note: {!activityLoaded ? "…" : r.last_note_date ? new Date(r.last_note_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "Never"}
                          </span>
                          <span style={{ fontSize: 12, color: T.textMuted }}>
                            Monthly recordings: {!activityLoaded ? "…" : (r.monthly_recordings ?? 0)}
                          </span>
                          <span style={{ fontSize: 12, color: T.textMuted }}>
                            Cumulative recordings: {!activityLoaded ? "…" : (r.cumulative_recordings ?? 0)}
                          </span>
                        </div>
                      );
                    })()}
                  </div>

                  <div style={{ height: 1, background: T.border }} />

                  {/* Batch lookup — paste-from-spreadsheet, moved in from the old
                      standalone "Other Tools" panel. */}
                  <div onClick={(e) => e.stopPropagation()}>
                    <div style={{ fontSize: 13, color: T.textMuted, marginBottom: 4 }}>Batch Lookup</div>
                    <div style={{ fontSize: 12, color: T.textDim, marginBottom: 10 }}>
                      Note: matches on name/email similarity (handles "Last, First" word order and partial emails), not exact spelling — a very different spelling or a typo may not be found.
                    </div>
                    <div style={{ fontSize: 13, color: T.textMuted, marginBottom: 12 }}>
                      Paste a list of VA emails or names straight from a spreadsheet to see who's launched, their specialty, account created date, last note, and cumulative recordings.
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                      <button onClick={() => setBulkLookupPasteOpen((o) => !o)} disabled={bulkLookupLoading}
                        style={{ fontSize: 13, padding: "6px 14px", borderRadius: T.radiusSm, background: bulkLookupPasteOpen ? T.accentSoft : "transparent", color: bulkLookupLoading ? T.textDim : T.accent, border: `1px solid ${bulkLookupLoading ? T.border : T.accent}`, cursor: bulkLookupLoading ? "default" : "pointer", fontFamily: T.font, fontWeight: 600 }}>
                        {bulkLookupPasteOpen ? "✕ Close paste box" : "📋 Paste from spreadsheet"}
                      </button>
                      {bulkLookupResults && !bulkLookupLoading && (() => {
                        const matched = new Set(bulkLookupResults.rows.filter(r => r["Found in DB"] === "Yes").map(r => r["Input"])).size;
                        const launched = new Set(bulkLookupResults.rows.filter(r => r["Launched"] === "Launched").map(r => r["Input"])).size;
                        return (
                          <>
                            <span style={{ fontSize: 13, color: T.textMuted }}>{matched} / {bulkLookupResults.total} matched</span>
                            <span style={{ fontSize: 13, color: T.accent, fontWeight: 600 }}>· {launched} launched</span>
                            <button onClick={() => setBulkLookupResults(null)}
                              title="Close results"
                              style={{ fontSize: 13, padding: "6px 10px", borderRadius: T.radiusSm, background: "transparent", color: T.textMuted, border: `1px solid ${T.border}`, cursor: "pointer", fontFamily: T.font, fontWeight: 600 }}>
                              ✕ Close results
                            </button>
                          </>
                        );
                      })()}
                    </div>

                    {bulkLookupPasteOpen && (
                      <div style={{ marginTop: 10 }}>
                        <textarea
                          value={bulkLookupPasteText}
                          onChange={(e) => setBulkLookupPasteText(e.target.value)}
                          placeholder={"Paste one column of emails or names here — copy straight from Excel/Sheets and paste (Ctrl/Cmd+V)"}
                          rows={5}
                          style={{ width: "100%", boxSizing: "border-box", fontSize: 13, fontFamily: T.mono || T.font, padding: "8px 10px", borderRadius: T.radiusSm, border: `1px solid ${T.border}`, background: T.surface2, color: T.text, resize: "vertical" }}
                        />
                        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8 }}>
                          <button
                            onClick={async () => { await runBulkLookupPaste(bulkLookupPasteText); }}
                            disabled={bulkLookupLoading || !bulkLookupPasteText.trim()}
                            style={{ fontSize: 13, padding: "6px 14px", borderRadius: T.radiusSm, background: (bulkLookupLoading || !bulkLookupPasteText.trim()) ? T.surface2 : T.accent, color: (bulkLookupLoading || !bulkLookupPasteText.trim()) ? T.textDim : "#fff", border: "none", cursor: (bulkLookupLoading || !bulkLookupPasteText.trim()) ? "default" : "pointer", fontFamily: T.font, fontWeight: 600 }}>
                            {bulkLookupLoading ? "Looking up…" : "Look up"}
                          </button>
                          {bulkLookupPasteText.trim() && (
                            <button onClick={() => setBulkLookupPasteText("")} disabled={bulkLookupLoading}
                              style={{ fontSize: 13, padding: "6px 14px", borderRadius: T.radiusSm, background: "transparent", color: T.textMuted, border: `1px solid ${T.border}`, cursor: bulkLookupLoading ? "default" : "pointer", fontFamily: T.font, fontWeight: 600 }}>
                              Clear
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {bulkLookupResults && !bulkLookupLoading && (() => {
                      const matchedRows = bulkLookupResults.rows.filter(r => r["Found in DB"] === "Yes");
                      return (
                        <>
                          {matchedRows.length > 0 && (
                            <div style={{ marginTop: 12, border: `1px solid ${T.border}`, borderRadius: T.radiusSm, overflow: "auto", maxHeight: 360 }}>
                              <table style={{ width: "100%", minWidth: 900, borderCollapse: "collapse" }}>
                                <thead>
                                  <tr>
                                    {["Provider", "Station", "VISN", "Specialty", "Launched", "Date Activated", "Last Note", "Monthly Recordings", "Cumulative Recordings"].map((h, hi) => (
                                      <th key={h} style={{
                                        ...S.th,
                                        position: "sticky",
                                        top: 0,
                                        ...(hi === 0 ? { left: 0, zIndex: 3 } : { zIndex: 2 }),
                                        background: T.surface,
                                      }}>{h}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {matchedRows.map((r, i) => {
                                    const rowBg = i % 2 === 0 ? T.surface : T.surface2;
                                    return (
                                      <tr key={i} style={{ background: rowBg }}>
                                        <td style={{ ...S.td, position: "sticky", left: 0, zIndex: 1, background: rowBg }}>{r["Provider Name"] || "—"}</td>
                                        <td style={{ ...S.td, color: T.textMuted }}>{r["Station"] || "—"}</td>
                                        <td style={{ ...S.td, color: T.textMuted }}>{r["VISN"] || "—"}</td>
                                        <td style={{ ...S.td, color: T.textMuted }}>{r["Specialty"] || "—"}</td>
                                        <td style={S.td}>
                                          <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, fontWeight: 600, background: r["Launched"] === "Launched" ? "rgba(34,197,94,0.12)" : "rgba(120,120,140,0.1)", color: r["Launched"] === "Launched" ? T.green : T.textMuted }}>
                                            {r["Launched"] || "—"}
                                          </span>
                                        </td>
                                        <td style={{ ...S.td, color: T.textMuted, fontSize: 13 }}>{r["Date Activated"] || "—"}</td>
                                        <td style={{ ...S.td, color: T.textMuted, fontSize: 13 }}>{r["Last Note"] || "—"}</td>
                                        <td style={{ ...S.td, color: T.textMuted, fontSize: 13 }}>{r["Monthly Recordings"]}</td>
                                        <td style={{ ...S.td, color: T.textMuted, fontSize: 13 }}>{r["Cumulative Recordings"]}</td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            )}

            {momMetric === "all" && launchUtilView === "breakdown" && (
              <div style={{ padding: "0 20px" }} onClick={(e) => e.stopPropagation()}>
                {/* At-Risk & Power User Breakdown — moved here from the User
                    Flagging & Outreach tab and given its own toggle above,
                    alongside Launch Report and Utilization Report. */}
                <div style={{
                  display: "flex", gap: 10, alignItems: "flex-start",
                  background: T.accentSoft, border: `1px solid ${T.accent}`, borderRadius: T.radiusSm,
                  padding: "10px 14px", marginBottom: 16,
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 2 }}>
                    <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                    <line x1="12" y1="9" x2="12" y2="13" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                  <div style={{ fontSize: 13, color: T.text, lineHeight: 1.6 }}>
                    See which stations, VISNs, RISEs, or specialties have the most at-risk providers (no recent recordings) and power users (top utilization). Click a count to see a rough breakdown of that group, with a link to download the full list.
                  </div>
                </div>
                <div style={{ display: "flex", gap: 0, borderBottom: `1px solid ${T.border}` }}>
                  {ENGAGEMENT_GROUP_TABS.map(({ launch, label }) => (
                    <button key={launch} onClick={() => { setActivityBreakdownInner(launch); setActivityBreakdownSearch(""); setActivityBreakdownPage(1); }} style={{ ...S.innertab(activityBreakdownInner === launch) }}>{label}</button>
                  ))}
                </div>

                <div style={{ padding: "16px 0 20px" }}>
                  {(() => {
                    const groupBy = ACTIVITY_BREAKDOWN_GROUP_BY[activityBreakdownInner];
                    const groupLabel = ACTIVITY_BREAKDOWN_LABELS[groupBy];
                    const rows = activityBreakdownData[groupBy] || [];

                    if (activityBreakdownLoading && rows.length === 0) {
                      return <div style={{ padding: 24, textAlign: "center", fontSize: 13, color: T.textDim }}>Loading...</div>;
                    }

                    const displayKey = (key) => (groupBy === "visn" ? `VISN ${key}` : groupBy === "new_visn" ? formatRiseLabel(key) : key);
                    const q = activityBreakdownSearch.toLowerCase();
                    // For By Station, also match the sub-site name (e.g. "Franklin
                    // VA Clinic"), not just the station id — same as the Utilization
                    // tab's station search.
                    const filteredRows = rows.filter((r) => {
                      if (!q) return true;
                      if (displayKey(r.group_key).toLowerCase().includes(q)) return true;
                      if (groupBy === "station_id") {
                        const siteName = siteNameByStationId[r.group_key] || "";
                        if (siteName.toLowerCase().includes(q)) return true;
                      }
                      return false;
                    });
                    const sorted = [...filteredRows].sort((a, b) => {
                      let diff = 0;
                      if (activityBreakdownSort.key === "active_licenses") diff = b.active_licenses - a.active_licenses;
                      else if (activityBreakdownSort.key === "at_risk_count") diff = b.at_risk_count - a.at_risk_count;
                      else if (activityBreakdownSort.key === "power_user_count") diff = b.power_user_count - a.power_user_count;
                      else if (activityBreakdownSort.key === "avg_dau") diff = b.avg_dau - a.avg_dau;
                      else diff = displayKey(a.group_key).localeCompare(displayKey(b.group_key), undefined, { numeric: true });
                      if (activityBreakdownSort.dir === "asc") diff = -diff;
                      return diff;
                    });
                    const perPage = 10;
                    const totalPages = Math.max(1, Math.ceil(sorted.length / perPage));
                    const page = Math.min(activityBreakdownPage, totalPages);
                    const paged = sorted.slice((page - 1) * perPage, page * perPage);
                    const toggleSort = (key) => {
                      setActivityBreakdownSort((s) => ({ key, dir: s.key === key && s.dir === "desc" ? "asc" : "desc" }));
                      setActivityBreakdownPage(1);
                    };

                    return (
                      <>
                        <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
                          <input
                            style={{ ...S.filterInput, flex: 1, backgroundImage: "none", padding: "6px 10px", fontSize: 13 }}
                            placeholder={groupBy === "station_id" ? "Search stations or site name..." : `Search ${groupLabel.toLowerCase()}s...`}
                            value={activityBreakdownSearch}
                            onChange={(e) => { setActivityBreakdownSearch(e.target.value); setActivityBreakdownPage(1); }}
                          />
                          <span style={{ fontSize: 12, color: T.textDim, whiteSpace: "nowrap" }}>{sorted.length} {groupLabel.toLowerCase()}s</span>
                        </div>
                        <div style={{ border: `1px solid ${T.border}`, borderRadius: T.radiusSm, overflow: "hidden" }}>
                          <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead><tr>
                              {[[groupLabel, "group"], ["Active Licenses", "active_licenses"], ["At Risk", "at_risk_count"], ["Power Users", "power_user_count"], ["Avg DAU", "avg_dau"]].map(([label, key]) => {
                                const active = activityBreakdownSort.key === key;
                                return (
                                  <th key={key} onClick={() => toggleSort(key)} style={{ ...S.th, color: active ? T.accent : T.text, cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}>
                                    {label} <span style={{ fontSize: 9, opacity: active ? 1 : 0.3 }}>{active && activityBreakdownSort.dir === "asc" ? "▲" : "▼"}</span>
                                  </th>
                                );
                              })}
                            </tr></thead>
                            <tbody>
                              {paged.length === 0 ? (
                                <tr><td colSpan={5} style={{ ...S.td, textAlign: "center", color: T.textDim, padding: 24 }}>No data found</td></tr>
                              ) : paged.map((r) => {
                                const isRowDetailOpen = !!(rowDetailSelection && rowDetailSelection.groupBy === groupBy && rowDetailSelection.groupKey === r.group_key);
                                const rowCacheKey = isRowDetailOpen ? `${groupBy}|${r.group_key}|${rowDetailSelection.type}` : null;
                                return (
                                  <Fragment key={r.group_key}>
                                    <tr style={{ background: T.surface }}>
                                      <td style={{ ...S.td, ...(groupBy === "station_id" ? S.mono : {}) }}>
                                        {displayKey(r.group_key)}
                                        {groupBy === "station_id" && siteNameByStationId[r.group_key] && (
                                          <div style={{ fontSize: 11, color: T.textDim, fontFamily: T.font, fontWeight: 400, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{siteNameByStationId[r.group_key]}</div>
                                        )}
                                      </td>
                                      <td style={S.td}>{r.active_licenses}</td>
                                      <td
                                        onClick={() => r.at_risk_count > 0 && toggleRowDetail(groupBy, r.group_key, "at_risk")}
                                        style={{ ...S.td, color: r.at_risk_count > 0 ? T.red : T.textMuted, cursor: r.at_risk_count > 0 ? "pointer" : "default", fontWeight: isRowDetailOpen && rowDetailSelection.type === "at_risk" ? 700 : 400, textDecoration: r.at_risk_count > 0 ? "underline dotted" : "none" }}
                                      >
                                        {r.at_risk_count}
                                      </td>
                                      <td
                                        onClick={() => r.power_user_count > 0 && toggleRowDetail(groupBy, r.group_key, "power_users")}
                                        style={{ ...S.td, color: r.power_user_count > 0 ? T.orange : T.textMuted, cursor: r.power_user_count > 0 ? "pointer" : "default", fontWeight: isRowDetailOpen && rowDetailSelection.type === "power_users" ? 700 : 400, textDecoration: r.power_user_count > 0 ? "underline dotted" : "none" }}
                                      >
                                        {r.power_user_count}
                                      </td>
                                      <td style={S.td}>{Number(r.avg_dau).toLocaleString(undefined, { maximumFractionDigits: 1 })}</td>
                                    </tr>
                                    {isRowDetailOpen && (
                                      <tr>
                                        <td colSpan={5} style={{ padding: "0 0 10px", border: "none" }}>
                                          <div style={{ border: `1px solid ${T.border}`, borderRadius: 8, overflow: "hidden" }}>
                                            {rowDetailLoading && !rowDetailData[rowCacheKey] ? (
                                              <div style={{ padding: 16, textAlign: "center", fontSize: 13, color: T.textDim }}>Loading...</div>
                                            ) : rowDetailError ? (
                                              <div style={{ padding: 16, textAlign: "center", fontSize: 13, color: T.red }}>{rowDetailError}</div>
                                            ) : (() => {
                                              const detailRows = rowDetailData[rowCacheKey] || [];
                                              if (detailRows.length === 0) {
                                                return <div style={{ padding: 16, textAlign: "center", fontSize: 13, color: T.textDim }}>No providers found</div>;
                                              }
                                              const { isAtRisk, counted, total } = computeActivityBuckets(rowDetailSelection.type, detailRows);
                                              const barColor = isAtRisk ? T.red : T.orange;
                                              return (
                                                <>
                                                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "8px 12px", borderBottom: `1px solid ${T.border}` }}>
                                                    <span style={{ fontSize: 11, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.6 }}>
                                                      {displayKey(r.group_key)} — {total.toLocaleString()} {isAtRisk ? "at-risk" : "power"} provider{total === 1 ? "" : "s"} — rough breakdown
                                                    </span>
                                                    <Btn sm variant="ghost" onClick={() => downloadRowDetailXlsx(groupBy, r.group_key, rowDetailSelection.type)}>
                                                      Download full list (XLSX)
                                                    </Btn>
                                                  </div>
                                                  {counted.map((b, i) => (
                                                    <div key={b.label} style={{ padding: "8px 12px", borderTop: i > 0 ? `1px solid ${T.border}` : "none" }}>
                                                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
                                                        <span style={{ color: T.text }}>{b.label}</span>
                                                        <span style={{ color: T.textDim, fontWeight: 600 }}>{b.count.toLocaleString()}</span>
                                                      </div>
                                                      <div style={{ height: 5, borderRadius: 3, background: T.surface2, overflow: "hidden" }}>
                                                        <div style={{ height: "100%", width: `${total > 0 ? (b.count / total) * 100 : 0}%`, background: barColor, borderRadius: 3 }} />
                                                      </div>
                                                    </div>
                                                  ))}
                                                </>
                                              );
                                            })()}
                                          </div>
                                        </td>
                                      </tr>
                                    )}
                                  </Fragment>
                                );
                              })}
                            </tbody>
                          </table>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 16px", borderTop: `1px solid ${T.border}` }}>
                            <span style={{ fontSize: 13, color: T.textDim }}>{sorted.length === 0 ? "0" : `${(page - 1) * perPage + 1}–${Math.min(page * perPage, sorted.length)}`} of {sorted.length}</span>
                            <div style={{ display: "flex", gap: 4 }}>
                              <Btn sm variant="ghost" onClick={() => setActivityBreakdownPage((p) => Math.max(1, p - 1))} style={{ opacity: page <= 1 ? 0.4 : 1 }}>Prev</Btn>
                              <Btn sm variant="ghost" onClick={() => setActivityBreakdownPage((p) => Math.min(totalPages, p + 1))} style={{ opacity: page >= totalPages ? 0.4 : 1 }}>Next</Btn>
                            </div>
                          </div>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            )}

            {/* Email Outreach Check — split out from the Doctors tab into its
                own top-level tab: uploading a list to check outreach status is
                a distinct workflow from the single/paste lookup above. */}
            {momMetric === "email-outreach" && (
              <div style={{ padding: "0 20px" }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 10 }}>Email Outreach Check</div>
                <div style={{ fontSize: 13, color: T.textMuted, lineHeight: 1.6, marginBottom: 16 }}>
                  Upload a VA email list (.xlsx) to see who's launched, their specialty, account created date, last note, and cumulative recordings.
                </div>
                <div>
                  <input ref={batchFileRef} type="file" accept=".xlsx,.xls" style={{ display: "none" }}
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) runBatchLookup(f); e.target.value = ""; }}
                  />
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    <button onClick={() => batchFileRef.current?.click()} disabled={batchLoading}
                      style={{ fontSize: 13, padding: "6px 14px", borderRadius: T.radiusSm, background: batchLoading ? T.surface2 : T.accentSoft, color: batchLoading ? T.textDim : T.accent, border: `1px solid ${batchLoading ? T.border : T.accent}`, cursor: batchLoading ? "default" : "pointer", fontFamily: T.font, fontWeight: 600 }}>
                      {batchLoading ? "Looking up…" : "↑ Upload"}
                    </button>
                    {batchResults && !batchLoading && (() => {
                      const matched = new Set(batchResults.rows.filter(r => r["Found in DB"] === "Yes").map(r => r["Input"])).size;
                      const launched = new Set(batchResults.rows.filter(r => r["Launch Detail"] === "Launched").map(r => r["Input"])).size;
                      return (
                        <>
                          <span style={{ fontSize: 13, color: T.textMuted }}>{matched} / {batchResults.total} matched</span>
                          <span style={{ fontSize: 13, color: T.accent, fontWeight: 600 }}>· {launched} launched</span>
                          <button onClick={downloadBatchXlsx}
                            style={{ fontSize: 13, padding: "6px 14px", borderRadius: T.radiusSm, background: T.accent, color: "#fff", border: "none", cursor: "pointer", fontFamily: T.font, fontWeight: 600 }}>
                            ↓ Download
                          </button>
                        </>
                      );
                    })()}
                  </div>
                  {batchResults && !batchLoading && (() => {
                    const notFound = batchResults.rows.filter(r => r["Found in DB"] === "No");
                    if (notFound.length === 0) return <div style={{ fontSize: 13, color: "#22c55e", marginTop: 8 }}>All providers matched.</div>;
                    return (
                      <div style={{ marginTop: 10, border: `1px solid ${T.border}`, borderRadius: T.radiusSm, overflow: "hidden" }}>
                        <div style={{ padding: "7px 12px", fontSize: 13, color: T.textDim, borderBottom: `1px solid ${T.border}` }}>
                          {notFound.length} unmatched{notFound.length > 4 ? ` · showing 4` : ""}
                        </div>
                        {notFound.slice(0, 4).map((r, i, arr) => (
                          <div key={i} style={{ padding: "6px 12px", fontSize: 13, fontFamily: "monospace", color: T.textMuted, borderBottom: i < arr.length - 1 ? `1px solid ${T.border}` : "none", background: i % 2 === 0 ? T.surface : T.surface2 }}>
                            {r["Input"]}
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}

            {/* User Leaderboard — top 10 recording providers, either over a
                trailing 30-day window or cumulative (all-time), optionally
                narrowed to one specialty/VISN/station. Re-fetched via
                fetchRecordingLeaderboard whenever any condition changes
                (backend caps at LIMIT 10, sorted by total_recordings DESC),
                then just sortable client-side by column — no search box or
                pagination since there are only 10 rows to begin with. Scoped
                to currently-licensed providers only (PROVIDER_CURRENT_PERIOD_WHERE
                on the backend), same reasoning as the rest of this session's
                is_current_period work — a since-decommissioned doctor's old
                recordings shouldn't surface them on a leaderboard about CURRENT
                top performers. */}
            {momMetric === "leaderboard" && (
              <div style={{ padding: "0 20px" }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 10 }}>User Leaderboard</div>
                <div style={{ fontSize: 13, color: T.textMuted, lineHeight: 1.6, marginBottom: 16 }}>
                  Top 10 recording providers{leaderboardCumulative ? ", cumulative (all-time)" : " over a trailing 30-day window"}, ranked by total recordings. Only currently-licensed providers are shown.
                </div>

                {/* ── Filter bar ── */}
                <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", marginBottom: 16 }}>
                  <div style={{ display: "flex", borderRadius: T.radiusSm, border: `1px solid ${T.border}`, overflow: "hidden" }}>
                    {[{ key: false, label: "Last 30 Days" }, { key: true, label: "Cumulative" }].map(({ key, label }) => (
                      <button
                        key={String(key)}
                        onClick={() => setLeaderboardCumulative(key)}
                        style={{
                          fontSize: 12, padding: "6px 12px", border: "none", cursor: "pointer", fontFamily: T.font, fontWeight: 600,
                          background: leaderboardCumulative === key ? T.accent : T.surface,
                          color: leaderboardCumulative === key ? "#fff" : T.textMuted,
                        }}
                      >{label}</button>
                    ))}
                  </div>
                  <select
                    value={leaderboardSpecialtyFilter}
                    onChange={(e) => setLeaderboardSpecialtyFilter(e.target.value)}
                    style={{ ...S.filterSelect, width: 200 }}
                  >
                    <option value="">All specialties</option>
                    {specialties.map((sp) => <option key={sp} value={sp}>{sp}</option>)}
                  </select>
                  <select
                    value={leaderboardVisnFilter}
                    onChange={(e) => setLeaderboardVisnFilter(e.target.value)}
                    style={{ ...S.filterSelect, width: 140 }}
                  >
                    <option value="">All VISNs</option>
                    {visns.map((v) => <option key={v} value={v}>VISN {v}</option>)}
                  </select>
                  <select
                    value={leaderboardStationFilter}
                    onChange={(e) => setLeaderboardStationFilter(e.target.value)}
                    style={{ ...S.filterSelect, width: 220 }}
                  >
                    <option value="">All stations/sites</option>
                    {leaderboardStationOptions.map(([id, name]) => (
                      <option key={id} value={id}>{id}{name ? ` — ${name}` : ""}</option>
                    ))}
                  </select>
                  {(leaderboardCumulative || leaderboardSpecialtyFilter || leaderboardVisnFilter || leaderboardStationFilter) && (
                    <button
                      onClick={() => {
                        setLeaderboardCumulative(false);
                        setLeaderboardSpecialtyFilter("");
                        setLeaderboardVisnFilter("");
                        setLeaderboardStationFilter("");
                      }}
                      style={{ background: "none", border: "none", color: T.textMuted, cursor: "pointer", fontSize: 12, fontFamily: T.font, whiteSpace: "nowrap" }}
                    >Clear filters</button>
                  )}
                </div>

                {leaderboardLoading ? (
                  <div style={{ padding: 32, textAlign: "center", fontSize: 13, color: T.textDim }}>Loading leaderboard...</div>
                ) : leaderboardData.length === 0 ? (
                  <div style={{ padding: 32, textAlign: "center", fontSize: 13, color: T.textDim }}>
                    No recordings {leaderboardCumulative ? "" : "in the last 30 days "}match the selected filters
                  </div>
                ) : (() => {
                  const sorted = [...leaderboardData].sort((a, b) => {
                    let diff = 0;
                    if (leaderboardSort.key === "recordings") diff = b.total_recordings - a.total_recordings;
                    else if (leaderboardSort.key === "name") diff = (a.provider_name || "").localeCompare(b.provider_name || "");
                    else if (leaderboardSort.key === "station") diff = (a.station_id || "").localeCompare(b.station_id || "");
                    else if (leaderboardSort.key === "specialty") diff = (a.specialty || "").localeCompare(b.specialty || "");
                    if (leaderboardSort.dir === "asc") diff = -diff;
                    return diff !== 0 ? diff : a.rank - b.rank;
                  });
                  const toggleSort = (key) => {
                    setLeaderboardSort((s) => ({ key, dir: s.key === key && s.dir === "desc" ? "asc" : "desc" }));
                  };
                  const SortTh = ({ label, sortKey }) => {
                    const active = leaderboardSort.key === sortKey;
                    return (
                      <th onClick={() => toggleSort(sortKey)} style={{ ...S.th, color: active ? T.accent : T.text, cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}>
                        {label} <span style={{ fontSize: 9, opacity: active ? 1 : 0.3 }}>{active && leaderboardSort.dir === "asc" ? "▲" : "▼"}</span>
                      </th>
                    );
                  };
                  return (
                    <div>
                      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
                        <button
                          onClick={downloadLeaderboardXlsx}
                          style={{ fontSize: 13, padding: "6px 14px", borderRadius: T.radiusSm, background: T.accent, color: "#fff", border: "none", cursor: "pointer", fontFamily: T.font, fontWeight: 600 }}
                        >
                          ⬇ Export XLSX
                        </button>
                      </div>
                      <div style={{ ...S.tableWrap }}>
                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                          <thead>
                            <tr>
                              <th style={{ ...S.th, width: 50 }}>Rank</th>
                              <SortTh label="Name" sortKey="name" />
                              <th style={S.th}>Email</th>
                              <SortTh label="Station" sortKey="station" />
                              <SortTh label="Specialty" sortKey="specialty" />
                              <SortTh label={leaderboardCumulative ? "Recordings (Cumulative)" : "Recordings (30d)"} sortKey="recordings" />
                            </tr>
                          </thead>
                          <tbody>
                            {sorted.map((r, i) => (
                              <tr key={r.doctor_user_id} style={{ background: i % 2 === 0 ? T.surface : T.surface2 }}>
                                <td style={{ ...S.td, fontWeight: 700, color: r.rank <= 3 ? T.orange : T.textMuted }}>
                                  {r.rank <= 3 ? ["🥇", "🥈", "🥉"][r.rank - 1] : `#${r.rank}`}
                                </td>
                                <td style={S.td}>{r.provider_name || "—"}</td>
                                <td style={{ ...S.td, color: T.textMuted }}>{r.email || "—"}</td>
                                <td style={{ ...S.td, ...S.mono }}>{r.station_id || "—"}</td>
                                <td style={{ ...S.td, color: T.textMuted }}>{r.specialty || "—"}</td>
                                <td style={{ ...S.td, fontWeight: 600, color: T.accent }}>{r.total_recordings.toLocaleString()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

          </div>
        )}


      </div>





      {/* ── Quick links ── */}
      {navigate && (
        <div style={{ marginTop: 8, padding: "0 16px" }}>
          <div style={{ fontSize: 13, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.2, color: T.textDim, marginBottom: 10 }}>Jump to</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
            {[
              {
                key: "support",
                label: "Help Desk",
                desc: "View support tickets, trends, and provider requests",
                icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>,
                accent: T.accent, accentSoft: T.accentSoft,
              },
              {
                key: "ratings",
                label: "Star Ratings",
                desc: "Explore provider satisfaction scores and feedback",
                icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>,
                accent: "#e8920a", accentSoft: "rgba(232,146,10,0.08)",
              },
              {
                key: "reports",
                label: "Reports",
                desc: "Download and review detailed utilization reports",
                icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
                accent: T.accent, accentSoft: T.accentSoft,
              },
            ].map(({ key, label, desc, icon, accent, accentSoft }) => (
              <div
                key={key}
                onClick={() => navigate(key)}
                style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: T.radius, padding: "16px 18px", cursor: "pointer", transition: "all 0.15s", display: "flex", flexDirection: "column", gap: 8 }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = accent; e.currentTarget.style.background = accentSoft; e.currentTarget.style.boxShadow = `0 0 0 3px ${accentSoft}`; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.background = T.surface; e.currentTarget.style.boxShadow = "none"; }}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span style={{ color: accent }}>{icon}</span>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.textDim} strokeWidth="2"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 3 }}>{label}</div>
                  <div style={{ fontSize: 13, color: T.textMuted, lineHeight: 1.5 }}>{desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── More coming ── */}
      <div style={{ textAlign: "center", padding: "24px 0 8px", display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{ flex: 1, height: 1, background: `linear-gradient(90deg, transparent, ${T.border})` }} />
        <span style={{ fontSize: 13, color: T.textDim, letterSpacing: 1, fontWeight: 500 }}>more coming soon</span>
        <div style={{ flex: 1, height: 1, background: `linear-gradient(90deg, ${T.border}, transparent)` }} />
      </div>

      {/* ── All providers modal ── */}
      {providerDetailModal && (() => {
        const { station, providers } = providerDetailModal;
        const fmtDate = (d) => {
          if (!d) return "—";
          return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
        };
        const activated = providers.filter((p) => !!p.is_launched).length;
        return (
          <div onClick={() => setProviderDetailModal(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div onClick={(e) => e.stopPropagation()} style={{ background: T.surface, borderRadius: T.radius, boxShadow: "0 8px 40px rgba(0,0,0,0.18)", width: 680, maxHeight: "80vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
              {/* Header */}
              <div style={{ padding: "16px 20px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 600 }}>Station {station.station_id} — All Providers</div>
                  <div style={{ fontSize: 13, color: T.textMuted, marginTop: 2 }}>
                    <span style={{ color: T.green, fontWeight: 500 }}>{activated} launched</span>
                    <span style={{ marginLeft: 8 }}>{providers.length} total</span>
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <button onClick={() => {
                    const rows = providers.map((p) => ({
                      "Provider": p.provider_name || p.full_name || p.secid || "",
                      "Email": p.email || "",
                      "Specialty": p.specialty || "",
                      "Site ID": p.station_id || "",
                      "VISN": p.visn ? `VISN ${p.visn}` : "",
                      "Activated": p.date_activated ? fmtDate(p.date_activated) : "",
                      "Status": p.is_launched ? "Launched" : "Remaining",
                    }));
                    ensureXLSX().then((XLSX) => {
                      const ws = XLSX.utils.json_to_sheet(rows);
                      ws["!cols"] = [{ wch: 24 }, { wch: 28 }, { wch: 16 }, { wch: 10 }, { wch: 8 }, { wch: 14 }, { wch: 10 }];
                      const wb = XLSX.utils.book_new();
                      XLSX.utils.book_append_sheet(wb, ws, "Providers");
                      XLSX.writeFile(wb, `providers_station_${station.station_id}.xlsx`);
                    });
                  }} style={{ background: T.accent, border: "none", borderRadius: T.radiusSm, color: "#fff", fontSize: 13, padding: "5px 12px", cursor: "pointer", fontFamily: T.font, fontWeight: 500 }}>⬇ Export XLSX</button>
                  <button onClick={() => setProviderDetailModal(null)} style={{ background: "none", border: "none", cursor: "pointer", color: T.textDim, fontSize: 20, lineHeight: 1, padding: "0 4px" }}>×</button>
                </div>
              </div>
              {/* Table */}
              <div style={{ overflowY: "auto", flex: 1 }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead style={{ position: "sticky", top: 0, background: T.surface, zIndex: 1 }}>
                    <tr>{["Provider", "Email", "Specialty", "Site ID", "VISN", "Launched", "Status"].map((h) => (
                      <th key={h} style={{ ...S.th, fontSize: 13, padding: "8px 14px", whiteSpace: "nowrap" }}>{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody>
                    {providers.length === 0 ? (
                      <tr><td colSpan={7} style={{ ...S.td, textAlign: "center", color: T.textDim, padding: 24 }}>No providers</td></tr>

                    ) : providers.map((p, i) => {
                      const name = p.provider_name || p.full_name || p.secid || `Provider ${i + 1}`;
                      const isActive = !!p.is_launched;
                      return (
                        <tr key={p.secid || i} onMouseEnter={(e) => e.currentTarget.style.background = T.accentSoft} onMouseLeave={(e) => e.currentTarget.style.background = ""} style={{ transition: "background 0.1s" }}>
                          <td style={{ ...S.td, fontSize: 13, padding: "8px 14px", whiteSpace: "nowrap" }}>{name}</td>
                          <td style={{ ...S.td, fontSize: 13, padding: "8px 14px", color: T.textMuted }}>{p.email || "—"}</td>
                          <td style={{ ...S.td, fontSize: 13, padding: "8px 14px", color: T.textMuted, whiteSpace: "nowrap" }}>{p.specialty || "—"}</td>
                          <td style={{ ...S.td, fontSize: 13, padding: "8px 14px", color: T.textMuted, fontFamily: "monospace" }}>{p.station_id || "—"}</td>
                          <td style={{ ...S.td, fontSize: 13, padding: "8px 14px", color: T.textMuted }}>{p.visn ? `VISN ${p.visn}` : "—"}</td>
                          <td style={{ ...S.td, fontSize: 13, padding: "8px 14px", color: T.textMuted, whiteSpace: "nowrap" }}>{fmtDate(p.date_activated)}</td>
                          <td style={{ ...S.td, fontSize: 13, padding: "8px 14px" }}>
                            <span style={{ fontSize: 13, padding: "2px 8px", borderRadius: 20, fontWeight: 500, background: isActive ? "rgba(10,160,110,0.1)" : "rgba(212,138,0,0.1)", color: isActive ? T.green : T.orange }}>
                              {isActive ? "Launched" : "Remaining"}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );
      })()}


      {/* ── User Journey panel — static reference diagram of the states a VA
          provider moves through; colors match the metrics already tracked
          elsewhere on this page (Provisioned/Launched/Active/Decommissioned)
          so it reads as an explainer for those, not a separate data source.
          Deliberately NOT a blocking modal — no full-screen backdrop — so it
          floats over the dashboard like a reference window: the rest of the
          page stays fully usable while it's open, and it can be dragged
          anywhere via its header. */}
      {userJourneyModalOpen && (
        <div style={{ position: "fixed", left: userJourneyModalPos?.x ?? "50%", top: userJourneyModalPos?.y ?? "50%", transform: userJourneyModalPos ? "none" : "translate(-50%, -50%)", zIndex: 1000 }}>
          <div style={{ position: "relative", background: T.surface, borderRadius: T.radius, boxShadow: "0 8px 40px rgba(0,0,0,0.28)", border: `1px solid ${T.border}`, width: userJourneyModalSize?.width ?? 780, height: userJourneyModalSize?.height, maxHeight: userJourneyModalSize ? undefined : "85vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <div
              onMouseDown={onUserJourneyDragMouseDown}
              title="Drag to move"
              style={{ padding: "16px 20px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0, cursor: "move", userSelect: "none" }}
            >
              <div style={{ fontSize: 15, fontWeight: 600 }}>Provider Lifecycle</div>
              <button onClick={() => setUserJourneyModalOpen(false)} onMouseDown={(e) => e.stopPropagation()} style={{ background: "none", border: "none", cursor: "pointer", color: T.textDim, fontSize: 20, lineHeight: 1, padding: "0 4px" }}>×</button>
            </div>
            <div style={{ overflowY: "auto", flex: 1, padding: "18px 24px 20px" }}>
              <div style={{ fontSize: 13, color: T.textMuted, lineHeight: 1.6, marginBottom: 12 }}>
                This diagram shows how a VA provider moves between states. The colored states match the metrics tracked (Provisioned, Launched, Active, Decommissioned).
              </div>
              <svg viewBox="0 0 1000 480" width="100%" role="img">
                <title>VA provider user-journey state machine</title>
                <defs>
                  <marker id="uj-arrow-neutral" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                    <path d="M0,0 L10,5 L0,10 z" fill={T.textDim} />
                  </marker>
                  <marker id="uj-arrow-accent" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                    <path d="M0,0 L10,5 L0,10 z" fill={T.accent} />
                  </marker>
                  <marker id="uj-arrow-blue" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                    <path d="M0,0 L10,5 L0,10 z" fill={T.blue} />
                  </marker>
                  <marker id="uj-arrow-green" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                    <path d="M0,0 L10,5 L0,10 z" fill={T.green} />
                  </marker>
                  <marker id="uj-arrow-red" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                    <path d="M0,0 L10,5 L0,10 z" fill={T.red} />
                  </marker>
                </defs>

                {/* No License / Decommissioned — combined box: this is both where a
                    brand-new provider starts (no license yet) and where a
                    decommissioned provider ends up (back to having no active
                    license), so Inactive's "no user event for X time" path
                    loops all the way back here instead of a separate box. */}
                <rect x="30" y="222" width="140" height="56" rx="10" fill={T.surface2} stroke={T.border} strokeWidth="1.5"/>
                <text x="100" y="248" textAnchor="middle" fontSize="11" fontWeight="700" fill={T.textMuted}>No License /</text>
                <text x="100" y="263" textAnchor="middle" fontSize="11" fontWeight="700" fill={T.textMuted}>Decommissioned</text>

                {/* Provisioned, Launched, Active — the three main tracked metrics,
                    same size, evenly spaced, and centered as a group in the
                    diagram. Training Completed and Inactive are both smaller
                    "step" pills that sit along the connecting paths rather than
                    full boxes, since neither is a metric tracked on its own. */}
                <rect x="255" y="212" width="130" height="76" rx="10" fill={T.accent}/>
                <text x="320" y="245" textAnchor="middle" fontSize="13" fontWeight="700" fill="#fff">Provisioned</text>
                <text x="320" y="262" textAnchor="middle" fontSize="9" fill="rgba(255,255,255,0.85)">license activated</text>

                {/* Training Completed — step pill on the way to Launched */}
                <rect x="410" y="233" width="100" height="34" rx="17" fill={T.blueSoft} stroke={T.blue} strokeWidth="1.5"/>
                <text x="460" y="255" textAnchor="middle" fontSize="10" fontWeight="700" fill={T.blue}>Training</text>

                <rect x="535" y="212" width="130" height="76" rx="10" fill={T.blue}/>
                <text x="600" y="245" textAnchor="middle" fontSize="13" fontWeight="700" fill="#fff">Launched</text>
                <text x="600" y="262" textAnchor="middle" fontSize="9" fill="rgba(255,255,255,0.85)">logged in</text>

                {/* Inactive — step pill between Launched and Active, same
                    lighter treatment as Training Completed */}
                <rect x="690" y="233" width="100" height="34" rx="17" fill={T.redSoft} stroke={T.red} strokeWidth="1.5"/>
                <text x="740" y="255" textAnchor="middle" fontSize="10" fontWeight="700" fill={T.red}>Inactive</text>

                <rect x="815" y="212" width="130" height="76" rx="10" fill={T.green}/>
                <text x="880" y="245" textAnchor="middle" fontSize="13" fontWeight="700" fill="#fff">Active</text>
                <text x="880" y="262" textAnchor="middle" fontSize="9" fill="rgba(255,255,255,0.85)">recording</text>

                {/* Main pipeline arrows */}
                <path d="M170,250 L255,250" stroke={T.accent} strokeWidth="1.6" fill="none" markerEnd="url(#uj-arrow-accent)"/>
                <text x="207" y="242" textAnchor="middle" fontSize="9" fill={T.accent}>Registered</text>

                <path d="M385,250 L410,250" stroke={T.blue} strokeWidth="1.6" fill="none" markerEnd="url(#uj-arrow-blue)"/>
                <text x="397" y="242" textAnchor="middle" fontSize="8" fill={T.blue}>Joined</text>

                <path d="M510,250 L535,250" stroke={T.blue} strokeWidth="1.6" fill="none" markerEnd="url(#uj-arrow-blue)"/>
                <text x="522" y="242" textAnchor="middle" fontSize="8" fill={T.blue}>Done</text>

                {/* Self-launch bypass (skips Training) */}
                <path d="M320,212 C320,160 600,160 600,212" stroke={T.accent} strokeWidth="1.4" fill="none" strokeDasharray="4 3" markerEnd="url(#uj-arrow-accent)"/>
                <text x="460" y="155" textAnchor="middle" fontSize="9" fill={T.accent}>Self-Launched</text>

                {/* Launched -> Inactive: the default state right after launching,
                    before any recording has happened yet */}
                <path d="M665,250 L690,250" stroke={T.red} strokeWidth="1.6" fill="none" markerEnd="url(#uj-arrow-red)"/>

                {/* Inactive <-> Active */}
                <path d="M790,272 L815,272" stroke={T.green} strokeWidth="1.6" fill="none" markerEnd="url(#uj-arrow-green)"/>
                <text x="800" y="286" textAnchor="middle" fontSize="8" fill={T.green}>if record</text>
                <path d="M815,230 L790,230" stroke={T.red} strokeWidth="1.6" fill="none" markerEnd="url(#uj-arrow-red)"/>
                <text x="800" y="220" textAnchor="middle" fontSize="8" fill={T.red}>idle X days</text>

                {/* Inactive -> No License / Decommissioned: looping all the way
                    back to the start, since going quiet for too long returns a
                    provider to having no active license. */}
                <path d="M690,233 C690,65 100,65 100,222" stroke={T.textDim} strokeWidth="1.4" fill="none" strokeDasharray="4 3" markerEnd="url(#uj-arrow-neutral)"/>
                <text x="395" y="58" textAnchor="middle" fontSize="8.5" fill={T.textDim}>no user event for X time</text>

              </svg>

              <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 6 }}>
                {[
                  { label: "No License / Decommissioned", desc: "No active license — either a brand-new provider who's requested one, or a returning one decommissioned after going quiet too long." },
                  { label: "Provisioned", desc: "License activated — counted in the Provisioned metric." },
                  { label: "Launched", desc: "Logged in to the platform — counted in the Launched metric." },
                  { label: "Active", desc: "Has recorded/uploaded at least one encounter — counted in the Active metric." },
                ].map((r) => (
                  <div key={r.label} style={{ fontSize: 12, color: T.textMuted }}>
                    <span style={{ fontWeight: 600, color: T.text }}>{r.label}: </span>{r.desc}
                  </div>
                ))}
              </div>
            </div>

            {/* Resize handle — drag to enlarge (or shrink) the popup */}
            <div
              onMouseDown={onUserJourneyResizeMouseDown}
              title="Drag to resize"
              style={{ position: "absolute", right: 0, bottom: 0, width: 20, height: 20, cursor: "nwse-resize", display: "flex", alignItems: "flex-end", justifyContent: "flex-end", padding: "0 3px 3px 0" }}
            >
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke={T.textDim} strokeWidth="1.4" strokeLinecap="round">
                <line x1="9" y1="1" x2="1" y2="9"/>
                <line x1="9" y1="5" x2="5" y2="9"/>
              </svg>
            </div>
          </div>
        </div>
      )}

      {/* ── Station Grouping modal — explains how "by station" rolls up in the
          Launched / Active detail tables. Same non-blocking, draggable,
          resizable floating panel as Provider Lifecycle. Opened from Thunder's
          bubble (second option). ── */}
      {stationGroupingModalOpen && (
        <div style={{ position: "fixed", left: stationGroupingModalPos?.x ?? "50%", top: stationGroupingModalPos?.y ?? "50%", transform: stationGroupingModalPos ? "none" : "translate(-50%, -50%)", zIndex: 1000 }}>
          <div style={{ position: "relative", background: T.surface, borderRadius: T.radius, boxShadow: "0 8px 40px rgba(0,0,0,0.28)", border: `1px solid ${T.border}`, width: stationGroupingModalSize?.width ?? 560, height: stationGroupingModalSize?.height, maxHeight: stationGroupingModalSize ? undefined : "70vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <div
              onMouseDown={onStationGroupingDragMouseDown}
              title="Drag to move"
              style={{ padding: "16px 20px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0, cursor: "move", userSelect: "none" }}
            >
              <div style={{ fontSize: 15, fontWeight: 600 }}>Station Grouping</div>
              <button onClick={() => setStationGroupingModalOpen(false)} onMouseDown={(e) => e.stopPropagation()} style={{ background: "none", border: "none", cursor: "pointer", color: T.textDim, fontSize: 20, lineHeight: 1, padding: "0 4px" }}>×</button>
            </div>
            <div style={{ overflowY: "auto", flex: 1, padding: "18px 24px 20px" }}>
              <div style={{ fontSize: 13, color: T.textMuted, lineHeight: 1.6, marginBottom: 14 }}>
                In the Launched and Active detail tables, "by station" doesn't mean one row per individual site code — sites are rolled up into their parent station group first.
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
                <div style={{ fontSize: 13, color: T.text, lineHeight: 1.6 }}>
                  <span style={{ fontWeight: 600 }}>The rule:</span> a station group is identified by the first three digits of the site number. A site like <code style={{ background: T.surface2, padding: "1px 5px", borderRadius: 4 }}>402GA</code> rolls up into the <code style={{ background: T.surface2, padding: "1px 5px", borderRadius: 4 }}>402</code> group, since that's the parent VA Medical Center's code.
                </div>
                <div style={{ fontSize: 13, color: T.text, lineHeight: 1.6 }}>
                  <span style={{ fontWeight: 600 }}>The exception:</span> if a site itself is classified as its own VA Medical Center — even with a suffix, like <code style={{ background: T.surface2, padding: "1px 5px", borderRadius: 4 }}>523A4</code> (West Roxbury VA Medical Center) — it doesn't fold into the parent's group. It stands on its own as its own station group instead. This can produce more than one standalone group under the same parent code — Boston's <code style={{ background: T.surface2, padding: "1px 5px", borderRadius: 4 }}>523</code> cluster alone splits into three: <code style={{ background: T.surface2, padding: "1px 5px", borderRadius: 4 }}>523</code> (Jamaica Plain), <code style={{ background: T.surface2, padding: "1px 5px", borderRadius: 4 }}>523A4</code> (West Roxbury), and <code style={{ background: T.surface2, padding: "1px 5px", borderRadius: 4 }}>523A5</code> (Brockton) — each is its own VA Medical Center, so each is its own group.
                </div>
              </div>
              <div style={{ background: T.surface2, border: `1px solid ${T.border}`, borderRadius: 10, padding: "12px 16px" }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 6 }}>Reference tally</div>
                <div style={{ fontSize: 13, color: T.text, lineHeight: 1.6 }}>
                  Running the current VA station reference list through this exact rule produces <span style={{ fontWeight: 700, color: T.accent }}>179 station groups</span> — matching what the Launched/Active detail tables show. Of those, <span style={{ fontWeight: 700, color: T.accent }}>130</span> are first-three-digit rollup groups and <span style={{ fontWeight: 700, color: T.accent }}>49</span> are standalone groups created by a VA Medical Center splitting off from its cluster.
                </div>
              </div>
            </div>

            {/* Resize handle */}
            <div
              onMouseDown={onStationGroupingResizeMouseDown}
              title="Drag to resize"
              style={{ position: "absolute", right: 0, bottom: 0, width: 20, height: 20, cursor: "nwse-resize", display: "flex", alignItems: "flex-end", justifyContent: "flex-end", padding: "0 3px 3px 0" }}
            >
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke={T.textDim} strokeWidth="1.4" strokeLinecap="round">
                <line x1="9" y1="1" x2="1" y2="9"/>
                <line x1="9" y1="5" x2="5" y2="9"/>
              </svg>
            </div>
          </div>
        </div>
      )}

      {/* ── Weekly history modal ── */}
      {weeklyHistoryModal && (
        <div onClick={() => setWeeklyHistoryModal(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: T.surface, borderRadius: T.radius, boxShadow: "0 8px 40px rgba(0,0,0,0.18)", width: 500, maxHeight: "80vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
            {/* Modal header */}
            <div style={{ padding: "16px 20px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 600 }}>Station {weeklyHistoryModal.station.station_id} — Weekly History</div>
                <div style={{ fontSize: 13, color: T.textMuted, marginTop: 2 }}>All weeks · launched providers per week</div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <button onClick={() => {
                  const rows = [...weeklyHistoryModal.weeks].reverse().map((w) => {
                    const total = (w.activated || 0) + (w.remaining_users || 0);
                    const pct = total > 0 ? (w.activated / total) : 0;
                    return { "Week": w.week, "Launched": w.activated, "Total": total, "Remaining": w.remaining_users, "Launch %": pct };
                  });
                  ensureXLSX().then((XLSX) => {
                    const ws = XLSX.utils.json_to_sheet(rows);
                    ws["!cols"] = [{ wch: 14 }, { wch: 10 }, { wch: 10 }, { wch: 18 }];
                    const wb = XLSX.utils.book_new();
                    XLSX.utils.book_append_sheet(wb, ws, "Weekly History");
                    XLSX.writeFile(wb, `weekly_history_station_${weeklyHistoryModal.station.station_id}.xlsx`);
                  });
                }} style={{ background: T.accent, border: "none", borderRadius: T.radiusSm, color: "#fff", fontSize: 13, padding: "5px 12px", cursor: "pointer", fontFamily: T.font, fontWeight: 500 }}>⬇ Export XLSX</button>
                <button onClick={() => setWeeklyHistoryModal(null)} style={{ background: "none", border: "none", cursor: "pointer", color: T.textDim, fontSize: 20, lineHeight: 1, padding: "0 4px" }}>×</button>
              </div>
            </div>
            {/* Modal body */}
            <div style={{ overflowY: "auto", flex: 1 }}>
              {weeklyHistoryModal.loading ? (
                <div style={{ padding: 40, textAlign: "center", color: T.textDim, fontSize: 13 }}>Loading...</div>
              ) : weeklyHistoryModal.weeks.length === 0 ? (
                <div style={{ padding: 40, textAlign: "center", color: T.textDim, fontSize: 13 }}>No data available</div>
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      {["Week", "Launched", "Total", "Remaining", "Launch %"].map((h) => (
                        <th key={h} style={{ ...S.th, color: T.text }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[...weeklyHistoryModal.weeks].reverse().map((w) => {
                      const d = new Date(w.week + "T12:00:00Z");
                      const label = d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
                      const total = (w.activated || 0) + (w.remaining_users || 0);
                      return (
                        <tr key={w.week} style={{ transition: "background 0.1s" }} onMouseEnter={(e) => e.currentTarget.style.background = T.surface2} onMouseLeave={(e) => e.currentTarget.style.background = ""}>
                          <td style={{ ...S.td, ...S.mono }}>{label}</td>
                          <td style={S.td}>{w.activated}</td>
                          <td style={S.td}>{total}</td>
                          <td style={S.td}>{w.remaining_users}</td>
                          <td style={S.td}>{total > 0 ? `${Math.round(w.activated / total * 100)}%` : "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Launched trend monthly table popup — non-blocking floating panel
          (no full-screen backdrop) so it doesn't get in the way of using the
          rest of the dashboard while it's open. ── */}
      {launchedTableOpen && (
        <div style={{ position: "fixed", left: launchedTablePos?.x ?? "50%", top: launchedTablePos?.y ?? "50%", transform: launchedTablePos ? "none" : "translate(-50%, -50%)", zIndex: 1000, background: T.surface, borderRadius: T.radius, boxShadow: "0 8px 40px rgba(0,0,0,0.28)", border: `1px solid ${T.border}`, width: 460, maxHeight: "80vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <div
              onMouseDown={onLaunchedTableDragMouseDown}
              title="Drag to move"
              style={{ padding: "16px 20px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0, cursor: "move", userSelect: "none" }}
            >
              <div style={{ fontSize: 15, fontWeight: 600 }}>Launched — by month</div>
              <button onClick={() => setLaunchedTableOpen(false)} onMouseDown={(e) => e.stopPropagation()} style={{ background: "none", border: "none", cursor: "pointer", color: T.textDim, fontSize: 20, lineHeight: 1, padding: "0 4px" }}>×</button>
            </div>
            <div style={{ overflowY: "auto", flex: 1 }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead style={{ position: "sticky", top: 0, background: T.surface, zIndex: 1 }}>
                  <tr>{["Month", "Total Launched", "New Launches"].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {cumulativeGrowthRows(launchedGrowth, activeLicensed).map((r, i) => {
                    const mo = parseInt(r.month.slice(4, 6), 10);
                    const yr = r.month.slice(0, 4);
                    return (
                      <tr key={r.month} style={{ background: i % 2 === 0 ? T.surface : T.surface2 }}>
                        <td style={S.td}>{MONTH_NAMES[mo]} {yr}</td>
                        <td style={{ ...S.td, fontWeight: 600, color: T.accent }}>{r.total.toLocaleString()}</td>
                        <td style={{ ...S.td, color: T.textMuted }}>{r.delta >= 0 ? "+" : ""}{r.delta.toLocaleString()}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
        </div>
      )}

      {/* ── Provisioned trend monthly table popup — non-blocking floating
          panel, same as Launched above. All three of these popups are
          independent (separate open/pos state), so they can be open at the
          same time; each defaults to a slightly different spot so they
          cascade instead of stacking exactly on top of one another when more
          than one is open at once. ── */}
      {provisionedTableOpen && (
        <div style={{ position: "fixed", left: provisionedTablePos?.x ?? "calc(50% - 40px)", top: provisionedTablePos?.y ?? "calc(50% - 40px)", transform: provisionedTablePos ? "none" : "translate(-50%, -50%)", zIndex: 1000, background: T.surface, borderRadius: T.radius, boxShadow: "0 8px 40px rgba(0,0,0,0.28)", border: `1px solid ${T.border}`, width: 460, maxHeight: "80vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <div
              onMouseDown={onProvisionedTableDragMouseDown}
              title="Drag to move"
              style={{ padding: "16px 20px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0, cursor: "move", userSelect: "none" }}
            >
              <div style={{ fontSize: 15, fontWeight: 600 }}>Provisioned Licenses — by month</div>
              <button onClick={() => setProvisionedTableOpen(false)} onMouseDown={(e) => e.stopPropagation()} style={{ background: "none", border: "none", cursor: "pointer", color: T.textDim, fontSize: 20, lineHeight: 1, padding: "0 4px" }}>×</button>
            </div>
            <div style={{ overflowY: "auto", flex: 1 }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead style={{ position: "sticky", top: 0, background: T.surface, zIndex: 1 }}>
                  <tr>{["Month", "Total Licensed", "New Provisioned"].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {cumulativeGrowthRows(licenseGrowth, totalLicensed).map((r, i) => {
                    const mo = parseInt(r.month.slice(4, 6), 10);
                    const yr = r.month.slice(0, 4);
                    return (
                      <tr key={r.month} style={{ background: i % 2 === 0 ? T.surface : T.surface2 }}>
                        <td style={S.td}>{MONTH_NAMES[mo]} {yr}</td>
                        <td style={{ ...S.td, fontWeight: 600, color: T.accent }}>{r.total.toLocaleString()}</td>
                        <td style={{ ...S.td, color: T.textMuted }}>{r.delta >= 0 ? "+" : ""}{r.delta.toLocaleString()}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
        </div>
      )}

      {/* ── Active trend monthly table popup — non-blocking floating panel,
          same as Launched/Provisioned above. ── */}
      {activeTableOpen && (
        <div style={{ position: "fixed", left: activeTablePos?.x ?? "calc(50% + 40px)", top: activeTablePos?.y ?? "calc(50% + 40px)", transform: activeTablePos ? "none" : "translate(-50%, -50%)", zIndex: 1000, background: T.surface, borderRadius: T.radius, boxShadow: "0 8px 40px rgba(0,0,0,0.28)", border: `1px solid ${T.border}`, width: 640, maxHeight: "80vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <div
              onMouseDown={onActiveTableDragMouseDown}
              title="Drag to move"
              style={{ padding: "16px 20px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0, cursor: "move", userSelect: "none" }}
            >
              <div style={{ fontSize: 15, fontWeight: 600 }}>Active Users — by month</div>
              <button onClick={() => setActiveTableOpen(false)} onMouseDown={(e) => e.stopPropagation()} style={{ background: "none", border: "none", cursor: "pointer", color: T.textDim, fontSize: 20, lineHeight: 1, padding: "0 4px" }}>×</button>
            </div>
            <div style={{ overflowY: "auto", flex: 1 }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead style={{ position: "sticky", top: 0, background: T.surface, zIndex: 1 }}>
                  <tr>{["Month", "Distinct Active Users", "Avg DAU", "AVG Daily recordings", "Total Recordings", "Days Used"].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {[...activitySummary.dau_history].reverse().map((r, i) => {
                    const MONTH_FULL = ["January","February","March","April","May","June","July","August","September","October","November","December"];
                    const mo = parseInt(r.month.slice(4, 6), 10) - 1;
                    const yr = r.month.slice(0, 4);
                    const isPartial = r.days_elapsed != null && r.days_in_month != null && r.days_elapsed !== r.days_in_month;
                    return (
                      <tr key={r.month} style={{ background: i % 2 === 0 ? T.surface : T.surface2 }}>
                        <td style={S.td}>{MONTH_FULL[mo]} {yr}</td>
                        <td style={{ ...S.td, fontWeight: 600, color: T.text }}>{Number(r.distinct_active_users ?? 0).toLocaleString()}</td>
                        <td style={{ ...S.td, fontWeight: 600, color: T.accent }}>{Number(r.avg_dau).toLocaleString(undefined, { maximumFractionDigits: 1 })}</td>
                        <td style={{ ...S.td, fontWeight: 600, color: T.text }}>{Number(r.avg_daily_patients ?? 0).toLocaleString(undefined, { maximumFractionDigits: 1 })}</td>
                        <td style={{ ...S.td, color: T.textMuted }}>{Number(r.sum_patients ?? 0).toLocaleString()}</td>
                        <td style={{ ...S.td, color: T.textMuted }}>
                          {isPartial ? `${r.days_elapsed} of ${r.days_in_month}` : r.days_in_month}
                          {isPartial && <span style={{ marginLeft: 6, fontSize: 10, color: T.orange, textTransform: "uppercase", letterSpacing: 0.5 }}>month in progress</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
        </div>
      )}

      {decommissionedTableOpen && (
        <div style={{ position: "fixed", left: decommissionedTablePos?.x ?? "calc(50% + 80px)", top: decommissionedTablePos?.y ?? "calc(50% - 80px)", transform: decommissionedTablePos ? "none" : "translate(-50%, -50%)", zIndex: 1000, background: T.surface, borderRadius: T.radius, boxShadow: "0 8px 40px rgba(0,0,0,0.28)", border: `1px solid ${T.border}`, width: 460, maxHeight: "80vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <div
              onMouseDown={onDecommissionedTableDragMouseDown}
              title="Drag to move"
              style={{ padding: "16px 20px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0, cursor: "move", userSelect: "none" }}
            >
              <div style={{ fontSize: 15, fontWeight: 600 }}>Decommissioned — by month</div>
              <button onClick={() => setDecommissionedTableOpen(false)} onMouseDown={(e) => e.stopPropagation()} style={{ background: "none", border: "none", cursor: "pointer", color: T.textDim, fontSize: 20, lineHeight: 1, padding: "0 4px" }}>×</button>
            </div>
            <div style={{ overflowY: "auto", flex: 1 }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead style={{ position: "sticky", top: 0, background: T.surface, zIndex: 1 }}>
                  <tr>{["Month", "Total Decommissioned", "New Decommissioned"].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {cumulativeGrowthRows(decommissionedGrowth, decommissionedTotal).map((r, i) => {
                    const mo = parseInt(r.month.slice(4, 6), 10);
                    const yr = r.month.slice(0, 4);
                    return (
                      <tr key={r.month} style={{ background: i % 2 === 0 ? T.surface : T.surface2 }}>
                        <td style={S.td}>{MONTH_NAMES[mo]} {yr}</td>
                        <td style={{ ...S.td, fontWeight: 600, color: T.accent }}>{r.total.toLocaleString()}</td>
                        <td style={{ ...S.td, color: T.textMuted }}>{r.delta >= 0 ? "+" : ""}{r.delta.toLocaleString()}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
        </div>
      )}

      {/* ── Station weekly utilization full table modal ── */}
      {stationWeeklyModalOpen && stationReport && (
        <div onClick={() => setStationWeeklyModalOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: T.surface, borderRadius: T.radius, boxShadow: "0 8px 40px rgba(0,0,0,0.18)", width: 560, maxHeight: "80vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <div style={{ padding: "16px 20px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 600 }}>Weekly Utilization — {stationReport.station.site_name || stationReport.station.station_id}</div>
              <button onClick={() => setStationWeeklyModalOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", color: T.textDim, fontSize: 20, lineHeight: 1, padding: "0 4px" }}>×</button>
            </div>
            <div style={{ overflowY: "auto", flex: 1 }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead style={{ position: "sticky", top: 0, background: T.surface, zIndex: 1 }}>
                  <tr>{["Week", "Total Recordings", "Total Patients", "Utilization Score"].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {[...stationReport.weekly].reverse().map((w, i) => (
                    <tr key={w.week} style={{ background: i % 2 === 0 ? T.surface : T.surface2 }}>
                      <td style={S.td}>{w.week}</td>
                      <td style={{ ...S.td, fontWeight: 600, color: T.accent }}>{w.total_recordings.toLocaleString()}</td>
                      <td style={S.td}>{w.total_patients.toLocaleString()}</td>
                      <td style={S.td}>{w.utilization_score != null ? `${(w.utilization_score * 100).toFixed(2)}%` : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── Station doctor drill-down full table modal ── */}
      {stationDoctorsModalOpen && stationReport && (
        <div onClick={() => setStationDoctorsModalOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: T.surface, borderRadius: T.radius, boxShadow: "0 8px 40px rgba(0,0,0,0.18)", width: 720, maxHeight: "80vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <div style={{ padding: "16px 20px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 600 }}>Doctor Drill-Down — {stationReport.station.site_name || stationReport.station.station_id}</div>
                {stationReport.doctors.filter((d) => !d.is_current_period).length > 0 && (
                  <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2 }}>
                    {stationReport.doctors.filter((d) => !d.is_current_period).length} of {stationReport.doctors.length} decommissioned
                  </div>
                )}
              </div>
              <button onClick={() => setStationDoctorsModalOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", color: T.textDim, fontSize: 20, lineHeight: 1, padding: "0 4px" }}>×</button>
            </div>
            <div style={{ overflowY: "auto", flex: 1 }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead style={{ position: "sticky", top: 0, background: T.surface, zIndex: 1 }}>
                  <tr>{["Name", "Email", "Specialty", "Created At", "Launched Date", "Status"].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {stationReport.doctors.map((d, i) => (
                    <tr key={d.doctor_user_id} style={{ background: i % 2 === 0 ? T.surface : T.surface2 }}>
                      <td style={S.td}>{d.provider_name || "—"}</td>
                      <td style={{ ...S.td, color: T.textMuted }}>{d.email || "—"}</td>
                      <td style={{ ...S.td, color: T.textMuted }}>{d.specialty || "—"}</td>
                      <td style={{ ...S.td, color: T.textMuted }}>{fmtDate(d.created_at)}</td>
                      <td style={{ ...S.td, color: T.textMuted }}>{(d.provider_name || "").trim().toLowerCase() === "placeholder" ? "Not Launched" : d.launched_date ? fmtDate(d.launched_date) : "—"}</td>
                      <td style={S.td}>
                        <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, fontWeight: 600, background: doctorStatusStyle(d).bg, color: doctorStatusStyle(d).color }}>
                          {doctorStatusLabel(d)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── Specialty weekly utilization full table modal ── */}
      {specialtyWeeklyModalOpen && specialtyReport && (
        <div onClick={() => setSpecialtyWeeklyModalOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: T.surface, borderRadius: T.radius, boxShadow: "0 8px 40px rgba(0,0,0,0.18)", width: 560, maxHeight: "80vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <div style={{ padding: "16px 20px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 600 }}>Weekly Utilization — {specialtyReport.specialty}</div>
              <button onClick={() => setSpecialtyWeeklyModalOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", color: T.textDim, fontSize: 20, lineHeight: 1, padding: "0 4px" }}>×</button>
            </div>
            <div style={{ overflowY: "auto", flex: 1 }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead style={{ position: "sticky", top: 0, background: T.surface, zIndex: 1 }}>
                  <tr>{["Week", "Total Recordings", "Total Patients", "Utilization Score"].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {[...specialtyReport.weekly].reverse().map((w, i) => (
                    <tr key={w.week} style={{ background: i % 2 === 0 ? T.surface : T.surface2 }}>
                      <td style={S.td}>{w.week}</td>
                      <td style={{ ...S.td, fontWeight: 600, color: T.accent }}>{w.total_recordings.toLocaleString()}</td>
                      <td style={S.td}>{w.total_patients.toLocaleString()}</td>
                      <td style={S.td}>{w.utilization_score != null ? `${(w.utilization_score * 100).toFixed(2)}%` : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── Specialty doctor drill-down full table modal ── */}
      {specialtyDoctorsModalOpen && specialtyReport && (
        <div onClick={() => setSpecialtyDoctorsModalOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: T.surface, borderRadius: T.radius, boxShadow: "0 8px 40px rgba(0,0,0,0.18)", width: 720, maxHeight: "80vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <div style={{ padding: "16px 20px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 600 }}>Doctor Drill-Down — {specialtyReport.specialty}</div>
                {specialtyReport.doctors.filter((d) => !d.is_current_period).length > 0 && (
                  <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2 }}>
                    {specialtyReport.doctors.filter((d) => !d.is_current_period).length} of {specialtyReport.doctors.length} decommissioned
                  </div>
                )}
              </div>
              <button onClick={() => setSpecialtyDoctorsModalOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", color: T.textDim, fontSize: 20, lineHeight: 1, padding: "0 4px" }}>×</button>
            </div>
            <div style={{ overflowY: "auto", flex: 1 }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead style={{ position: "sticky", top: 0, background: T.surface, zIndex: 1 }}>
                  <tr>{["Name", "Email", "Station", "Created At", "Launched Date", "Status"].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {specialtyReport.doctors.map((d, i) => (
                    <tr key={d.doctor_user_id} style={{ background: i % 2 === 0 ? T.surface : T.surface2 }}>
                      <td style={S.td}>{d.provider_name || "—"}</td>
                      <td style={{ ...S.td, color: T.textMuted }}>{d.email || "—"}</td>
                      <td style={{ ...S.td, color: T.textMuted }}>{d.station_id || "—"}</td>
                      <td style={{ ...S.td, color: T.textMuted }}>{fmtDate(d.created_at)}</td>
                      <td style={{ ...S.td, color: T.textMuted }}>{(d.provider_name || "").trim().toLowerCase() === "placeholder" ? "Not Launched" : d.launched_date ? fmtDate(d.launched_date) : "—"}</td>
                      <td style={S.td}>
                        <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, fontWeight: 600, background: doctorStatusStyle(d).bg, color: doctorStatusStyle(d).color }}>
                          {doctorStatusLabel(d)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── VISN weekly utilization full table modal ── */}
      {visnWeeklyModalOpen && visnReport && (
        <div onClick={() => setVisnWeeklyModalOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: T.surface, borderRadius: T.radius, boxShadow: "0 8px 40px rgba(0,0,0,0.18)", width: 560, maxHeight: "80vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <div style={{ padding: "16px 20px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 600 }}>Weekly Utilization — VISN {visnReport.visn}</div>
              <button onClick={() => setVisnWeeklyModalOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", color: T.textDim, fontSize: 20, lineHeight: 1, padding: "0 4px" }}>×</button>
            </div>
            <div style={{ overflowY: "auto", flex: 1 }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead style={{ position: "sticky", top: 0, background: T.surface, zIndex: 1 }}>
                  <tr>{["Week", "Total Recordings", "Total Patients", "Utilization Score"].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {[...visnReport.weekly].reverse().map((w, i) => (
                    <tr key={w.week} style={{ background: i % 2 === 0 ? T.surface : T.surface2 }}>
                      <td style={S.td}>{w.week}</td>
                      <td style={{ ...S.td, fontWeight: 600, color: T.accent }}>{w.total_recordings.toLocaleString()}</td>
                      <td style={S.td}>{w.total_patients.toLocaleString()}</td>
                      <td style={S.td}>{w.utilization_score != null ? `${(w.utilization_score * 100).toFixed(2)}%` : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── VISN doctor drill-down full table modal ── */}
      {riseWeeklyModalOpen && riseReport && (
        <div onClick={() => setRiseWeeklyModalOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: T.surface, borderRadius: T.radius, boxShadow: "0 8px 40px rgba(0,0,0,0.18)", width: 560, maxHeight: "80vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <div style={{ padding: "16px 20px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 600 }}>Weekly Utilization — {formatRiseLabel(riseReport.new_visn)}</div>
              <button onClick={() => setRiseWeeklyModalOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", color: T.textDim, fontSize: 20, lineHeight: 1, padding: "0 4px" }}>×</button>
            </div>
            <div style={{ overflowY: "auto", flex: 1 }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead style={{ position: "sticky", top: 0, background: T.surface, zIndex: 1 }}>
                  <tr>{["Week", "Total Recordings", "Total Patients", "Utilization Score"].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {[...riseReport.weekly].reverse().map((w, i) => (
                    <tr key={w.week} style={{ background: i % 2 === 0 ? T.surface : T.surface2 }}>
                      <td style={S.td}>{w.week}</td>
                      <td style={{ ...S.td, fontWeight: 600, color: T.accent }}>{w.total_recordings.toLocaleString()}</td>
                      <td style={S.td}>{w.total_patients.toLocaleString()}</td>
                      <td style={S.td}>{w.utilization_score != null ? `${(w.utilization_score * 100).toFixed(2)}%` : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {riseDoctorsModalOpen && riseReport && (
        <div onClick={() => setRiseDoctorsModalOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: T.surface, borderRadius: T.radius, boxShadow: "0 8px 40px rgba(0,0,0,0.18)", width: 720, maxHeight: "80vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <div style={{ padding: "16px 20px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 600 }}>Doctor Drill-Down — {formatRiseLabel(riseReport.new_visn)}</div>
                {riseReport.doctors.filter((d) => !d.is_current_period).length > 0 && (
                  <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2 }}>
                    {riseReport.doctors.filter((d) => !d.is_current_period).length} of {riseReport.doctors.length} decommissioned
                  </div>
                )}
              </div>
              <button onClick={() => setRiseDoctorsModalOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", color: T.textDim, fontSize: 20, lineHeight: 1, padding: "0 4px" }}>×</button>
            </div>
            <div style={{ overflowY: "auto", flex: 1 }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead style={{ position: "sticky", top: 0, background: T.surface, zIndex: 1 }}>
                  <tr>{["Name", "Email", "Station", "Specialty", "Created At", "Launched Date", "Status"].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {riseReport.doctors.map((d, i) => (
                    <tr key={d.doctor_user_id} style={{ background: i % 2 === 0 ? T.surface : T.surface2 }}>
                      <td style={S.td}>{d.provider_name || "—"}</td>
                      <td style={{ ...S.td, color: T.textMuted }}>{d.email || "—"}</td>
                      <td style={{ ...S.td, color: T.textMuted }}>{d.station_id || "—"}</td>
                      <td style={{ ...S.td, color: T.textMuted }}>{d.specialty || "—"}</td>
                      <td style={{ ...S.td, color: T.textMuted }}>{fmtDate(d.created_at)}</td>
                      <td style={{ ...S.td, color: T.textMuted }}>{(d.provider_name || "").trim().toLowerCase() === "placeholder" ? "Not Launched" : d.launched_date ? fmtDate(d.launched_date) : "—"}</td>
                      <td style={S.td}>
                        <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, fontWeight: 600, background: doctorStatusStyle(d).bg, color: doctorStatusStyle(d).color }}>
                          {doctorStatusLabel(d)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {visnDoctorsModalOpen && visnReport && (
        <div onClick={() => setVisnDoctorsModalOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: T.surface, borderRadius: T.radius, boxShadow: "0 8px 40px rgba(0,0,0,0.18)", width: 720, maxHeight: "80vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <div style={{ padding: "16px 20px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 600 }}>Doctor Drill-Down — VISN {visnReport.visn}</div>
                {visnReport.doctors.filter((d) => !d.is_current_period).length > 0 && (
                  <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2 }}>
                    {visnReport.doctors.filter((d) => !d.is_current_period).length} of {visnReport.doctors.length} decommissioned
                  </div>
                )}
              </div>
              <button onClick={() => setVisnDoctorsModalOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", color: T.textDim, fontSize: 20, lineHeight: 1, padding: "0 4px" }}>×</button>
            </div>
            <div style={{ overflowY: "auto", flex: 1 }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead style={{ position: "sticky", top: 0, background: T.surface, zIndex: 1 }}>
                  <tr>{["Name", "Email", "Station", "Specialty", "Created At", "Launched Date", "Status"].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {visnReport.doctors.map((d, i) => (
                    <tr key={d.doctor_user_id} style={{ background: i % 2 === 0 ? T.surface : T.surface2 }}>
                      <td style={S.td}>{d.provider_name || "—"}</td>
                      <td style={{ ...S.td, color: T.textMuted }}>{d.email || "—"}</td>
                      <td style={{ ...S.td, color: T.textMuted }}>{d.station_id || "—"}</td>
                      <td style={{ ...S.td, color: T.textMuted }}>{d.specialty || "—"}</td>
                      <td style={{ ...S.td, color: T.textMuted }}>{fmtDate(d.created_at)}</td>
                      <td style={{ ...S.td, color: T.textMuted }}>{(d.provider_name || "").trim().toLowerCase() === "placeholder" ? "Not Launched" : d.launched_date ? fmtDate(d.launched_date) : "—"}</td>
                      <td style={S.td}>
                        <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, fontWeight: 600, background: doctorStatusStyle(d).bg, color: doctorStatusStyle(d).color }}>
                          {doctorStatusLabel(d)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function DinoEgg() {
  return (
    <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12, paddingBottom: 8 }}>
      <div
        title="ThunderCats, ho!"
        style={{ display: "flex", alignItems: "center", gap: 5, opacity: 0.25, transition: "opacity 0.2s", padding: "4px 8px", borderRadius: 6 }}
        onMouseEnter={(e) => e.currentTarget.style.opacity = "1"}
        onMouseLeave={(e) => e.currentTarget.style.opacity = "0.25"}
      >
        <span style={{ fontSize: 16 }}>⚡</span>
        <span style={{ fontSize: 13, fontWeight: 600, letterSpacing: "0.5px", color: "#f39c12", fontFamily: "monospace" }}>THUNDERCATS</span>
        <span style={{ fontSize: 16 }}>⚡</span>
      </div>
    </div>
  );
}

function _unused_DinoEgg_game() {
  const [open, setOpen] = useState(false);
  const canvasRef = useRef(null);
  const state = useRef(null);
  const raf = useRef(null);

  const GROUND = 100;

  const initState = () => ({
    running: false, over: false, score: 0, hiScore: 0,
    cat: { x: 55, y: GROUND - 28, vy: 0, grounded: true, w: 22, h: 28 },
    obstacles: [], frame: 0, speed: 4, nextObs: 90,
  });

  const jump = () => {
    const s = state.current;
    if (!s) return;
    if (s.over) {
      Object.assign(s, { over: false, score: 0, obstacles: [], speed: 4, frame: 0, nextObs: 90 });
      s.cat.y = GROUND - 28; s.cat.vy = 0; s.cat.grounded = true; s.running = true;
      loop(); return;
    }
    if (!s.running) { s.running = true; loop(); return; }
    if (s.cat.grounded) { s.cat.vy = -12; s.cat.grounded = false; }
  };

  const drawLionO = (ctx, x, y, frame, grounded) => {
    // ThunderCats Lion-O — canvas pixel art
    const f = frame;

    // legs (animate when running)
    ctx.fillStyle = "#1a3a6b"; // blue pants
    if (grounded) {
      const l = f % 10 < 5;
      ctx.fillRect(x + 4, y + 22, 6, 8 + (l ? 2 : 0));
      ctx.fillRect(x + 12, y + 22, 6, 8 + (l ? 0 : 2));
      // boots
      ctx.fillStyle = "#c0392b";
      ctx.fillRect(x + 3, y + 28 + (l ? 2 : 0), 8, 4);
      ctx.fillRect(x + 11, y + 28 + (l ? 0 : 2), 8, 4);
    } else {
      ctx.fillRect(x + 4, y + 22, 6, 6);
      ctx.fillRect(x + 12, y + 22, 6, 6);
      ctx.fillStyle = "#c0392b";
      ctx.fillRect(x + 3, y + 26, 8, 4);
      ctx.fillRect(x + 11, y + 26, 8, 4);
    }

    // body / tunic
    ctx.fillStyle = "#c0392b"; // red tunic
    ctx.fillRect(x + 2, y + 10, 18, 14);

    // ThunderCats logo on chest
    ctx.fillStyle = "#f39c12";
    ctx.fillRect(x + 8, y + 14, 6, 5);
    ctx.fillRect(x + 9, y + 13, 4, 1);

    // arms
    ctx.fillStyle = "#e8a87c"; // fur/skin
    ctx.fillRect(x, y + 11, 4, 10);      // left arm
    ctx.fillRect(x + 18, y + 11, 4, 10); // right arm

    // sword of omens (right hand)
    ctx.fillStyle = "#bdc3c7";
    ctx.fillRect(x + 20, y + 8, 2, 16);  // blade
    ctx.fillStyle = "#f39c12";
    ctx.fillRect(x + 18, y + 13, 6, 3);  // crossguard

    // neck / head base
    ctx.fillStyle = "#e8a87c";
    ctx.fillRect(x + 7, y + 6, 8, 6);

    // head
    ctx.fillRect(x + 5, y - 2, 12, 10);

    // mane (red-orange hair)
    ctx.fillStyle = "#e74c3c";
    ctx.fillRect(x + 3, y - 4, 16, 5);   // top mane
    ctx.fillRect(x + 2, y - 1, 3, 8);    // left mane
    ctx.fillRect(x + 17, y - 1, 3, 8);   // right mane

    // cat ears
    ctx.fillStyle = "#e8a87c";
    ctx.fillRect(x + 5, y - 7, 4, 5);    // left ear
    ctx.fillRect(x + 13, y - 7, 4, 5);   // right ear
    ctx.fillStyle = "#c0392b";
    ctx.fillRect(x + 6, y - 6, 2, 3);    // inner ear L
    ctx.fillRect(x + 14, y - 6, 2, 3);   // inner ear R

    // eyes (ThunderCats blue eyes)
    ctx.fillStyle = "#3498db";
    ctx.fillRect(x + 7, y + 1, 3, 3);
    ctx.fillRect(x + 12, y + 1, 3, 3);
    ctx.fillStyle = "#000";
    ctx.fillRect(x + 8, y + 2, 2, 2);
    ctx.fillRect(x + 13, y + 2, 2, 2);

    // nose / muzzle
    ctx.fillStyle = "#d4a574";
    ctx.fillRect(x + 8, y + 4, 6, 3);
    ctx.fillStyle = "#c0392b";
    ctx.fillRect(x + 10, y + 5, 2, 1);

    // whiskers
    ctx.fillStyle = "#fff";
    ctx.fillRect(x + 4, y + 4, 4, 1);
    ctx.fillRect(x + 14, y + 4, 4, 1);
  };

  const drawObstacle = (ctx, c) => {
    // Stone pillar / ancient ruin obstacle
    ctx.fillStyle = "#7f8c8d";
    ctx.fillRect(c.x, GROUND - c.h, c.w, c.h);
    ctx.fillStyle = "#95a5a6";
    ctx.fillRect(c.x + 1, GROUND - c.h, c.w - 2, 4); // top cap
    ctx.fillRect(c.x, GROUND - c.h + 5, 3, c.h - 5); // left edge highlight
    ctx.fillStyle = "#636e72";
    ctx.fillRect(c.x + 2, GROUND - c.h + 8, c.w - 4, 3); // crack detail
    ctx.fillRect(c.x + 3, GROUND - c.h + 16, c.w - 6, 2);
  };

  const loop = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const s = state.current;
    if (!s.running) return;

    const W = canvas.width;
    ctx.clearRect(0, 0, W, canvas.height);

    // sky gradient (flat)
    ctx.fillStyle = "#0a1628";
    ctx.fillRect(0, 0, W, GROUND);

    // stars (static seed)
    ctx.fillStyle = "rgba(255,255,255,0.6)";
    [[40,12],[90,25],[160,8],[230,18],[310,6],[400,22],[470,10],[520,15]].forEach(([sx,sy]) => {
      ctx.fillRect(sx, sy, 1, 1);
    });

    // ThunderCats eye of thundera in background (subtle)
    ctx.fillStyle = "rgba(243,156,18,0.08)";
    ctx.beginPath(); ctx.arc(W - 60, 45, 20, 0, Math.PI * 2); ctx.fill();

    // ground line
    ctx.fillStyle = "#4a6741";
    ctx.fillRect(0, GROUND, W, 2);
    ctx.fillStyle = "#3d5934";
    ctx.fillRect(0, GROUND + 2, W, 6);

    // physics
    s.cat.vy += 0.55;
    s.cat.y += s.cat.vy;
    if (s.cat.y >= GROUND - 28) { s.cat.y = GROUND - 28; s.cat.vy = 0; s.cat.grounded = true; }

    drawLionO(ctx, s.cat.x, s.cat.y, s.frame, s.cat.grounded);

    // obstacles
    s.frame++;
    s.nextObs--;
    if (s.nextObs <= 0) {
      const ch = 22 + Math.floor(Math.random() * 20);
      s.obstacles.push({ x: W, w: 14, h: ch });
      s.nextObs = 65 + Math.floor(Math.random() * 70);
    }
    s.obstacles.forEach((c) => { c.x -= s.speed; drawObstacle(ctx, c); });
    s.obstacles = s.obstacles.filter((c) => c.x + c.w > 0);

    // collision
    const { x, y, w, h } = s.cat;
    for (const c of s.obstacles) {
      if (x + w - 4 > c.x + 2 && x + 4 < c.x + c.w - 2 && y + h > GROUND - c.h + 3) {
        s.running = false; s.over = true;
        if (s.score > s.hiScore) s.hiScore = s.score;
        ctx.fillStyle = "rgba(0,0,0,0.5)";
        ctx.fillRect(0, 0, W, canvas.height);
        ctx.fillStyle = "#f39c12";
        ctx.font = "bold 16px monospace";
        ctx.textAlign = "center";
        ctx.fillText("⚡ THUNDER... THUNDER... THUNDERCATS! ⚡", W / 2, 48);
        ctx.fillStyle = "#ccc";
        ctx.font = "11px monospace";
        ctx.fillText(`Score: ${s.score}   Best: ${s.hiScore}`, W / 2, 68);
        ctx.fillText("tap or space to try again", W / 2, 86);
        return;
      }
    }

    // score
    s.score++;
    s.speed = 4 + s.score / 400;
    ctx.fillStyle = "#f39c12";
    ctx.font = "bold 11px monospace";
    ctx.textAlign = "right";
    ctx.fillText(`⚡ ${String(s.score).padStart(5,"0")}`, W - 10, 16);

    raf.current = requestAnimationFrame(loop);
  };

  useEffect(() => {
    if (!open) return;
    state.current = initState();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    // draw idle screen
    ctx.fillStyle = "#0a1628";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "rgba(255,255,255,0.5)";
    [[40,12],[90,25],[160,8],[230,18],[310,6],[400,22],[470,10],[520,15]].forEach(([sx,sy]) => ctx.fillRect(sx,sy,1,1));
    ctx.fillStyle = "#4a6741";
    ctx.fillRect(0, GROUND, canvas.width, 8);
    drawLionO(ctx, 55, GROUND - 28, 0, true);
    ctx.fillStyle = "#f39c12";
    ctx.font = "bold 13px monospace";
    ctx.textAlign = "center";
    ctx.fillText("⚡  THUNDERCATS — tap or press space  ⚡", canvas.width / 2, 48);

    const onKey = (e) => { if (e.code === "Space" || e.code === "ArrowUp") { e.preventDefault(); jump(); } };
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("keydown", onKey); cancelAnimationFrame(raf.current); };
  }, [open]);

  return (
    <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12, paddingBottom: 8 }}>
      {!open ? (
        <div
          onClick={() => setOpen(true)}
          title="ThunderCats, ho!"
          style={{ display: "flex", alignItems: "center", gap: 5, cursor: "pointer", opacity: 0.25, transition: "opacity 0.2s", padding: "4px 8px", borderRadius: 6 }}
          onMouseEnter={(e) => e.currentTarget.style.opacity = "1"}
          onMouseLeave={(e) => e.currentTarget.style.opacity = "0.25"}
        >
          <span style={{ fontSize: 16 }}>⚡</span>
          <span style={{ fontSize: 13, fontWeight: 600, letterSpacing: "0.5px", color: "#f39c12", fontFamily: "monospace" }}>THUNDERCATS</span>
          <span style={{ fontSize: 16 }}>⚡</span>
        </div>
      ) : (
        <div style={{ position: "relative" }}>
          <canvas
            ref={canvasRef}
            width={580} height={130}
            onClick={jump}
            style={{ display: "block", border: "2px solid #f39c12", borderRadius: 8, cursor: "pointer" }}
          />
          <button
            onClick={() => { cancelAnimationFrame(raf.current); setOpen(false); }}
            style={{ position: "absolute", top: 4, right: 6, background: "none", border: "none", cursor: "pointer", fontSize: 16, color: "#f39c12", fontWeight: "bold" }}
          >×</button>
        </div>
      )}
    </div>
  );
}
